/**
 * reservoirPdf.ts — Real text-based PDF generation for reservoir aeration reports.
 *
 * Produces native PDF using jsPDF + autoTable (NOT browser-print screenshots).
 * All text is selectable / searchable; tables paginate automatically; pages
 * carry consistent branded headers, footers and vector-drawn visuals.
 *
 * Exports:
 *   generateCalculationStepsPDF(data) — full step-by-step calc report
 *   generateSedimentBedPDF(data)      — bed physics & recommendations report
 *   generatePreDredgingPDF(data)      — pre-dredge scenario report (with silt)
 *   generatePostDredgingPDF(data)     — post-dredge scenario report (clean bed)
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { SeasonalProfile } from './seasonalProfiles';

// ─── ASCII Sanitiser ──────────────────────────────────────────────────────────
// jsPDF default fonts (Helvetica, Times, Courier) only render Latin-1.
// Any Greek letters, sub/superscripts, arrows etc. show as garbled symbols.
// `s()` replaces those characters with safe ASCII equivalents.
export function s(text: string | number | undefined | null): string {
  if (text == null) return '';
  let t = String(text);
  const map: Record<string, string> = {
    'θ':'theta','Θ':'THETA','σ':'sigma','Σ':'Sigma','ρ':'rho','μ':'u','π':'pi',
    'Δ':'delta','α':'alpha','β':'beta','γ':'gamma','δ':'d','ε':'eps','η':'eta',
    'λ':'lambda','τ':'tau','φ':'phi','χ':'chi','ψ':'psi','ω':'omega','ν':'nu',
    '²':'^2','³':'^3','⁰':'^0','¹':'^1','⁴':'^4','⁵':'^5','⁶':'^6','⁷':'^7','⁸':'^8','⁹':'^9',
    '₀':'0','₁':'1','₂':'2','₃':'3','₄':'4','₅':'5','₆':'6','₇':'7','₈':'8','₉':'9',
    '×':'x','÷':'/','±':'+/-','∞':'inf','√':'sqrt','∫':'int','∂':'d',
    '≥':'>=','≤':'<=','≠':'!=','≈':'~','≡':'=','∝':'~',
    '→':'->','←':'<-','↑':'^','↓':'v','⇒':'=>','⇐':'<=','↔':'<->',
    '°':'deg','′':"'",'″':'"','‰':'permille',
    '–':'-','—':'--','―':'--',
    '‘':"'", '’':"'", '“':'"', '”':'"',
    '«':'<<','»':'>>',
    '…':'...','·':'.','•':'*','◦':'o','▪':'*','▫':'o',
    '✓':'[OK]','✔':'[OK]','✗':'[X]','✘':'[X]','⚠':'[!]','★':'*',
    'Ω':'Ohm','Å':'A','€':'EUR','£':'GBP','¥':'JPY','¢':'c',
    ' ':' ',  // non-breaking space
    '​':'',   // zero-width space
    ' ':' ',  // narrow no-break space
    ' ':' ',  // thin space
    '½':'1/2','⅓':'1/3','⅔':'2/3','¼':'1/4','¾':'3/4','⅕':'1/5',
  };
  for (const [k, v] of Object.entries(map)) {
    t = t.split(k).join(v);
  }
  // Replace any remaining non-ASCII with safe placeholder
  return t.replace(/[^\x20-\x7E\n\r\t]/g, '?');
}

// ─── Brand palette ────────────────────────────────────────────────────────────
const C = {
  navy:   [10, 22, 40]   as [number, number, number],
  gold:   [205, 164, 46] as [number, number, number],
  white:  [255, 255, 255] as [number, number, number],
  black:  [15, 23, 42]   as [number, number, number],
  grey:   [100, 116, 139] as [number, number, number],
  greyLt: [226, 232, 240] as [number, number, number],
  greyBg: [248, 250, 252] as [number, number, number],
  emerald:[5, 150, 105]  as [number, number, number],
  red:    [220, 38, 38]  as [number, number, number],
  amber:  [217, 119, 6]  as [number, number, number],
  blue:   [37, 99, 235]  as [number, number, number],
  siltBrown: [139, 90, 43] as [number, number, number],
  cleanSand: [212, 175, 100] as [number, number, number],
  waterBlue: [88, 145, 200] as [number, number, number],
};

interface PageContext {
  doc: jsPDF;
  pageW: number;
  pageH: number;
  margin: number;
  y: number;
  reportTitle: string;
  reportRef: string;
}

function fmt(v: number, dp = 2): string { return isNaN(v) || !isFinite(v) ? '-' : v.toFixed(dp); }

// ─── Header / footer ──────────────────────────────────────────────────────────
function drawHeader(ctx: PageContext): number {
  const { doc, pageW, margin, reportTitle, reportRef } = ctx;
  doc.setFillColor(...C.navy);
  doc.rect(0, 0, pageW, 22, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...C.white);
  doc.text(s('REYNOLDS & BAUHM LTD'), margin, 14);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(200, 210, 225);
  doc.text(s('Reservoir Aeration Engineering . Smart Water Suite'), margin, 19);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...C.gold);
  doc.text(s(reportTitle), pageW - margin, 14, { align: 'right' });
  doc.setFontSize(8);
  doc.text(s(reportRef), pageW - margin, 19, { align: 'right' });
  return 28;
}

/**
 * Apply document-level PDF metadata so the file shows its origin in any
 * PDF reader (Properties / Document Info panel). Also affects search engines
 * if the PDF is indexed.
 */
const APP_VERSION = '2.0';
const APP_NAME    = 'Reynolds & Bauhm Ltd Smart Wastewater Assessment Tool';
const APP_COPY    = `(c) ${new Date().getFullYear()} Reynolds & Bauhm Ltd. All rights reserved.`;

export function applyPDFMetadata(
  doc: jsPDF,
  opts: {
    title: string;          // visible report title
    subject: string;        // 1-line description
    keywords?: string[];    // searchable keywords
    extra?: string;         // additional notes added to Subject
  },
): void {
  const subject = opts.extra ? `${opts.subject} . ${opts.extra}` : opts.subject;
  const baseKeywords = [
    'Reynolds & Bauhm Ltd',
    'Smart Wastewater Assessment Tool',
    `v${APP_VERSION}`,
    'reservoir aeration',
    'Barcombe Reservoir',
    'Atkins Realis',
    'CONFIDENTIAL',
  ];
  const allKeywords = [...baseKeywords, ...(opts.keywords ?? [])].join(', ');

  try {
    doc.setProperties({
      title:    opts.title,
      subject,
      author:   'Reynolds & Bauhm Ltd',
      keywords: allKeywords,
      creator:  `${APP_NAME} v${APP_VERSION}`,
    });
  } catch { /* jsPDF version variance — fall through */ }

  // Also embed visible meta-data on the LAST page as a small footer block
  // so the metadata is preserved even when PDF is printed and re-scanned.
  // (Drawn during drawFooters via the watermark; here we only set the
  //  PDF Info Dictionary.)
}

/**
 * Draw a CONFIDENTIAL diagonal watermark on the current page.
 * Light pinkish-red, large, rotated -45°, semi-transparent — visible but
 * doesn't obscure body text. Drawn BEFORE the footer line so it sits
 * underneath all content visually.
 */
function drawWatermark(doc: jsPDF, pageW: number, pageH: number): void {
  try {
    // Save state so opacity change is local to the watermark
    // jsPDF supports GState via doc.GState; in some builds it's exported globally.
    const anyDoc = doc as any;
    if (typeof anyDoc.saveGraphicsState === 'function') anyDoc.saveGraphicsState();
    if (typeof anyDoc.GState === 'function' && typeof anyDoc.setGState === 'function') {
      anyDoc.setGState(new anyDoc.GState({ opacity: 0.12 }));
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(72);
    doc.setTextColor(200, 30, 30);  // pinkish-red
    // Centred, rotated -45° around the page centre
    doc.text('CONFIDENTIAL', pageW / 2, pageH / 2, { align: 'center', angle: 45 });
    // Smaller subtitle just below
    doc.setFontSize(14);
    doc.text('Reynolds & Bauhm Smart Water - Atkins Realis', pageW / 2, pageH / 2 + 25,
      { align: 'center', angle: 45 });
    if (typeof anyDoc.restoreGraphicsState === 'function') anyDoc.restoreGraphicsState();
    // Reset colour for following draws
    doc.setTextColor(...C.black);
  } catch {
    // Fallback: simple light grey text, no transparency
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(60);
    doc.setTextColor(240, 200, 200);
    doc.text('CONFIDENTIAL', pageW / 2, pageH / 2, { align: 'center', angle: 45 });
    doc.setTextColor(...C.black);
  }
}

function drawFooters(doc: jsPDF, pageW: number, pageH: number, margin: number, reportRef: string) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    // CONFIDENTIAL watermark first (so footer lines sit on top of it)
    drawWatermark(doc, pageW, pageH);
    // Footer divider + page info
    doc.setDrawColor(...C.greyLt); doc.setLineWidth(0.2);
    doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...C.grey);
    doc.text(s(`${reportRef} . Generated ${new Date().toLocaleString('en-GB')}`), margin, pageH - 7);
    doc.text(s(`Page ${i} of ${total}`), pageW - margin, pageH - 7, { align: 'right' });
    doc.setTextColor(...C.gold);
    doc.text(s(`${APP_NAME} v${APP_VERSION} . CONFIDENTIAL`), pageW / 2, pageH - 7, { align: 'center' });
    // Tiny copyright line under footer
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...C.grey);
    doc.text(s(APP_COPY), pageW / 2, pageH - 3.5, { align: 'center' });
  }
}

function newPage(ctx: PageContext): void { ctx.doc.addPage(); ctx.y = drawHeader(ctx); }
function ensureSpace(ctx: PageContext, needed: number): void {
  if (ctx.y + needed > ctx.pageH - 18) newPage(ctx);
}

// ─── Content blocks ───────────────────────────────────────────────────────────
function sectionHeading(ctx: PageContext, num: string, title: string): void {
  ensureSpace(ctx, 14);
  ctx.doc.setFillColor(...C.navy);
  ctx.doc.rect(ctx.margin, ctx.y, ctx.pageW - 2 * ctx.margin, 7, 'F');
  ctx.doc.setFont('helvetica', 'bold'); ctx.doc.setFontSize(10); ctx.doc.setTextColor(...C.gold);
  ctx.doc.text(s(`Section ${num}`), ctx.margin + 2, ctx.y + 5);
  ctx.doc.setTextColor(...C.white);
  ctx.doc.text(s(title), ctx.margin + 22, ctx.y + 5);
  ctx.y += 11;
}

function paragraph(ctx: PageContext, text: string, opts: { italic?: boolean; small?: boolean; bold?: boolean } = {}): void {
  ctx.doc.setFont('helvetica', opts.italic ? 'italic' : opts.bold ? 'bold' : 'normal');
  ctx.doc.setFontSize(opts.small ? 8 : 9);
  ctx.doc.setTextColor(...C.black);
  const w = ctx.pageW - 2 * ctx.margin;
  const lines = ctx.doc.splitTextToSize(s(text), w);
  ensureSpace(ctx, lines.length * (opts.small ? 3.5 : 4.5) + 2);
  for (const line of lines) {
    if (ctx.y > ctx.pageH - 18) newPage(ctx);
    ctx.doc.text(line, ctx.margin, ctx.y);
    ctx.y += opts.small ? 3.5 : 4.5;
  }
  ctx.y += 2;
}

function formulaBlock(ctx: PageContext, lines: string[]): void {
  const h = lines.length * 4 + 4;
  ensureSpace(ctx, h);
  ctx.doc.setFillColor(...C.greyBg);
  ctx.doc.rect(ctx.margin, ctx.y, ctx.pageW - 2 * ctx.margin, h, 'F');
  ctx.doc.setDrawColor(...C.greyLt);
  ctx.doc.rect(ctx.margin, ctx.y, ctx.pageW - 2 * ctx.margin, h);
  ctx.doc.setFont('courier', 'normal'); ctx.doc.setFontSize(8.5); ctx.doc.setTextColor(...C.navy);
  let y0 = ctx.y + 4;
  for (const ln of lines) { ctx.doc.text(s(ln), ctx.margin + 3, y0); y0 += 4; }
  ctx.y += h + 3;
}

function kvTable(ctx: PageContext, rows: { k: string; v: string; src?: string }[]): void {
  autoTable(ctx.doc, {
    startY: ctx.y, margin: { left: ctx.margin, right: ctx.margin },
    head: [[s('Parameter'), s('Value'), s('Source / formula')]],
    body: rows.map(r => [s(r.k), s(r.v), s(r.src || '')]),
    theme: 'grid',
    headStyles: { fillColor: C.navy, textColor: C.gold, fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8.5, textColor: C.black },
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 'auto', textColor: C.grey, fontStyle: 'italic', fontSize: 7.5 },
    },
    styles: { lineColor: C.greyLt, lineWidth: 0.1 },
  });
  // @ts-expect-error - lastAutoTable dynamic
  ctx.y = (ctx.doc.lastAutoTable?.finalY ?? ctx.y) + 5;
}

function dataTable(ctx: PageContext, head: string[], rows: (string | number)[][]): void {
  autoTable(ctx.doc, {
    startY: ctx.y, margin: { left: ctx.margin, right: ctx.margin },
    head: [head.map(s)], body: rows.map(r => r.map(c => s(String(c)))),
    theme: 'striped',
    headStyles: { fillColor: C.navy, textColor: C.gold, fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 7.5, textColor: C.black },
    alternateRowStyles: { fillColor: C.greyBg },
    styles: { lineColor: C.greyLt, lineWidth: 0.1 },
  });
  // @ts-expect-error
  ctx.y = (ctx.doc.lastAutoTable?.finalY ?? ctx.y) + 5;
}

function spacer(ctx: PageContext, h = 4): void { ctx.y += h; }

function captionLine(ctx: PageContext, text: string): void {
  ctx.doc.setFont('helvetica', 'italic'); ctx.doc.setFontSize(7.5); ctx.doc.setTextColor(...C.grey);
  ctx.doc.text(s(text), ctx.pageW / 2, ctx.y, { align: 'center' });
  ctx.y += 5;
}

// ═════════════════════════════════════════════════════════════════════════════
// VECTOR VISUAL DRAWING
// ═════════════════════════════════════════════════════════════════════════════

// Reservoir polygon (same coordinates as web pages, 1 unit ~ 1 metre)
const RESERVOIR_POLY: [number, number][] = [
  [85, 18], [95, 15], [112, 14], [128, 18], [142, 24],
  [155, 34], [163, 48], [168, 65], [170, 82],
  [169, 100], [166, 118], [168, 136],
  [170, 155], [169, 173], [167, 192],
  [162, 210], [156, 228], [148, 245],
  [138, 262], [127, 277], [116, 290],
  [108, 302], [102, 315], [98, 330],
  [94, 345], [91, 360], [89, 375],
  [88, 390], [87, 403], [87, 415], [85, 432],
  [82, 420], [79, 408], [76, 396], [73, 383],
  [70, 368], [67, 352], [64, 336], [60, 320],
  [56, 303], [51, 287], [47, 270],
  [42, 253], [39, 236], [37, 218],
  [35, 200], [34, 182], [35, 165],
  [38, 148], [38, 130], [36, 112],
  [37, 94], [40, 77], [46, 62],
  [52, 48], [60, 35], [72, 25],
];
const POLY_X_MIN = 34, POLY_X_MAX = 170, POLY_Y_MIN = 14, POLY_Y_MAX = 432;
const POLY_W = POLY_X_MAX - POLY_X_MIN;     // 136 m
const POLY_H = POLY_Y_MAX - POLY_Y_MIN;     // 418 m

/**
 * Draw a polygon by issuing line segments. Style: 'F' fill, 'S' stroke, 'FD' both.
 */
function drawPolygon(doc: jsPDF, pts: { x: number; y: number }[], style: 'F' | 'S' | 'FD'): void {
  if (pts.length < 3) return;
  const deltas: [number, number][] = [];
  for (let i = 1; i < pts.length; i++) {
    deltas.push([pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y]);
  }
  deltas.push([pts[0].x - pts[pts.length - 1].x, pts[0].y - pts[pts.length - 1].y]);
  doc.lines(deltas, pts[0].x, pts[0].y, [1, 1], style);
}

interface PlanViewOpts {
  bedColour: [number, number, number];
  bedLabel: string;
  modules?: { x: number; y: number }[];      // in reservoir coords
  arrayLabels?: { id: string; x: number; y: number }[];
  deepZones?: { cx: number; cy: number; rx: number; ry: number; depthLabel: string }[];
  gridSpacingM?: number;          // if set, draw grid at this spacing (e.g. 20 m)
  showCoordinates?: boolean;      // show E/N coordinate labels at grid intersections
}

/**
 * Draw a scaled plan-view of the reservoir into a PDF region (x, y, w, h).
 * Returns the y coordinate just below the drawing.
 */
function drawReservoirPlanView(doc: jsPDF, x: number, y: number, w: number, h: number, opts: PlanViewOpts): number {
  const scale = Math.min(w / POLY_W, h / POLY_H);
  const drawW = POLY_W * scale;
  const drawH = POLY_H * scale;
  const ox = x + (w - drawW) / 2;
  const oy = y;
  // Transform reservoir coord (m) to PDF mm
  const tx = (rx: number) => ox + (rx - POLY_X_MIN) * scale;
  const ty = (ry: number) => oy + (ry - POLY_Y_MIN) * scale;

  // Frame
  doc.setDrawColor(...C.greyLt); doc.setLineWidth(0.3);
  doc.rect(x, y, w, h);

  // Grid (drawn BEFORE reservoir fill so polygon overlays it; lines also extend across whole drawing area)
  if (opts.gridSpacingM && opts.gridSpacingM > 0) {
    const g = opts.gridSpacingM;
    doc.setDrawColor(220, 230, 240); doc.setLineWidth(0.15);
    // Vertical lines
    for (let mx = Math.ceil(POLY_X_MIN / g) * g; mx <= POLY_X_MAX; mx += g) {
      const xp = tx(mx);
      doc.line(xp, y + 0.5, xp, y + h - 0.5);
    }
    // Horizontal lines
    for (let my = Math.ceil(POLY_Y_MIN / g) * g; my <= POLY_Y_MAX; my += g) {
      const yp = ty(my);
      doc.line(x + 0.5, yp, x + w - 0.5, yp);
    }
    // Grid labels (E / N) at top and right
    if (opts.showCoordinates !== false) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(5); doc.setTextColor(...C.grey);
      for (let mx = Math.ceil(POLY_X_MIN / g) * g; mx <= POLY_X_MAX; mx += g) {
        const xp = tx(mx);
        doc.text(`${mx}E`, xp, y - 0.8, { align: 'center' });
      }
      for (let my = Math.ceil(POLY_Y_MIN / g) * g; my <= POLY_Y_MAX; my += g) {
        const yp = ty(my);
        doc.text(`${my}N`, x - 0.8, yp + 1, { align: 'right' });
      }
    }
  }

  // Reservoir fill
  const polyPts = RESERVOIR_POLY.map(([rx, ry]) => ({ x: tx(rx), y: ty(ry) }));
  doc.setFillColor(...opts.bedColour);
  drawPolygon(doc, polyPts, 'F');

  // Deep zones (drawn ON TOP of bed colour, with clip not applied — they're inside anyway)
  if (opts.deepZones) {
    for (const z of opts.deepZones) {
      doc.setFillColor(...C.blue);
      doc.setGState && doc.setGState(new (doc as any).GState({ opacity: 0.55 }));
      const cx = tx(z.cx), cy = ty(z.cy), rx = z.rx * scale, ry = z.ry * scale;
      doc.ellipse(cx, cy, rx, ry, 'F');
      doc.setGState && doc.setGState(new (doc as any).GState({ opacity: 1.0 }));
    }
  }

  // Outline stroke
  doc.setDrawColor(...C.amber); doc.setLineWidth(0.6);
  drawPolygon(doc, polyPts, 'S');

  // Module positions
  if (opts.modules) {
    doc.setFillColor(...C.gold);
    doc.setDrawColor(...C.navy); doc.setLineWidth(0.1);
    for (const m of opts.modules) {
      const size = Math.max(0.8, 2.5 * scale);
      doc.rect(tx(m.x) - size / 2, ty(m.y) - size / 2, size, size, 'FD');
    }
  }

  // Array labels (letter inside small circle)
  if (opts.arrayLabels) {
    for (const lab of opts.arrayLabels) {
      const cx = tx(lab.x), cy = ty(lab.y);
      doc.setFillColor(...C.white); doc.setDrawColor(...C.gold); doc.setLineWidth(0.4);
      doc.circle(cx, cy, 3, 'FD');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(...C.navy);
      doc.text(s(lab.id), cx, cy + 1.8, { align: 'center' });
    }
  }

  // Scale bar 50 m
  const sx0 = x + 4;
  const sy0 = y + h - 6;
  const sb = 50 * scale;
  doc.setDrawColor(...C.black); doc.setLineWidth(0.4);
  doc.line(sx0, sy0, sx0 + sb, sy0);
  doc.line(sx0, sy0 - 1.5, sx0, sy0 + 1.5);
  doc.line(sx0 + sb, sy0 - 1.5, sx0 + sb, sy0 + 1.5);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...C.black);
  doc.text('50 m', sx0 + sb / 2, sy0 - 2, { align: 'center' });

  // North arrow
  const nx = x + w - 6, ny = y + 8;
  doc.setFillColor(...C.black);
  doc.triangle(nx - 1.8, ny + 3, nx + 1.8, ny + 3, nx, ny - 2.5, 'F');
  doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
  doc.text('N', nx, ny + 6.5, { align: 'center' });

  // Bed colour legend
  doc.setFillColor(...opts.bedColour);
  doc.rect(x + 3, y + 3, 3, 3, 'F');
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...C.black);
  doc.text(s(`Bed: ${opts.bedLabel}`), x + 7.5, y + 5.2);

  return y + h + 2;
}

interface CrossSectionOpts {
  title: string;
  scenarioLabel: string;
  // Geometry (metres)
  totalDepthM: number;      // water depth from surface to top of silt (or bed)
  siltDepthM: number;       // thickness of silt layer (0 for post-dredge clean)
  bedColour: [number, number, number];
  siltColour: [number, number, number];
  // Module
  mountingHeightM: number;  // height of module frame above silt/bed surface
  moduleWidthM: number;     // physical width (2 m)
  diffuserCount: number;
  bubbleDiamMm: number;
  plumeSurfaceRadiusM: number;
  arrayLabel: string;
  // Diffuser type (changes visual + plume cone shape)
  diffuserShape?: 'disc' | 'tube';
  tubeLengthM?: number;
  tubeCount?: number;
  // Annotations
  doAfter?: number;         // mg/L after aeration (caption)
  notes?: string[];
}

/**
 * Draw a side-view cross section showing water column, bed, silt layer, module,
 * bubble plume and labels. Returns y below the drawing.
 */
function drawBedCrossSection(doc: jsPDF, x: number, y: number, w: number, h: number, opts: CrossSectionOpts): number {
  // Frame
  doc.setDrawColor(...C.greyLt); doc.setLineWidth(0.3);
  doc.rect(x, y, w, h);

  // Title bar
  doc.setFillColor(...C.navy);
  doc.rect(x, y, w, 6, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...C.gold);
  doc.text(s(opts.title), x + 2, y + 4);
  doc.setTextColor(...C.white); doc.setFontSize(7);
  doc.text(s(opts.scenarioLabel), x + w - 2, y + 4, { align: 'right' });

  // Drawing area below title
  const dx = x + 2;
  const dy = y + 8;
  const dw = w - 4;
  const dh = h - 12;

  // Vertical scale: total depth + 0.5 m headroom + 1.0 m below silt
  const totalH = opts.totalDepthM + opts.siltDepthM + 1.5;
  const sV = dh / totalH;

  // Y positions in PDF mm
  const ySurface = dy + 0.5 * sV;
  const ySiltTop = ySurface + opts.totalDepthM * sV;
  const yBedBase = ySiltTop + opts.siltDepthM * sV;

  // Water column (light blue gradient via fill — single colour)
  doc.setFillColor(...C.waterBlue);
  doc.setGState && doc.setGState(new (doc as any).GState({ opacity: 0.18 }));
  doc.rect(dx, ySurface, dw, ySiltTop - ySurface, 'F');
  doc.setGState && doc.setGState(new (doc as any).GState({ opacity: 1.0 }));

  // Silt layer (if present)
  if (opts.siltDepthM > 0.01) {
    doc.setFillColor(...opts.siltColour);
    doc.rect(dx, ySiltTop, dw, opts.siltDepthM * sV, 'F');
    // Cross-hatching to indicate silt
    doc.setDrawColor(60, 30, 10); doc.setLineWidth(0.15);
    const step = 2;
    for (let i = 0; i < dw + (opts.siltDepthM * sV); i += step) {
      const x1 = dx + i, y1 = ySiltTop;
      const x2 = dx + i - opts.siltDepthM * sV, y2 = yBedBase;
      if (x2 > dx && x1 < dx + dw) {
        doc.line(Math.max(dx, x1), y1 + Math.max(0, (x1 - (dx + dw)) ),
                 Math.min(dx + dw, x2), y2);
      }
    }
  }

  // Hard bed below silt (or directly below water if post-dredge)
  doc.setFillColor(...opts.bedColour);
  doc.rect(dx, yBedBase, dw, dh - (yBedBase - dy), 'F');

  // Surface line (water)
  doc.setDrawColor(...C.blue); doc.setLineWidth(0.6);
  doc.line(dx, ySurface, dx + dw, ySurface);
  // Small wave indicators
  doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(...C.blue);
  doc.text('~~~~~~~~~~~~~~~~~~~~~~~~~~', dx + dw / 2, ySurface - 0.5, { align: 'center' });

  // Silt-top line
  if (opts.siltDepthM > 0.01) {
    doc.setDrawColor(...C.siltBrown); doc.setLineWidth(0.4); doc.setLineDashPattern([1, 1], 0);
    doc.line(dx, ySiltTop, dx + dw, ySiltTop);
    doc.setLineDashPattern([], 0);
  }

  // Module frame in centre
  const modX = dx + dw / 2;
  const moduleSizeMm = Math.min(opts.moduleWidthM * sV, 14);
  const yModule = ySiltTop - opts.mountingHeightM * sV;   // top of module
  const moduleW = moduleSizeMm;
  // Frame legs
  doc.setDrawColor(...C.grey); doc.setLineWidth(0.4);
  doc.line(modX - moduleW / 2, ySiltTop, modX - moduleW / 2, yModule);
  doc.line(modX + moduleW / 2, ySiltTop, modX + moduleW / 2, yModule);
  // Frame plate
  doc.setFillColor(...C.grey);
  doc.rect(modX - moduleW / 2, yModule - 1.2, moduleW, 1.2, 'F');
  // Diffuser visual — TYPE-DEPENDENT
  if (opts.diffuserShape === 'tube') {
    // Tubes — render as thick horizontal bars on top of frame, with pore dots
    const nTubes = Math.max(1, Math.min(4, opts.tubeCount ?? 2));
    const tubeRenderLen = moduleW * 0.9 * Math.min(1, (opts.tubeLengthM ?? 1.8) / 2.0);
    for (let i = 0; i < nTubes; i++) {
      const ty = yModule - 1.6 - i * 0.7;
      doc.setFillColor(...C.amber);
      doc.rect(modX - tubeRenderLen / 2, ty - 0.35, tubeRenderLen, 0.7, 'F');
      // Pore dots along tube
      const nDots = 12;
      doc.setFillColor(60, 90, 120);
      for (let p = 0; p < nDots; p++) {
        doc.circle(modX - tubeRenderLen / 2 + (tubeRenderLen / (nDots + 1)) * (p + 1), ty, 0.18, 'F');
      }
    }
  } else {
    // Discs — small filled circles
    const nDiff = Math.min(opts.diffuserCount, 8);
    for (let i = 0; i < nDiff; i++) {
      const dxd = modX - moduleW / 2 + (moduleW / (nDiff + 1)) * (i + 1);
      doc.setFillColor(...C.amber);
      doc.circle(dxd, yModule - 1.6, 0.5, 'F');
    }
  }
  // Module label
  doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(...C.navy);
  doc.text(s(`Array ${opts.arrayLabel}`), modX, yModule - 3, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(...C.grey);
  const subLabel = opts.diffuserShape === 'tube'
    ? `${opts.tubeCount ?? 2} tubes x ${(opts.tubeLengthM ?? 1.8).toFixed(1)}m, h=${opts.mountingHeightM.toFixed(2)}m`
    : `${opts.diffuserCount} discs, h=${opts.mountingHeightM.toFixed(2)}m`;
  doc.text(s(subLabel), modX, yModule + 3, { align: 'center' });

  // Bubble plume — TYPE-DEPENDENT shape
  // Disc: triangular cone (apex at module)
  // Tube: parallel-sided curtain (line source per Wuest 1992)
  const plumeRadMm = Math.min(opts.plumeSurfaceRadiusM * sV, dw / 3);
  doc.setDrawColor(...C.waterBlue); doc.setLineWidth(0.35); doc.setLineDashPattern([1.2, 1.2], 0);
  if (opts.diffuserShape === 'tube') {
    // Curtain: same width at module and at surface (line plume keeps narrower spread)
    const halfW = plumeRadMm * 0.75;
    doc.line(modX - halfW, yModule - 2, modX - halfW, ySurface);
    doc.line(modX + halfW, yModule - 2, modX + halfW, ySurface);
  } else {
    // Cone: narrow at module, wide at surface
    doc.line(modX - moduleW / 2, yModule - 2, modX - plumeRadMm, ySurface);
    doc.line(modX + moduleW / 2, yModule - 2, modX + plumeRadMm, ySurface);
  }
  doc.setLineDashPattern([], 0);
  // Bubble glyphs (small circles)
  for (let i = 0; i < 5; i++) {
    const frac = i / 5;
    const bsz = (opts.bubbleDiamMm / 3) * (1 + frac * 0.5);
    const by = yModule - 2 - frac * (yModule - 2 - ySurface) - 1;
    doc.setFillColor(220, 240, 255);
    doc.circle(modX - 2 + i * 1, by, Math.min(0.6, bsz / 3), 'F');
    doc.circle(modX + 1 - i * 0.5, by - 1, Math.min(0.6, bsz / 3), 'F');
  }

  // Depth labels (right side)
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...C.black);
  doc.text(s('Surface (0 m)'), dx + dw - 1, ySurface - 0.5, { align: 'right' });
  doc.text(s(`Top of bed (${opts.totalDepthM.toFixed(1)} m)`), dx + dw - 1, ySiltTop + 2.5, { align: 'right' });
  if (opts.siltDepthM > 0.01) {
    doc.text(s(`Below silt (${(opts.totalDepthM + opts.siltDepthM).toFixed(1)} m)`),
      dx + dw - 1, yBedBase + 2.5, { align: 'right' });
  }
  // Mounting height annotation
  doc.setDrawColor(...C.navy); doc.setLineWidth(0.3);
  doc.line(dx + 4, yModule, dx + 4, ySiltTop);
  doc.text(s(`h = ${opts.mountingHeightM.toFixed(2)} m`), dx + 5.5, (yModule + ySiltTop) / 2 + 1);
  // Bubble Ø annotation
  doc.text(s(`bubble Ø ${opts.bubbleDiamMm.toFixed(2)} mm`), dx + 2, ySurface + (yModule - ySurface) / 2);

  // Caption below
  if (opts.doAfter !== undefined) {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(6.5); doc.setTextColor(...C.grey);
    doc.text(s(`Expected DO after aeration: ${opts.doAfter.toFixed(1)} mg/L`),
      x + w / 2, y + h - 1.5, { align: 'center' });
  }

  return y + h + 2;
}

// ═════════════════════════════════════════════════════════════════════════════
// REPORT ASSUMPTIONS BLOCK (shows seasonal profile + all data used)
// ═════════════════════════════════════════════════════════════════════════════
function drawAssumptionsBlock(
  ctx: PageContext,
  profile: SeasonalProfile,
  designSpec: {
    diffuserCount: number; diffuserType: string; diffuserDiamMm: number;
    poreSizeUm: number; airflowPerDiffNm3h: number; mountingHeightM: number;
    cornerOffsetM: number; diffuserDropKpa: number; blowerEff: number; safetyPct: number;
    alphaFactor: number; betaFactor: number;
    tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
    tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
  },
  reservoirInfo: { areaHa: number; meanDepthM: number; surveyRef: string },
): void {
  sectionHeading(ctx, 'A', 'Design Assumptions & Input Data');
  paragraph(ctx, 'This report is generated from the following assumptions. All calculations downstream use these values directly. Where values differ from your live session, the profile values take precedence.', { italic: true });

  // Sub-heading: Seasonal water profile
  ctx.doc.setFillColor(...C.gold);
  ensureSpace(ctx, 6);
  ctx.doc.rect(ctx.margin, ctx.y, ctx.pageW - 2 * ctx.margin, 5, 'F');
  ctx.doc.setFont('helvetica', 'bold'); ctx.doc.setFontSize(8.5); ctx.doc.setTextColor(...C.navy);
  ctx.doc.text(s(`A.1  Seasonal Water Profile: ${profile.label}`), ctx.margin + 2, ctx.y + 3.5);
  ctx.y += 7;

  kvTable(ctx, [
    { k: 'Profile ID', v: profile.id, src: 'selected by operator' },
    { k: 'Description', v: profile.desc },
    { k: 'Months covered', v: profile.monthsRange },
    { k: 'Water temperature', v: `${profile.waterTempC.toFixed(1)} degC`, src: profile.source },
    { k: 'Baseline dissolved O2', v: `${profile.backgroundDOMgL.toFixed(1)} mg/L`, src: profile.source },
    { k: 'Sediment oxygen demand (SOD)', v: `${profile.sodRateG_m2_d.toFixed(2)} g O2/m2/d`, src: 'Hickey & Gibbs 2009 adjusted' },
    { k: 'Algal bloom risk', v: profile.algalRisk.toUpperCase() },
    { k: 'Stratification risk', v: profile.stratificationRisk.toUpperCase() },
    { k: 'Alpha factor (use in AOTR)', v: profile.alphaFactor.toFixed(2), src: 'biological correction' },
    { k: 'Beta factor (use in AOTR)', v: profile.betaFactor.toFixed(2), src: 'O2 saturation correction' },
    { k: 'Operational priority', v: profile.priority },
    { k: 'Data source', v: profile.source },
  ]);

  // Sub-heading: Reservoir basis
  ensureSpace(ctx, 7);
  ctx.doc.setFillColor(...C.gold);
  ctx.doc.rect(ctx.margin, ctx.y, ctx.pageW - 2 * ctx.margin, 5, 'F');
  ctx.doc.setFont('helvetica', 'bold'); ctx.doc.setFontSize(8.5); ctx.doc.setTextColor(...C.navy);
  ctx.doc.text(s('A.2  Reservoir Geometry Basis'), ctx.margin + 2, ctx.y + 3.5);
  ctx.y += 7;

  kvTable(ctx, [
    { k: 'Survey reference', v: reservoirInfo.surveyRef },
    { k: 'Reservoir plan area', v: `${reservoirInfo.areaHa.toFixed(2)} ha`, src: 'shoelace formula on digitised polygon' },
    { k: 'Mean depth (sampled)', v: `${reservoirInfo.meanDepthM.toFixed(2)} m`, src: 'volume / area' },
    { k: 'Polygon vertices', v: '57', src: 'traced from J2020 drawing' },
  ]);

  // Sub-heading: Equipment
  ensureSpace(ctx, 7);
  ctx.doc.setFillColor(...C.gold);
  ctx.doc.rect(ctx.margin, ctx.y, ctx.pageW - 2 * ctx.margin, 5, 'F');
  ctx.doc.setFont('helvetica', 'bold'); ctx.doc.setFontSize(8.5); ctx.doc.setTextColor(...C.navy);
  ctx.doc.text(s('A.3  Equipment Specification'), ctx.margin + 2, ctx.y + 3.5);
  ctx.y += 7;

  const isTube = designSpec.diffuserType === 'hdpe_porous_tube' || designSpec.diffuserType === 'epdm_porous_tube';
  kvTable(ctx, [
    { k: 'Module frame size', v: '2.0 x 2.0 m (standard)', src: 'product standard' },
    { k: 'Diffuser type', v: designSpec.diffuserType, src: 'client selection' },
    { k: 'Diffuser shape (plume class)', v: isTube ? 'TUBE - line source (Wuest 1992)' : 'DISC - point source (Seol 2007)',
      src: 'derives plume physics' },
    { k: isTube ? 'Tube outer diameter' : 'Diffuser diameter',
      v: isTube ? `${(designSpec as any).tubeDiamMm ?? 75} mm` : `${designSpec.diffuserDiamMm} mm`,
      src: 'client selection' },
    ...(isTube ? [
      { k: 'Tube length (each)', v: `${((designSpec as any).tubeLengthM ?? 1.8).toFixed(2)} m (max 2.0)`, src: 'frame side length' },
      { k: 'Tubes per module', v: String((designSpec as any).tubeCount ?? 2), src: 'client selection' },
      { k: 'Total tube length per module',
        v: `${(((designSpec as any).tubeLengthM ?? 1.8) * ((designSpec as any).tubeCount ?? 2)).toFixed(2)} m`,
        src: 'tubes x length' },
      { k: 'Tube pattern on frame', v: String((designSpec as any).tubePattern ?? 'parallel'), src: 'layout choice' },
      { k: 'Tube centre offset', v: `${((designSpec as any).tubeCentreOffsetM ?? 0.5).toFixed(2)} m`, src: 'parallel pattern only' },
      { k: 'Pore density along tube', v: `${(designSpec as any).porePerCm ?? 8} pores/cm`, src: 'manufacturer' },
      { k: 'Total pores per module',
        v: Math.round(((designSpec as any).porePerCm ?? 8) * ((designSpec as any).tubeLengthM ?? 1.8) * 100 * ((designSpec as any).tubeCount ?? 2)).toLocaleString(),
        src: 'pores/cm x L x n_tubes' },
    ] : [
      { k: 'Diffusers per module', v: String(designSpec.diffuserCount), src: 'client selection' },
      { k: 'Corner offset within frame', v: `${designSpec.cornerOffsetM.toFixed(2)} m`, src: 'frame geometry' },
    ]),
    { k: 'Pore size', v: `${designSpec.poreSizeUm} um`, src: 'client selection' },
    { k: 'Mounting height above bed', v: `${designSpec.mountingHeightM.toFixed(2)} m`, src: 'site / design' },
    { k: isTube ? 'Airflow per single tube' : 'Airflow per single diffuser',
      v: `${designSpec.airflowPerDiffNm3h.toFixed(2)} Nm3/h`, src: 'manufacturer rated' },
    { k: 'Diffuser pressure drop', v: `${designSpec.diffuserDropKpa.toFixed(2)} kPa`, src: 'manufacturer data' },
    { k: 'Blower efficiency', v: designSpec.blowerEff.toFixed(2), src: 'PD blower typical' },
    { k: 'Pressure safety margin', v: `${designSpec.safetyPct}%`, src: 'design margin' },
  ]);
}

// ═════════════════════════════════════════════════════════════════════════════
// HIERARCHY BREAKDOWN (Diffuser -> Module -> Array -> System)
// ═════════════════════════════════════════════════════════════════════════════
function drawHierarchyBlock(
  ctx: PageContext,
  perDiffuser: { qNm3h: number; bubbleMm: number; soteMperM: number; poresEach: number },
  perModule:   { nDiff: number; qNm3h: number; sotrKgh: number; aotrKgh: number; kw: number; poresTotal: number },
  arrays:      { id: string; nModules: number; nDiff: number; qNm3h: number; aotrKgh: number; kw: number; depthM: number }[],
  totals:      { nArrays: number; nModules: number; nDiff: number; qNm3h: number; sotrKgh: number; aotrKgh: number; kw: number },
): void {
  sectionHeading(ctx, 'H', 'Calculation Hierarchy : Diffuser -> Module -> Array -> System');
  paragraph(ctx, 'The aeration system is built up in four levels. Each level inherits and aggregates values from the level below. The table sequence below shows how a single diffuser scales up to the full reservoir system.');

  // Level 1: Single diffuser
  ensureSpace(ctx, 6);
  ctx.doc.setFillColor(...C.greyBg);
  ctx.doc.rect(ctx.margin, ctx.y, ctx.pageW - 2 * ctx.margin, 5, 'F');
  ctx.doc.setFont('helvetica', 'bold'); ctx.doc.setFontSize(8.5); ctx.doc.setTextColor(...C.navy);
  ctx.doc.text(s(`LEVEL 1  Single Diffuser  ->  ${perDiffuser.poresEach} pores`), ctx.margin + 2, ctx.y + 3.5);
  ctx.y += 7;
  dataTable(ctx,
    ['Parameter', 'Value', 'Unit', 'Note'],
    [
      ['Air flow (rated)',          fmt(perDiffuser.qNm3h, 2),   'Nm3/h', 'manufacturer rating'],
      ['Bubble diameter at orifice', fmt(perDiffuser.bubbleMm, 2), 'mm',    'Fritz 1935 / Loubiere 2003'],
      ['SOTE per metre submergence', fmt(perDiffuser.soteMperM, 2), '%/m',   'Stenstrom & Rosso 2008'],
      ['Pores per disc',             String(perDiffuser.poresEach), 'pores', '0.35 x pi x r^2 (EPDM)'],
    ]);

  // Level 2: Module
  ensureSpace(ctx, 6);
  ctx.doc.setFillColor(...C.greyBg);
  ctx.doc.rect(ctx.margin, ctx.y, ctx.pageW - 2 * ctx.margin, 5, 'F');
  ctx.doc.setFont('helvetica', 'bold'); ctx.doc.setFontSize(8.5); ctx.doc.setTextColor(...C.navy);
  ctx.doc.text(s(`LEVEL 2  Module = ${perModule.nDiff} diffusers on 2x2 m frame  ->  ${perModule.poresTotal} pores`), ctx.margin + 2, ctx.y + 3.5);
  ctx.y += 7;
  dataTable(ctx,
    ['Parameter', 'Value', 'Unit', 'Derivation'],
    [
      ['Diffusers per module',        String(perModule.nDiff),       '',       'design choice'],
      ['Total pores per module',      String(perModule.poresTotal),  'pores',  'pores_each x n_diff'],
      ['Module air flow',             fmt(perModule.qNm3h, 2),       'Nm3/h',  `${perModule.nDiff} x ${perDiffuser.qNm3h.toFixed(2)}`],
      ['Module SOTR',                 fmt(perModule.sotrKgh, 3),     'kg O2/h','Q x rho_air x w_O2 x SOTE/100'],
      ['Module AOTR',                 fmt(perModule.aotrKgh, 3),     'kg O2/h','SOTR x alpha x beta x ...'],
      ['Module blower power',         fmt(perModule.kw, 3),          'kW',     'isentropic compression'],
    ]);

  // Level 3: Arrays
  ensureSpace(ctx, 6);
  ctx.doc.setFillColor(...C.greyBg);
  ctx.doc.rect(ctx.margin, ctx.y, ctx.pageW - 2 * ctx.margin, 5, 'F');
  ctx.doc.setFont('helvetica', 'bold'); ctx.doc.setFontSize(8.5); ctx.doc.setTextColor(...C.navy);
  ctx.doc.text(s(`LEVEL 3  Arrays = ${arrays.length} groups of modules (per depth zone)`), ctx.margin + 2, ctx.y + 3.5);
  ctx.y += 7;
  dataTable(ctx,
    ['Array', 'Modules', 'Diffusers', 'Depth (m)', 'Air (Nm3/h)', 'AOTR (kg/h)', 'kW'],
    arrays.map(a => [
      a.id, a.nModules, a.nDiff, fmt(a.depthM, 1),
      fmt(a.qNm3h, 1), fmt(a.aotrKgh, 2), fmt(a.kw, 2),
    ]));

  // Level 4: System totals
  ensureSpace(ctx, 6);
  ctx.doc.setFillColor(...C.gold);
  ctx.doc.rect(ctx.margin, ctx.y, ctx.pageW - 2 * ctx.margin, 5, 'F');
  ctx.doc.setFont('helvetica', 'bold'); ctx.doc.setFontSize(8.5); ctx.doc.setTextColor(...C.navy);
  ctx.doc.text(s('LEVEL 4  Total System  ->  the complete installation'), ctx.margin + 2, ctx.y + 3.5);
  ctx.y += 7;
  dataTable(ctx,
    ['Component', 'Quantity', 'Aggregated value', 'Note'],
    [
      ['Arrays',         totals.nArrays,                    String(totals.nArrays),                       'one sub-manifold each'],
      ['Modules',        totals.nModules,                    String(totals.nModules),                      'sum across all arrays'],
      ['Diffusers',      totals.nDiff,                       String(totals.nDiff),                         `modules x ${perModule.nDiff}`],
      ['Total airflow',  '-',                                `${fmt(totals.qNm3h, 0)} Nm3/h`,              'continuous duty'],
      ['Total SOTR',     '-',                                `${fmt(totals.sotrKgh, 2)} kg O2/h`,           'standard conditions'],
      ['Total AOTR',     '-',                                `${fmt(totals.aotrKgh, 2)} kg O2/h`,           'site-corrected actual'],
      ['Total power',    '-',                                `${fmt(totals.kw, 1)} kW`,                     'blower duty'],
      ['Specific air',   '-',                                `${fmt(totals.qNm3h / Math.max(0.01, totals.nDiff), 2)} Nm3/h/diff`, 'sanity check'],
      ['Aeration efficiency', '-',                           `${fmt(totals.aotrKgh / Math.max(0.001, totals.kw), 2)} kg O2/kWh`,  'good > 1.2'],
    ]);
}

// Helper to recompute seasonal-adjusted O2 transfer
function seasonalAOTR(baseSotr_kgh: number, profile: SeasonalProfile, dosatMgL: number): number {
  return baseSotr_kgh
    * profile.alphaFactor
    * profile.betaFactor
    * Math.pow(1.024, profile.waterTempC - 20)
    * Math.max(0, dosatMgL - profile.backgroundDOMgL) / 9.09;
}

// ═════════════════════════════════════════════════════════════════════════════
// CALCULATION STEPS REPORT
// ═════════════════════════════════════════════════════════════════════════════
export interface CalcStepData {
  spec: {
    diffuserCount: number; cornerOffsetM: number; mountingHeightM: number;
    diffuserType: string;  diffuserDiamMm: number; poreSizeUm: number;
    airflowPerDiffNm3h: number; diffuserDropKpa: number; waterDepthM: number;
    waterTempC: number; currentDoMgL: number; alphaFactor: number;
    betaFactor: number; blowerEff: number; safetyPct: number;
    tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
    tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
  };
  reservoir: { areaHa: number; meanDepth: number; volumeM3: number;
    shallow_m2: number; medium_m2: number; deep_m2: number; deepest_m2: number };
  physics: { T: number; rho: number; mu: number; nu: number; sigma: number;
    dosat: number; poresEach: number; poresTotal: number; qPerDiff: number;
    qPerModule: number; d_b: number; v_rise: number; soteM: number };
  optimization: { totalSOD: number; sodSafety: number; modsBySOD: number;
    rMix: number; areaPerMod: number; effArea: number; modsByCov: number;
    rec: number; aotrTypical: number };
  arrays: {
    id: string; zoneLabel: string; meanDepthM: number; moduleCount: number;
    diffPerModule: number; totalDiffusers: number; spacingM: number;
    airflowNm3h: number; aotrKgh: number; blowerKw: number;
    centroidX: number; centroidY: number;
  }[];
  totals: { arrays: number; modules: number; diffusers: number;
    airflow: number; aotr: number; sotr: number; kw: number };
  validation: { lbl: string; pass: boolean; val: string }[];
}

export function generateCalculationStepsPDF(d: CalcStepData, profile: SeasonalProfile): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const reportRef = `RB-RES-CALC-${new Date().toISOString().slice(0, 10)}`;
  applyPDFMetadata(doc, {
    title:    `Reservoir Calculation Report - Barcombe Reservoir`,
    subject:  `Step-by-step calculation walkthrough for ${profile.label} profile`,
    extra:    reportRef,
    keywords: ['calculation steps', 'SOTR', 'AOTR', 'bubble physics', profile.id],
  });
  const ctx: PageContext = { doc, pageW, pageH, margin, y: 0, reportTitle: 'Reservoir Calculation Report', reportRef };

  // ─── COVER ─────────────────────────────────────────────────────────────
  ctx.y = drawHeader(ctx);
  doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(...C.navy);
  doc.text(s('RESERVOIR AERATION DESIGN'), pageW / 2, 55, { align: 'center' });
  doc.setFontSize(14); doc.setTextColor(...C.grey);
  doc.text(s('Calculation & Engineering Report'), pageW / 2, 64, { align: 'center' });
  doc.setDrawColor(...C.gold); doc.setLineWidth(0.8);
  doc.line(margin, 72, pageW - margin, 72);

  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...C.navy);
  doc.text(s('Project: Barcombe Reservoir Aeration'), pageW / 2, 84, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...C.grey);
  doc.text(s('East Sussex, United Kingdom'), pageW / 2, 92, { align: 'center' });
  doc.text(s('Client: Atkins Realis'), pageW / 2, 98, { align: 'center' });

  // System summary box
  doc.setFillColor(255, 251, 235); doc.setDrawColor(...C.gold); doc.setLineWidth(0.5);
  doc.rect(margin, 115, pageW - 2 * margin, 32, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...C.gold);
  doc.text(s('SYSTEM SUMMARY'), pageW / 2, 124, { align: 'center' });
  doc.setFontSize(16); doc.setTextColor(...C.navy);
  doc.text(s(`${d.totals.arrays} arrays . ${d.totals.modules} modules . ${d.totals.diffusers} diffusers`),
    pageW / 2, 134, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...C.black);
  doc.text(s(`${fmt(d.totals.airflow, 0)} Nm^3/h . ${fmt(d.totals.aotr, 2)} kg O2/h . ${fmt(d.totals.kw, 1)} kW`),
    pageW / 2, 142, { align: 'center' });

  // Reservoir layout on cover
  ctx.y = drawReservoirPlanView(doc, margin, 155, pageW - 2 * margin, 90, {
    bedColour: [124, 74, 42], bedLabel: 'Reservoir bed (current condition)',
    modules: d.arrays.flatMap(a => Array.from({ length: a.moduleCount }, (_, i) => ({
      x: a.centroidX + (i % 3 - 1) * 4, y: a.centroidY + Math.floor(i / 3) * 4 - 4,
    }))),
    arrayLabels: d.arrays.map(a => ({ id: a.id, x: a.centroidX, y: a.centroidY })),
  });
  ctx.y += 2;
  captionLine(ctx, `Plan view of Barcombe Reservoir with arrays at depth-optimised positions . Seasonal profile: ${profile.label}`);

  // ── ASSUMPTIONS PAGE ──────────────────────────────────────────────────
  newPage(ctx);
  drawAssumptionsBlock(ctx, profile, {
    diffuserCount: d.spec.diffuserCount, diffuserType: d.spec.diffuserType,
    diffuserDiamMm: d.spec.diffuserDiamMm, poreSizeUm: d.spec.poreSizeUm,
    airflowPerDiffNm3h: d.spec.airflowPerDiffNm3h, mountingHeightM: d.spec.mountingHeightM,
    cornerOffsetM: d.spec.cornerOffsetM, diffuserDropKpa: d.spec.diffuserDropKpa,
    blowerEff: d.spec.blowerEff, safetyPct: d.spec.safetyPct,
    alphaFactor: d.spec.alphaFactor, betaFactor: d.spec.betaFactor,
    tubeLengthM: d.spec.tubeLengthM, tubeCount: d.spec.tubeCount,
    tubeDiamMm: d.spec.tubeDiamMm, tubePattern: d.spec.tubePattern,
    tubeCentreOffsetM: d.spec.tubeCentreOffsetM, porePerCm: d.spec.porePerCm,
  }, {
    areaHa: d.reservoir.areaHa, meanDepthM: d.reservoir.meanDepth,
    surveyRef: 'Shoreline Surveys Ltd J2020 (09 Jan 2023)',
  });

  // ── HIERARCHY PAGE ────────────────────────────────────────────────────
  newPage(ctx);
  drawHierarchyBlock(ctx,
    { qNm3h: d.spec.airflowPerDiffNm3h, bubbleMm: d.physics.d_b, soteMperM: d.physics.soteM, poresEach: d.physics.poresEach },
    {
      nDiff: d.spec.diffuserCount, qNm3h: d.physics.qPerModule,
      sotrKgh: d.arrays[0] ? d.arrays[0].aotrKgh / Math.max(0.01, d.arrays[0].moduleCount) / (profile.alphaFactor * profile.betaFactor) : 0,
      aotrKgh: d.optimization.aotrTypical,
      kw: d.totals.kw / Math.max(1, d.totals.modules),
      poresTotal: d.physics.poresTotal,
    },
    d.arrays.map(a => ({
      id: a.id, nModules: a.moduleCount, nDiff: a.totalDiffusers,
      qNm3h: a.airflowNm3h, aotrKgh: a.aotrKgh, kw: a.blowerKw, depthM: a.meanDepthM,
    })),
    {
      nArrays: d.totals.arrays, nModules: d.totals.modules, nDiff: d.totals.diffusers,
      qNm3h: d.totals.airflow, sotrKgh: d.totals.sotr, aotrKgh: d.totals.aotr, kw: d.totals.kw,
    });

  // ── SUBSEQUENT SECTIONS (concise) ────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'B', 'Design Inputs Summary');
  paragraph(ctx, 'The following inputs are loaded from the active session. All subsequent calculations derive from these.');
  kvTable(ctx, [
    { k: 'Frame size (module footprint)', v: '2.0 x 2.0 m', src: 'standard' },
    { k: 'Diffusers per module', v: String(d.spec.diffuserCount), src: 'client input' },
    { k: 'Diffuser type', v: d.spec.diffuserType, src: 'client input' },
    { k: 'Diffuser diameter', v: `${d.spec.diffuserDiamMm} mm`, src: 'client input' },
    { k: 'Pore size', v: `${d.spec.poreSizeUm} um`, src: 'client input' },
    { k: 'Corner offset', v: `${fmt(d.spec.cornerOffsetM, 2)} m`, src: 'frame geometry' },
    { k: 'Mounting height (above bed)', v: `${fmt(d.spec.mountingHeightM, 2)} m`, src: 'site survey' },
    { k: 'Air flow per diffuser', v: `${fmt(d.spec.airflowPerDiffNm3h, 2)} Nm3/h`, src: 'client input' },
    { k: 'Water temperature', v: `${fmt(d.spec.waterTempC, 1)} degC`, src: 'site monitoring' },
    { k: 'Current dissolved oxygen', v: `${fmt(d.spec.currentDoMgL, 2)} mg/L`, src: 'site monitoring' },
    { k: 'alpha factor', v: fmt(d.spec.alphaFactor, 2), src: 'freshwater typical' },
    { k: 'beta factor', v: fmt(d.spec.betaFactor, 2), src: 'freshwater typical' },
  ]);

  sectionHeading(ctx, '1', 'Water Physical Properties at Design Temperature');
  formulaBlock(ctx, [
    'rho_w = 999.842 + 6.794e-2*T - 9.095e-3*T^2 + ... [Kell 1975]',
    'mu_w = 2.414e-5 x 10^(247.8/(T+133.15)) [Andrade-Vogel]',
    'sigma_w = 0.07564 - 1.376e-4 x T [Vargaftik 1983]',
    'C_sat = 468 / (31.6 + T) [Benson & Krause]',
  ]);
  kvTable(ctx, [
    { k: 'Density rho_w', v: `${fmt(d.physics.rho, 2)} kg/m^3`, src: 'Kell 1975' },
    { k: 'Dynamic viscosity mu_w', v: `${(d.physics.mu * 1000).toFixed(3)} mPa.s`, src: 'Andrade-Vogel' },
    { k: 'Surface tension sigma', v: `${(d.physics.sigma * 1000).toFixed(2)} mN/m`, src: 'Vargaftik 1983' },
    { k: 'DO saturation', v: `${fmt(d.physics.dosat, 2)} mg/L`, src: 'Benson & Krause' },
  ]);

  sectionHeading(ctx, '2', 'Bubble Formation Physics');
  formulaBlock(ctx, [
    'N_pores per diffuser = 0.35 x pi x (D/2)^2',
    'q_pore = Q_module / (3600 x N_pores_total)',
    'd_stat = [6 x sigma x d_pore / (rho_w x g)]^(1/3) [Fritz]',
    'd_b = d_stat x (1 + 0.9 x (q_pore x 1e9)^0.25) x 1000 [Loubiere]',
  ]);
  kvTable(ctx, [
    { k: 'Pores per single diffuser', v: String(d.physics.poresEach) },
    { k: 'Total pores per module', v: String(d.physics.poresTotal) },
    { k: 'Operating bubble diameter', v: `${fmt(d.physics.d_b, 3)} mm`, src: 'Loubiere 2003' },
    { k: 'Bubble rise velocity', v: `${fmt(d.physics.v_rise, 3)} m/s`, src: 'Clift 1978' },
    { k: 'SOTE per metre', v: `${fmt(d.physics.soteM, 2)} %/m`, src: 'Stenstrom 2008' },
  ]);

  sectionHeading(ctx, '3', 'Reservoir Geometry & Effective Aeration Area');
  kvTable(ctx, [
    { k: 'Plan area', v: `${fmt(d.reservoir.areaHa, 3)} ha`, src: 'shoelace formula' },
    { k: 'Mean depth', v: `${fmt(d.reservoir.meanDepth, 2)} m`, src: 'sampled' },
    { k: 'Volume', v: `${fmt(d.reservoir.volumeM3, 0)} m^3`, src: 'Sum d x dA' },
    { k: 'Effective area >= 2 m', v: `${fmt(d.optimization.effArea / 10000, 2)} ha`, src: 'aeration zone' },
  ]);

  sectionHeading(ctx, '4', 'SOD Demand & Total Module Count');
  formulaBlock(ctx, [
    `SOD_total = A_res x SOD_rate / 24000 = ${fmt(d.optimization.totalSOD, 3)} kg O2/h`,
    `SOD_safety = SOD_total x 1.3 = ${fmt(d.optimization.sodSafety, 3)} kg O2/h`,
    `n_SOD = ceil(SOD_safety / AOTR_typical) = ${d.optimization.modsBySOD} modules`,
    `r_mix = 5 x H_mean = ${fmt(d.optimization.rMix, 1)} m [Wuest 1992]`,
    `n_cov = ceil(70% x A_eff / A_mod) = ${d.optimization.modsByCov} modules`,
    `RECOMMENDED = max(${d.optimization.modsBySOD}, ${d.optimization.modsByCov}) = ${d.optimization.rec} modules`,
  ]);

  sectionHeading(ctx, '5', 'Array Allocation - Position & Module Distribution');
  dataTable(ctx,
    ['Array', 'Zone label', 'Depth (m)', 'Pos (x,y)', 'Modules', 'Diff/mod', 'Total diff', 'Spacing (m)', 'Air (Nm3/h)', 'AOTR (kg/h)', 'kW'],
    d.arrays.map(a => [
      a.id, a.zoneLabel, fmt(a.meanDepthM, 1), `(${a.centroidX},${a.centroidY})`,
      a.moduleCount, a.diffPerModule, a.totalDiffusers,
      fmt(a.spacingM, 1), fmt(a.airflowNm3h, 1), fmt(a.aotrKgh, 2), fmt(a.blowerKw, 2),
    ]));

  sectionHeading(ctx, '6', 'Bill of Materials');
  dataTable(ctx, ['Item', 'Qty', 'Specification / notes'], [
    ['Diffuser arrays', d.totals.arrays, 'Logical groups, one sub-manifold per array'],
    ['Modules (2x2 m)', d.totals.modules, 'Stainless steel frame + air feed'],
    ['Diffusers', d.totals.diffusers, `${d.spec.diffuserType} ${d.spec.diffuserDiamMm} mm dia, ${d.spec.poreSizeUm} um pore`],
    ['Sub-manifolds', d.totals.arrays, 'PE ring main, balanced orifices'],
    ['Total airflow', `${fmt(d.totals.airflow, 0)} Nm3/h`, 'Continuous duty'],
    ['Electrical load', `${fmt(d.totals.kw, 1)} kW`, 'Recommend 2x units for N+1 redundancy'],
  ]);

  sectionHeading(ctx, '7', 'Validation Checks');
  const passed = d.validation.filter(v => v.pass).length;
  paragraph(ctx, `Overall: ${passed} / ${d.validation.length} validation checks pass.`);
  dataTable(ctx, ['#', 'Check', 'Status', 'Value'],
    d.validation.map((v, i) => [i + 1, v.lbl, v.pass ? 'PASS' : 'WARN', v.val]));

  sectionHeading(ctx, '8', 'Literature References');
  for (const ref of REFERENCES) paragraph(ctx, ref, { small: true });

  spacer(ctx, 8);
  drawSignatureBlock(ctx);
  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`${reportRef}.pdf`);
}

// ═════════════════════════════════════════════════════════════════════════════
// SEDIMENT BED PHYSICS REPORT
// ═════════════════════════════════════════════════════════════════════════════
export interface BedPhysData {
  spec: {
    diffuserCount: number; diffuserType: string; diffuserDiamMm: number;
    poreSizeUm: number; airflowPerDiffNm3h: number; mountingHeightM: number;
    waterTempC: number; alphaFactor: number;
  };
  beds: {
    id: string; label: string; desc: string;
    d50_um: number; rho_s: number; theta_cr: number; k_cohesive: number;
  }[];
  depths: number[];
  matrix: Record<string, Record<number, {
    H_eff: number; d_b_bed: number; d_b_surf: number; v_rise: number;
    soteM: number; U_plume_bed: number; tau_bed: number; theta: number;
    theta_cr_eff: number; resusp_factor: number; erosion_rate: number;
    sod_enhance: number; o2_penetration: number; stable: boolean;
  }>>;
  arrays: {
    id: string; zoneLabel: string; depthMid: number;
    cx: number; cy: number; moduleCount: number; spacing: number;
  }[];
  recommendations: {
    bedId: string; criticalDepthM: number;
    recMountingHeightM: number; recAirflowPerDiff: number;
    recPoreSizeUm: number; recDiffuserCount: number;
    expectedTheta: number; expectedRatio: number;
    rationale: string;
  };
}

export function generateSedimentBedPDF(d: BedPhysData, profile: SeasonalProfile): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const reportRef = `RB-BED-PHYS-${new Date().toISOString().slice(0, 10)}`;
  applyPDFMetadata(doc, {
    title:    'Sediment Bed Physics & Recommendations - Barcombe Reservoir',
    subject:  `Plume-bed interaction matrix for 3 bed types under ${profile.label} profile`,
    extra:    reportRef,
    keywords: ['bed physics', 'Shields parameter', 'sediment', 'plume', profile.id],
  });
  const ctx: PageContext = { doc, pageW, pageH, margin, y: 0, reportTitle: 'Sediment Bed Physics & Recommendations', reportRef };

  ctx.y = drawHeader(ctx);
  doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(...C.navy);
  doc.text(s('SEDIMENT BED PHYSICS'), pageW / 2, 55, { align: 'center' });
  doc.setFontSize(13); doc.setTextColor(...C.grey);
  doc.text(s('Plume - Bed Interaction Analysis & Recommended Settings'), pageW / 2, 64, { align: 'center' });
  doc.setDrawColor(...C.gold); doc.setLineWidth(0.8);
  doc.line(margin, 72, pageW - margin, 72);

  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...C.navy);
  doc.text(s('Project: Barcombe Reservoir Aeration Design'), pageW / 2, 85, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.grey);
  doc.text(s(`Active session: ${d.spec.diffuserCount} x ${d.spec.diffuserType}, ${d.spec.diffuserDiamMm} mm`),
    pageW / 2, 92, { align: 'center' });
  doc.text(s(`${d.spec.poreSizeUm} um pore, ${fmt(d.spec.airflowPerDiffNm3h, 2)} Nm3/h per diffuser, mounting ${fmt(d.spec.mountingHeightM, 2)} m`),
    pageW / 2, 97, { align: 'center' });

  // Recommendation hero box
  doc.setFillColor(255, 251, 235); doc.setDrawColor(...C.gold); doc.setLineWidth(0.5);
  doc.rect(margin, 110, pageW - 2 * margin, 50, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...C.gold);
  doc.text(s('RECOMMENDED SETTINGS'), pageW / 2, 119, { align: 'center' });
  doc.setFontSize(9); doc.setTextColor(...C.black);
  doc.text(s(`For worst-case bed type: ${d.recommendations.bedId}`), pageW / 2, 126, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  let cy = 133;
  for (const [k, v] of [
    ['Mounting height (above sediment)', `${fmt(d.recommendations.recMountingHeightM, 2)} m`],
    ['Airflow per diffuser', `${fmt(d.recommendations.recAirflowPerDiff, 2)} Nm3/h`],
    ['Pore size', `${d.recommendations.recPoreSizeUm} um`],
    ['Diffusers per module', `${d.recommendations.recDiffuserCount}`],
    ['Expected Shields ratio (theta/theta_cr)', `${fmt(d.recommendations.expectedRatio, 2)}x (stable < 1.0)`],
  ]) {
    doc.setTextColor(...C.grey); doc.text(s(k), margin + 10, cy);
    doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold');
    doc.text(s(v), pageW / 2 + 10, cy);
    doc.setFont('helvetica', 'normal');
    cy += 5;
  }

  // Reservoir layout
  drawReservoirPlanView(doc, margin, 170, pageW - 2 * margin, 95, {
    bedColour: [156, 113, 75], bedLabel: 'Worst-case bed (silt/clay)',
    arrayLabels: d.arrays.map(a => ({ id: a.id, x: a.cx, y: a.cy })),
  });

  // ── ASSUMPTIONS PAGE ─────────────────────────────────────────────────
  newPage(ctx);
  drawAssumptionsBlock(ctx, profile, {
    diffuserCount: d.spec.diffuserCount, diffuserType: d.spec.diffuserType,
    diffuserDiamMm: d.spec.diffuserDiamMm, poreSizeUm: d.spec.poreSizeUm,
    airflowPerDiffNm3h: d.spec.airflowPerDiffNm3h, mountingHeightM: d.spec.mountingHeightM,
    cornerOffsetM: 0.35, diffuserDropKpa: 1.5,
    blowerEff: 0.70, safetyPct: 10,
    alphaFactor: d.spec.alphaFactor, betaFactor: 0.98,
  }, {
    areaHa: 3.4, meanDepthM: 2.3,
    surveyRef: 'Shoreline Surveys Ltd J2020 (09 Jan 2023)',
  });

  // ── HIERARCHY PAGE ───────────────────────────────────────────────────
  const totalModules = d.arrays.reduce((s, a) => s + a.moduleCount, 0);
  const qPerModule = d.spec.airflowPerDiffNm3h * d.spec.diffuserCount;
  // Approximate per-module SOTR/AOTR from the first array's depth
  const sample = d.arrays[0];
  const sampleMatrix = sample ? d.matrix[d.recommendations.bedId]?.[d.depths[Math.floor(d.depths.length / 2)]] : null;
  const soteM = sampleMatrix?.soteM ?? 4;
  const Heff = (sample?.depthMid ?? 3) - d.spec.mountingHeightM;
  const sotrMod = qPerModule * 1.293 * 0.2314 * (soteM * Heff / 100);
  const aotrMod = seasonalAOTR(sotrMod, profile, 468 / (31.6 + profile.waterTempC));
  const kwMod = 0.6 * qPerModule / 10;  // rough
  newPage(ctx);
  drawHierarchyBlock(ctx,
    { qNm3h: d.spec.airflowPerDiffNm3h, bubbleMm: sampleMatrix?.d_b_bed ?? 2.5, soteMperM: soteM, poresEach: Math.round(0.35 * Math.PI * (d.spec.diffuserDiamMm / 2) ** 2) },
    {
      nDiff: d.spec.diffuserCount, qNm3h: qPerModule,
      sotrKgh: sotrMod, aotrKgh: aotrMod, kw: kwMod,
      poresTotal: Math.round(0.35 * Math.PI * (d.spec.diffuserDiamMm / 2) ** 2) * d.spec.diffuserCount,
    },
    d.arrays.map(a => ({
      id: a.id, nModules: a.moduleCount, nDiff: a.moduleCount * d.spec.diffuserCount,
      qNm3h: a.moduleCount * qPerModule, aotrKgh: a.moduleCount * aotrMod, kw: a.moduleCount * kwMod, depthM: a.depthMid,
    })),
    {
      nArrays: d.arrays.length, nModules: totalModules,
      nDiff: totalModules * d.spec.diffuserCount,
      qNm3h: totalModules * qPerModule,
      sotrKgh: totalModules * sotrMod,
      aotrKgh: totalModules * aotrMod,
      kw: totalModules * kwMod,
    });

  // Bed types
  newPage(ctx);
  sectionHeading(ctx, '1', 'Reservoir Bed Conditions Analysed');
  paragraph(ctx, 'Three bed conditions are analysed, representing the states Barcombe Reservoir may be in over its dredging cycle.');
  dataTable(ctx,
    ['Bed type', 'd50 (um)', 'rho_s (kg/m^3)', 'theta_cr', 'Cohesion k', 'Description'],
    d.beds.map(b => [b.label, b.d50_um, b.rho_s, b.theta_cr, `x${b.k_cohesive}`, b.desc]));

  // Per-bed physics matrices
  for (const bed of d.beds) {
    sectionHeading(ctx, '2', `Plume - Bed Interaction Matrix : ${bed.label}`);
    paragraph(ctx, `Per-depth physics for ${bed.label.toLowerCase()} (d50 = ${bed.d50_um} um, theta_cr_eff = ${fmt(bed.theta_cr * bed.k_cohesive, 3)}).`);
    dataTable(ctx,
      ['Depth (m)', 'H_eff (m)', 'd_b bed (mm)', 'd_b surf (mm)', 'v_rise (m/s)', 'U_bed (m/s)', 'tau (Pa)', 'theta', 'theta/cr', 'f_resusp', 'Status'],
      d.depths.map(depth => {
        const p = d.matrix[bed.id][depth];
        const ratio = p.theta / p.theta_cr_eff;
        const status = p.stable ? 'STABLE' : ratio < 2 ? 'MILD' : 'ERODING';
        return [
          depth.toFixed(1), fmt(p.H_eff, 2), fmt(p.d_b_bed, 2), fmt(p.d_b_surf, 2),
          fmt(p.v_rise, 3), fmt(p.U_plume_bed, 3), fmt(p.tau_bed, 3),
          p.theta.toExponential(2), fmt(ratio, 2), `${fmt(p.resusp_factor, 2)}x`, status,
        ];
      }));
  }

  // Cross-bed comparison
  sectionHeading(ctx, '3', 'Cross-Bed Comparison at Mean Reservoir Depth (3.0 m)');
  const refDepth = 3.0;
  dataTable(ctx,
    ['Bed type', 'theta', 'theta/cr_eff', 'f_resusp', 'Erosion (kg/m2/d)', 'O2 pen (mm)', 'Outcome'],
    d.beds.map(b => {
      const p = d.matrix[b.id][refDepth];
      const ratio = p.theta / p.theta_cr_eff;
      return [b.label, p.theta.toExponential(2), fmt(ratio, 2),
        `${fmt(p.resusp_factor, 2)}x`, fmt(p.erosion_rate * 24, 2), fmt(p.o2_penetration, 1),
        p.stable ? 'Stable' : ratio < 2 ? 'Mild resusp' : 'Active erosion'];
    }));

  // Recommendations rationale
  sectionHeading(ctx, '4', 'Recommended Settings & Rationale');
  paragraph(ctx, d.recommendations.rationale);
  spacer(ctx, 2);
  kvTable(ctx, [
    { k: 'Worst-case bed for design', v: d.beds.find(b => b.id === d.recommendations.bedId)?.label ?? '-' },
    { k: 'Critical depth (worst point)', v: `${fmt(d.recommendations.criticalDepthM, 1)} m` },
    { k: 'Recommended mounting height', v: `${fmt(d.recommendations.recMountingHeightM, 2)} m`, src: 'minimises bed shear' },
    { k: 'Recommended airflow / diffuser', v: `${fmt(d.recommendations.recAirflowPerDiff, 2)} Nm3/h`, src: 'balances SOTE vs disturbance' },
    { k: 'Recommended pore size', v: `${d.recommendations.recPoreSizeUm} um`, src: 'fine-bubble band' },
    { k: 'Recommended diffusers / module', v: `${d.recommendations.recDiffuserCount}`, src: 'distributed load' },
    { k: 'Expected theta at recommended', v: fmt(d.recommendations.expectedTheta, 4) },
    { k: 'Expected theta/theta_cr ratio', v: `${fmt(d.recommendations.expectedRatio, 2)}x`, src: '< 1.0 = stable' },
  ]);

  // Array layout
  sectionHeading(ctx, '5', 'Array Layout on Reservoir Bed');
  paragraph(ctx, `${d.arrays.length} arrays placed across depth zones. Positions in reservoir coordinates (metres from SW corner).`);
  dataTable(ctx,
    ['Array', 'Zone', 'Depth (m)', 'Position (x,y)', 'Modules', 'Diffusers', 'Spacing (m)'],
    d.arrays.map(a => [
      a.id, a.zoneLabel, a.depthMid, `(${a.cx}, ${a.cy})`,
      a.moduleCount, a.moduleCount * d.spec.diffuserCount, fmt(a.spacing, 1),
    ]));

  // Science background
  sectionHeading(ctx, '6', 'Science Background');
  paragraph(ctx, '1. Bubble formation (Fritz 1935 + Loubiere & Hebrard 2003): Bubble diameter at the orifice is set by surface tension vs buoyancy balance, with a dynamic correction for finite per-pore airflow.');
  paragraph(ctx, '2. Plume scaling (Seol 2007 / Schladow 1993): Each module generates a buoyant plume entraining water. By dimensional analysis, the centreline velocity at depth H is U_plume = 1.46 x (B0 x H)^(1/3). At the bed this creates shear stress tau = rho x C_D x U^2.');
  paragraph(ctx, '3. Shields criterion (Shields 1936, van Rijn 1984): theta = tau / [(rho_s - rho_w) x g x d50]. For cohesive sediments (clay/silt) the effective critical Shields is raised by a factor of 1.3-5x (Mehta 1986).');
  paragraph(ctx, '4. Cohesive erosion (Partheniades 1965): E = E0 x (theta/theta_cr - 1). For typical reservoir silts E0 ~ 1e-4 kg/m^2/s.');
  paragraph(ctx, '5. SOD interaction (Hickey & Gibbs 2009): Baseline SOD is 0.5-3.0 g O2/m^2/d. Aeration enhances SOD transiently but long-term shifts the sediment surface from anaerobic to aerobic, reducing phosphorus release.');
  paragraph(ctx, '6. O2 penetration (Rasmussen & Jorgensen 1992): delta = sqrt(2 x D x C / SOD). Typical: fine silt 1-2 mm, medium silt 3-5 mm, fine sand 6-10 mm.');

  sectionHeading(ctx, '7', 'References');
  for (const ref of REFERENCES) paragraph(ctx, ref, { small: true });

  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`${reportRef}.pdf`);
}

// ═════════════════════════════════════════════════════════════════════════════
// PRE-DREDGING REPORT
// ═════════════════════════════════════════════════════════════════════════════
export interface DredgingScenarioData {
  spec: {
    diffuserCount: number; diffuserType: string; diffuserDiamMm: number;
    poreSizeUm: number; airflowPerDiffNm3h: number; mountingHeightM: number;
    waterTempC: number; alphaFactor: number; betaFactor: number;
    diffuserDropKpa: number; blowerEff: number; safetyPct: number;
    // Tube-specific (optional)
    tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
    tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
  };
  reservoirAreaHa: number;
  baseWaterDepthM: number;         // depth of clean (original) reservoir bed below surface
  arrays: { id: string; zoneLabel: string; depthMid: number;
    cx: number; cy: number; moduleCount: number }[];
}

interface DredgingComputed {
  scenarioLabel: string;
  bedLabel: string;
  bedColour: [number, number, number];
  siltColour: [number, number, number];
  siltDepthM: number;             // current silt thickness
  effectiveWaterDepthM: number;   // depth from surface to top of silt (or bed)
  mountingHeightM: number;        // recommended for this scenario
  d50_um: number; rho_s: number; theta_cr_eff: number;
  sodRate: number;                // g O2/m2/d
  currentDO: number;              // mg/L typical for scenario
  goals: string[];                // bullet objectives
  caveats: string[];              // bullet warnings
  expectedDOAfter: number;        // mg/L target after aeration
  totalModules: number;
}

function preDredgeParams(d: DredgingScenarioData): DredgingComputed {
  return {
    scenarioLabel: 'PRE-DREDGING (current condition)',
    bedLabel: 'Fine silt / clay (cohesive)',
    bedColour: C.cleanSand,
    siltColour: C.siltBrown,
    siltDepthM: 1.2,
    effectiveWaterDepthM: Math.max(1.5, d.baseWaterDepthM - 1.2),
    mountingHeightM: 0.60,            // raised above silt to limit shear
    d50_um: 15, rho_s: 2500, theta_cr_eff: 0.10 * 2.5,
    sodRate: 2.5,
    currentDO: 2.0,
    goals: [
      'Suppress sediment oxygen demand (SOD) to break the anaerobic cycle in the silt layer',
      'Prevent phosphorus and ammonium release from anoxic silt during summer months',
      'Reduce risk of blue-green algal blooms by destratifying the water column',
      'Pre-condition silt for dredging by maintaining the surface in aerobic state',
      'Suspend fine particles where dredging operations require',
    ],
    caveats: [
      'Raised mounting height (0.6 m) reduces direct bubble impingement on silt to prevent excessive resuspension',
      'Lower airflow per diffuser (recommended) keeps plume buoyancy moderate while maintaining adequate AOTR',
      'Expect transient turbidity increase in first 2-4 weeks of operation as surface flocs are oxidised',
      'Monitor DO at 0.5 m above sediment - target > 4 mg/L sustained',
    ],
    expectedDOAfter: 6.5,
    totalModules: d.arrays.reduce((s, a) => s + a.moduleCount, 0),
  };
}

function postDredgeParams(d: DredgingScenarioData): DredgingComputed {
  return {
    scenarioLabel: 'POST-DREDGING (restored substrate)',
    bedLabel: 'Fine sand (non-cohesive, restored)',
    bedColour: C.cleanSand,
    siltColour: C.cleanSand,         // no silt
    siltDepthM: 0.0,
    effectiveWaterDepthM: d.baseWaterDepthM,
    mountingHeightM: 0.35,            // close to firm bed
    d50_um: 200, rho_s: 2650, theta_cr_eff: 0.045 * 1.0,
    sodRate: 0.7,
    currentDO: 6.0,
    goals: [
      'Maintain thermal destratification through summer - prevent hypolimnion anoxia',
      'Sustain dissolved oxygen > 7 mg/L throughout water column',
      'Provide long-term protection against algal blooms (planktonic and benthic)',
      'Support ecological recovery on restored sand substrate',
      'Operate at reduced airflow / lower energy than pre-dredge phase',
    ],
    caveats: [
      'Lower mounting height (0.35 m) acceptable on stable sand bed - no silt resuspension risk',
      'Module count can be reduced by 30-40% vs pre-dredge - SOD demand far lower',
      'Continue monitoring for re-silting from catchment loading; address upstream if rate > 50 mm/year',
      'Aeration can be reduced to seasonal (Apr-Oct) operation once water quality stabilises',
    ],
    expectedDOAfter: 8.5,
    totalModules: Math.ceil(d.arrays.reduce((s, a) => s + a.moduleCount, 0) * 0.65),
  };
}

function drawScenarioReport(d: DredgingScenarioData, computed: DredgingComputed, reportTitleSuffix: string, fileRef: string, profile: SeasonalProfile): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  applyPDFMetadata(doc, {
    title:    `${reportTitleSuffix} Aeration Design - Barcombe Reservoir`,
    subject:  `${reportTitleSuffix} scenario design for ${profile.label} profile`,
    extra:    fileRef,
    keywords: [reportTitleSuffix.toLowerCase(), 'dredging', 'silt', 'aeration', profile.id],
  });
  const ctx: PageContext = {
    doc, pageW, pageH, margin, y: 0,
    reportTitle: `${reportTitleSuffix} Aeration Design`,
    reportRef: fileRef,
  };

  // ─── COVER PAGE ───────────────────────────────────────────────────────
  ctx.y = drawHeader(ctx);
  doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(...C.navy);
  doc.text(s(reportTitleSuffix.toUpperCase()), pageW / 2, 55, { align: 'center' });
  doc.setFontSize(13); doc.setTextColor(...C.grey);
  doc.text(s('Aeration Design Report'), pageW / 2, 64, { align: 'center' });
  doc.setDrawColor(...C.gold); doc.setLineWidth(0.8);
  doc.line(margin, 72, pageW - margin, 72);

  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...C.navy);
  doc.text(s('Project: Barcombe Reservoir'), pageW / 2, 84, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...C.grey);
  doc.text(s('East Sussex, UK . Client: Atkins Realis'), pageW / 2, 91, { align: 'center' });

  // Scenario tag
  doc.setFillColor(...C.gold); doc.rect(margin, 100, pageW - 2 * margin, 8, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...C.navy);
  doc.text(s(computed.scenarioLabel), pageW / 2, 106, { align: 'center' });

  // Seasonal banner
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.grey);
  doc.text(s(`Seasonal water profile used: ${profile.label}  .  T=${profile.waterTempC} degC  .  DO_baseline=${profile.backgroundDOMgL} mg/L  .  SOD=${profile.sodRateG_m2_d} g/m2/d`),
    pageW / 2, 112, { align: 'center' });

  // Key numbers panel (shifted down by 5 to accommodate seasonal banner)
  doc.setFillColor(255, 251, 235); doc.setDrawColor(...C.gold); doc.setLineWidth(0.4);
  doc.rect(margin, 120, pageW - 2 * margin, 40, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...C.gold);
  doc.text(s('SYSTEM AT-A-GLANCE'), pageW / 2, 127, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.black);
  const colW = (pageW - 2 * margin) / 4;
  const kpiRow1Y = 137;
  const kpis = [
    { lbl: 'Arrays', v: `${d.arrays.length}` },
    { lbl: 'Modules', v: `${computed.totalModules}` },
    { lbl: 'Diffusers', v: `${computed.totalModules * d.spec.diffuserCount}` },
    { lbl: 'Reservoir area', v: `${d.reservoirAreaHa.toFixed(2)} ha` },
  ];
  for (let i = 0; i < kpis.length; i++) {
    const cx = margin + colW * i + colW / 2;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...C.grey);
    doc.text(s(kpis[i].lbl), cx, kpiRow1Y, { align: 'center' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(...C.navy);
    doc.text(s(kpis[i].v), cx, kpiRow1Y + 7, { align: 'center' });
  }
  doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(...C.grey);
  doc.text(s(`Bed: ${computed.bedLabel} . Mounting ${computed.mountingHeightM.toFixed(2)} m . Effective water column ${computed.effectiveWaterDepthM.toFixed(1)} m`),
    pageW / 2, 157, { align: 'center' });

  // Reservoir plan view on cover
  drawReservoirPlanView(doc, margin, 168, pageW - 2 * margin, 95, {
    bedColour: computed.scenarioLabel.startsWith('PRE') ? C.siltBrown : C.cleanSand,
    bedLabel: computed.bedLabel,
    arrayLabels: d.arrays.map(a => ({ id: a.id, x: a.cx, y: a.cy })),
  });
  ctx.y = 265;
  captionLine(ctx, 'Fig 1 - Plan view of Barcombe Reservoir showing the eight (8) aeration arrays (A-H)');

  // ─── ASSUMPTIONS PAGE ─────────────────────────────────────────────────
  newPage(ctx);
  drawAssumptionsBlock(ctx, profile, {
    diffuserCount: d.spec.diffuserCount, diffuserType: d.spec.diffuserType,
    diffuserDiamMm: d.spec.diffuserDiamMm, poreSizeUm: d.spec.poreSizeUm,
    airflowPerDiffNm3h: d.spec.airflowPerDiffNm3h, mountingHeightM: computed.mountingHeightM,
    cornerOffsetM: 0.35, diffuserDropKpa: d.spec.diffuserDropKpa,
    blowerEff: d.spec.blowerEff, safetyPct: d.spec.safetyPct,
    alphaFactor: d.spec.alphaFactor, betaFactor: d.spec.betaFactor,
    tubeLengthM: d.spec.tubeLengthM, tubeCount: d.spec.tubeCount,
    tubeDiamMm: d.spec.tubeDiamMm, tubePattern: d.spec.tubePattern,
    tubeCentreOffsetM: d.spec.tubeCentreOffsetM, porePerCm: d.spec.porePerCm,
  }, {
    areaHa: d.reservoirAreaHa, meanDepthM: computed.effectiveWaterDepthM,
    surveyRef: 'Shoreline Surveys Ltd J2020 (09 Jan 2023)',
  });

  // Scenario-specific assumption block
  sectionHeading(ctx, 'A.4', 'Scenario-Specific Assumptions');
  kvTable(ctx, [
    { k: 'Scenario',                       v: computed.scenarioLabel },
    { k: 'Bed description',                v: computed.bedLabel },
    { k: 'Silt layer thickness',           v: `${computed.siltDepthM.toFixed(2)} m`,
      src: computed.siltDepthM > 0 ? 'measured pre-dredge' : 'no silt (post-dredge clean)' },
    { k: 'Original bed depth (clean)',     v: `${d.baseWaterDepthM.toFixed(2)} m`, src: 'survey datum' },
    { k: 'Effective water column',         v: `${computed.effectiveWaterDepthM.toFixed(2)} m`, src: 'surface to top of bed' },
    { k: 'Sediment d50',                   v: `${computed.d50_um} um` },
    { k: 'Sediment density rho_s',         v: `${computed.rho_s} kg/m3` },
    { k: 'Effective critical Shields',     v: computed.theta_cr_eff.toFixed(3), src: 'with cohesion correction' },
    { k: 'SOD rate (scenario)',            v: `${computed.sodRate.toFixed(2)} g O2/m2/d`,
      src: computed.siltDepthM > 0 ? 'high SOD pre-dredge' : 'reduced SOD post-dredge' },
    { k: 'Recommended mounting height',    v: `${computed.mountingHeightM.toFixed(2)} m`,
      src: computed.siltDepthM > 0 ? 'raised above silt surface' : 'standard mounting on firm bed' },
    { k: 'Current bed DO (pre-aeration)',  v: `${computed.currentDO.toFixed(1)} mg/L` },
    { k: 'Target sustained DO',            v: `${computed.expectedDOAfter.toFixed(1)} mg/L` },
    { k: 'Modules deployed (scenario)',    v: String(computed.totalModules) },
  ]);

  // ─── HIERARCHY PAGE ───────────────────────────────────────────────────
  const qPerMod = d.spec.airflowPerDiffNm3h * d.spec.diffuserCount;
  const soteM = Math.max(1.5, Math.min(9.5, 10.5 * Math.exp(-0.32 * 2.5)));
  const Heff  = Math.max(0.5, computed.effectiveWaterDepthM - computed.mountingHeightM);
  const sotrPerMod = qPerMod * 1.293 * 0.2314 * (soteM * Heff / 100);
  const aotrPerMod = seasonalAOTR(sotrPerMod, profile, 468 / (31.6 + profile.waterTempC));
  const kwPerMod = (qPerMod / 3600) * 70 * computed.effectiveWaterDepthM / d.spec.blowerEff;
  const arraysScaled = d.arrays.map(a => {
    const n = Math.max(1, Math.round(a.moduleCount * (computed.totalModules / d.arrays.reduce((s, x) => s + x.moduleCount, 0))));
    return { id: a.id, nModules: n, nDiff: n * d.spec.diffuserCount,
      qNm3h: n * qPerMod, aotrKgh: n * aotrPerMod, kw: n * kwPerMod, depthM: a.depthMid };
  });
  newPage(ctx);
  drawHierarchyBlock(ctx,
    { qNm3h: d.spec.airflowPerDiffNm3h, bubbleMm: 2.5, soteMperM: soteM,
      poresEach: Math.round(0.35 * Math.PI * (d.spec.diffuserDiamMm / 2) ** 2) },
    {
      nDiff: d.spec.diffuserCount, qNm3h: qPerMod, sotrKgh: sotrPerMod,
      aotrKgh: aotrPerMod, kw: kwPerMod,
      poresTotal: Math.round(0.35 * Math.PI * (d.spec.diffuserDiamMm / 2) ** 2) * d.spec.diffuserCount,
    },
    arraysScaled,
    {
      nArrays: arraysScaled.length, nModules: computed.totalModules,
      nDiff: computed.totalModules * d.spec.diffuserCount,
      qNm3h: computed.totalModules * qPerMod,
      sotrKgh: computed.totalModules * sotrPerMod,
      aotrKgh: computed.totalModules * aotrPerMod,
      kw: computed.totalModules * kwPerMod,
    });

  // ─── PAGE 2: Scenario overview ─────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '1', 'Scenario Overview');
  paragraph(ctx, `This report covers the ${computed.scenarioLabel.toLowerCase()} aeration design for Barcombe Reservoir. ` +
    `In this scenario, the reservoir bed comprises ${computed.bedLabel.toLowerCase()}, ` +
    (computed.siltDepthM > 0
      ? `with an estimated ${computed.siltDepthM.toFixed(1)} m thick silt layer accumulated over the original substrate. ` +
        `The effective water column (surface to top of silt) is therefore reduced from ${d.baseWaterDepthM.toFixed(1)} m (clean) ` +
        `to ${computed.effectiveWaterDepthM.toFixed(1)} m. `
      : `with the original sand substrate exposed and a full ${computed.effectiveWaterDepthM.toFixed(1)} m water column restored. `) +
    `Typical pre-aeration DO at the bed interface is ${computed.currentDO.toFixed(1)} mg/L; ` +
    `target sustained DO after aeration is ${computed.expectedDOAfter.toFixed(1)} mg/L.`);

  paragraph(ctx, 'Design objectives for this scenario:', { bold: true });
  for (const g of computed.goals) paragraph(ctx, `* ${g}`, { small: true });
  spacer(ctx, 3);
  paragraph(ctx, 'Operational notes and caveats:', { bold: true });
  for (const c of computed.caveats) paragraph(ctx, `* ${c}`, { small: true });

  // ─── PAGE 3: Visual representation - bed cross-section ────────────────
  newPage(ctx);
  sectionHeading(ctx, '2', 'Visual Representation of Reservoir Bed');
  paragraph(ctx, 'The diagram below shows a vertical cross-section through one diffuser array, illustrating the bed conditions, mounting geometry, and bubble plume in this scenario.');
  spacer(ctx, 2);

  const isTube = d.spec.diffuserType === 'hdpe_porous_tube' || d.spec.diffuserType === 'epdm_porous_tube';
  drawBedCrossSection(doc, margin, ctx.y, pageW - 2 * margin, 100, {
    title: 'Reservoir Bed Cross-Section',
    scenarioLabel: computed.scenarioLabel,
    totalDepthM: computed.effectiveWaterDepthM,
    siltDepthM: computed.siltDepthM,
    bedColour: C.cleanSand,
    siltColour: C.siltBrown,
    mountingHeightM: computed.mountingHeightM,
    moduleWidthM: 2.0,
    diffuserCount: d.spec.diffuserCount,
    bubbleDiamMm: 2.3,
    plumeSurfaceRadiusM: 0.5 + 0.1 * computed.effectiveWaterDepthM,
    arrayLabel: d.arrays[0]?.id ?? 'A',
    diffuserShape: isTube ? 'tube' : 'disc',
    tubeLengthM: d.spec.tubeLengthM,
    tubeCount: d.spec.tubeCount,
    doAfter: computed.expectedDOAfter,
  });
  ctx.y += 102;
  captionLine(ctx, 'Fig 2 - Side view of single array installation in this scenario');

  spacer(ctx, 4);
  paragraph(ctx, 'Key features of this installation:', { bold: true });
  if (computed.siltDepthM > 0) {
    paragraph(ctx, `* Silt layer (${computed.siltDepthM.toFixed(1)} m thick, hatched brown) sits above the original reservoir bed. ` +
      `This is the source of high SOD and phosphorus release.`, { small: true });
  } else {
    paragraph(ctx, `* Original sand substrate is exposed after dredging. ` +
      `No silt layer means SOD is reduced to natural background levels.`, { small: true });
  }
  paragraph(ctx, `* Module frame mounted ${computed.mountingHeightM.toFixed(2)} m above the bed surface. ` +
    `${computed.siltDepthM > 0 ? 'Raised position prevents direct silt impingement.' : 'Standard low mounting on stable bed.'}`, { small: true });
  paragraph(ctx, `* Bubble plume rises from diffuser face, expanding into a cone with surface radius ~${(0.5 + 0.1 * computed.effectiveWaterDepthM).toFixed(1)} m.`, { small: true });
  paragraph(ctx, `* Water column ${computed.effectiveWaterDepthM.toFixed(1)} m provides ${(computed.effectiveWaterDepthM - computed.mountingHeightM).toFixed(1)} m of effective bubble path for oxygen transfer.`, { small: true });

  // ─── PAGE 4: Plan view + array allocation table ─────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '3', 'Reservoir Plan View & Array Locations');
  drawReservoirPlanView(doc, margin, ctx.y, pageW - 2 * margin, 130, {
    bedColour: computed.scenarioLabel.startsWith('PRE') ? C.siltBrown : C.cleanSand,
    bedLabel: computed.bedLabel,
    arrayLabels: d.arrays.map(a => ({ id: a.id, x: a.cx, y: a.cy })),
  });
  ctx.y += 132;
  captionLine(ctx, 'Fig 3 - Plan view with arrays positioned at depth-optimised locations on reservoir bed');

  paragraph(ctx, 'Array allocation - module count per zone:', { bold: true });
  dataTable(ctx,
    ['Array', 'Zone label', 'Position (x,y) m', 'Depth (m)', 'Modules', 'Diffusers'],
    d.arrays.map(a => [
      a.id, a.zoneLabel, `(${a.cx}, ${a.cy})`, a.depthMid.toFixed(1),
      Math.max(1, Math.round(a.moduleCount * (computed.totalModules / d.arrays.reduce((s, x) => s + x.moduleCount, 0)))),
      Math.max(1, Math.round(a.moduleCount * (computed.totalModules / d.arrays.reduce((s, x) => s + x.moduleCount, 0)))) * d.spec.diffuserCount,
    ]));

  // ─── PAGE 5: Bed physics & key formulas ────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '4', 'Bed Physics & Disturbance Analysis');
  paragraph(ctx, `Bed disturbance is governed by the Shields parameter theta, which compares bubble-plume induced bed shear to the bed's resistance to mobilisation.`);
  formulaBlock(ctx, [
    'tau_bed = rho_w x C_D x U_plume^2  (van Rijn 1984, C_D = 0.01)',
    'theta = tau_bed / [(rho_s - rho_w) x g x d50]  (Shields 1936)',
    'theta_cr_eff = theta_cr x k_cohesive  (Mehta 1986)',
    'Bed stable if theta < theta_cr_eff',
  ]);
  kvTable(ctx, [
    { k: 'Median particle size d50', v: `${computed.d50_um} um`, src: computed.siltDepthM > 0 ? 'cohesive silt' : 'restored sand' },
    { k: 'Particle density rho_s', v: `${computed.rho_s} kg/m^3` },
    { k: 'Effective critical Shields', v: fmt(computed.theta_cr_eff, 3), src: 'with cohesion correction' },
    { k: 'Recommended mounting height', v: `${computed.mountingHeightM.toFixed(2)} m`, src: 'minimise bed shear' },
    { k: 'Effective water column', v: `${computed.effectiveWaterDepthM.toFixed(1)} m`, src: 'surface to bed top' },
  ]);

  sectionHeading(ctx, '5', 'Oxygen Demand & Supply');
  formulaBlock(ctx, [
    `SOD_total = A_res x SOD_rate / 24000`,
    `         = ${(d.reservoirAreaHa * 1e4).toFixed(0)} x ${computed.sodRate.toFixed(1)} / 24000`,
    `         = ${(d.reservoirAreaHa * 1e4 * computed.sodRate / 24000).toFixed(2)} kg O2/h`,
  ]);
  kvTable(ctx, [
    { k: 'Assumed SOD rate', v: `${computed.sodRate.toFixed(1)} g O2/m2/day`, src: computed.siltDepthM > 0 ? 'Hickey & Gibbs 2009 (pre-dredge)' : 'post-dredge restored' },
    { k: 'Total SOD demand', v: `${(d.reservoirAreaHa * 1e4 * computed.sodRate / 24000).toFixed(2)} kg O2/h` },
    { k: 'Current bed DO', v: `${computed.currentDO.toFixed(1)} mg/L` },
    { k: 'Target sustained DO', v: `${computed.expectedDOAfter.toFixed(1)} mg/L` },
    { k: 'Total airflow', v: `${(computed.totalModules * d.spec.diffuserCount * d.spec.airflowPerDiffNm3h).toFixed(0)} Nm3/h` },
    { k: 'Modules deployed', v: `${computed.totalModules}` },
  ]);

  // ─── PAGE 6: Outcomes & monitoring ─────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '6', 'Expected Outcomes & Customer Benefits');
  paragraph(ctx, computed.siltDepthM > 0
    ? 'PRE-DREDGING aeration delivers measurable improvements within the first treatment season:'
    : 'POST-DREDGING aeration sustains the dredge investment and prevents recurrence of water-quality issues:');
  for (const g of computed.goals) paragraph(ctx, `* ${g}`, { small: true });

  sectionHeading(ctx, '7', 'Monitoring & Verification');
  paragraph(ctx, 'The following programme verifies that the design meets specification once commissioned:');
  dataTable(ctx,
    ['Parameter', 'Frequency', 'Target value', 'Sampling location'],
    [
      ['Dissolved oxygen at bed', 'Daily', `> ${computed.expectedDOAfter - 1.5} mg/L`, '0.5 m above sediment, mid-reservoir'],
      ['Water temperature profile', 'Weekly', 'Uniform (delta < 1.5 degC)', 'Surface, mid-depth, near-bed'],
      ['Turbidity (NTU)', 'Weekly first month, then monthly', '< 20 NTU stable', 'Mid-water samples'],
      ['Soluble reactive phosphorus', 'Monthly', `< 0.05 mg/L`, 'Near-bed water'],
      ['Cyanobacteria cell count', 'Bi-weekly Apr-Sept', '< 20,000 cells/mL', 'Surface composite'],
      ['Blower current draw', 'Continuous', `< ${(computed.totalModules * 0.6 * 1.1).toFixed(1)} kW`, 'Pump house'],
    ]);

  // ─── References ────────────────────────────────────────────────────────
  sectionHeading(ctx, '8', 'References');
  for (const ref of REFERENCES) paragraph(ctx, ref, { small: true });

  drawSignatureBlock(ctx);
  drawFooters(doc, pageW, pageH, margin, fileRef);
  doc.save(`${fileRef}.pdf`);
}

export function generatePreDredgingPDF(d: DredgingScenarioData, profile: SeasonalProfile): void {
  const ref = `RB-PRE-DREDGE-${profile.id}-${new Date().toISOString().slice(0, 10)}`;
  drawScenarioReport(d, preDredgeParams(d), 'Pre-Dredging', ref, profile);
}

export function generatePostDredgingPDF(d: DredgingScenarioData, profile: SeasonalProfile): void {
  const ref = `RB-POST-DREDGE-${profile.id}-${new Date().toISOString().slice(0, 10)}`;
  drawScenarioReport(d, postDredgeParams(d), 'Post-Dredging', ref, profile);
}

// ═════════════════════════════════════════════════════════════════════════════
// RESERVOIR LAYOUT REPORT — customer-facing plan + recommendations
// ═════════════════════════════════════════════════════════════════════════════
export interface LayoutReportData {
  spec: {
    diffuserCount: number; diffuserType: string; diffuserDiamMm: number;
    poreSizeUm: number; airflowPerDiffNm3h: number; mountingHeightM: number;
    waterTempC: number; currentDoMgL: number; alphaFactor: number; betaFactor: number;
    cornerOffsetM: number; diffuserDropKpa: number; blowerEff: number; safetyPct: number;
    tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
    tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
  };
  reservoir: { areaHa: number; meanDepth: number; volumeM3: number; effAreaHa: number };
  layout: {
    currentSpacing: number;
    currentMinDepth: number;
    currentModuleCount: number;
    currentCoverage: number;
    sodRate: number;
    targetCoverage: number;
  };
  recommendations: {
    bestCount: number;
    bestSpacing: number;
    bestHexSpacing: number;
    modulesPerHa: number;
    rMix: number;
    areaPerMod: number;
    expectedCoverage: number;
    totalAOTR: number;
    totalAirflow: number;
    totalKw: number;
    o2Surplus: number;
    sodDemand: number;
    sodWithSafety: number;
    reasons: string[];     // narrative explanations
  };
  arrayLabels: { id: string; x: number; y: number; depthM: number; modulesAssigned: number }[];
  modules: { x: number; y: number }[];
}

export function generateReservoirLayoutPDF(d: LayoutReportData, profile: SeasonalProfile): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const reportRef = `RB-RES-LAYOUT-${profile.id}-${new Date().toISOString().slice(0, 10)}`;
  applyPDFMetadata(doc, {
    title:    'Reservoir Layout Report - Barcombe Reservoir',
    subject:  `Plan-view module placement and recommendations for ${profile.label}`,
    extra:    reportRef,
    keywords: ['layout', 'recommendations', 'AWWA', 'module placement', profile.id],
  });
  const ctx: PageContext = {
    doc, pageW, pageH, margin, y: 0,
    reportTitle: 'Reservoir Layout Report',
    reportRef,
  };
  const isTube = d.spec.diffuserType === 'hdpe_porous_tube' || d.spec.diffuserType === 'epdm_porous_tube';

  // ── COVER ────────────────────────────────────────────────────────────
  ctx.y = drawHeader(ctx);
  doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(...C.navy);
  doc.text(s('RESERVOIR LAYOUT REPORT'), pageW / 2, 55, { align: 'center' });
  doc.setFontSize(13); doc.setTextColor(...C.grey);
  doc.text(s('Plan-view Module Placement & Recommended Configuration'), pageW / 2, 64, { align: 'center' });
  doc.setDrawColor(...C.gold); doc.setLineWidth(0.8);
  doc.line(margin, 72, pageW - margin, 72);

  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...C.navy);
  doc.text(s('Project: Barcombe Reservoir Aeration'), pageW / 2, 84, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.grey);
  doc.text(s(`Seasonal water profile: ${profile.label}  .  T=${profile.waterTempC} degC  .  DO_baseline=${profile.backgroundDOMgL} mg/L`),
    pageW / 2, 91, { align: 'center' });

  // Recommendation hero box
  doc.setFillColor(255, 251, 235); doc.setDrawColor(...C.gold); doc.setLineWidth(0.5);
  doc.rect(margin, 100, pageW - 2 * margin, 50, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...C.gold);
  doc.text(s('RECOMMENDED LAYOUT'), pageW / 2, 109, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.black);
  let cy = 117;
  for (const [k, v] of [
    ['Recommended module count',  `${d.recommendations.bestCount} modules (${d.recommendations.modulesPerHa.toFixed(1)} /ha)`],
    ['Square-grid spacing',        `${d.recommendations.bestSpacing.toFixed(1)} m`],
    ['Hex-grid spacing',           `${d.recommendations.bestHexSpacing.toFixed(1)} m`],
    ['Expected coverage',          `${d.recommendations.expectedCoverage.toFixed(0)} % (target ${d.layout.targetCoverage}%)`],
    ['Total AOTR',                 `${d.recommendations.totalAOTR.toFixed(2)} kg O2/h (SOD demand ${d.recommendations.sodDemand.toFixed(2)})`],
    ['Total power',                `${d.recommendations.totalKw.toFixed(1)} kW`],
  ]) {
    doc.setTextColor(...C.grey); doc.text(s(k), margin + 10, cy);
    doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold');
    doc.text(s(v), pageW / 2 + 10, cy);
    doc.setFont('helvetica', 'normal');
    cy += 5;
  }

  // Plan view on cover (with 20m grid)
  drawReservoirPlanView(doc, margin, 158, pageW - 2 * margin, 110, {
    bedColour: [124, 74, 42], bedLabel: 'Reservoir bed',
    arrayLabels: d.arrayLabels,
    modules: d.modules,
    gridSpacingM: 20, showCoordinates: true,
  });
  ctx.y = 270;
  captionLine(ctx, `Fig 1 - Recommended layout (${d.recommendations.bestCount} modules across ${d.arrayLabels.length} arrays) on 20 m grid`);

  // ── ASSUMPTIONS ──────────────────────────────────────────────────────
  newPage(ctx);
  drawAssumptionsBlock(ctx, profile, {
    diffuserCount: d.spec.diffuserCount, diffuserType: d.spec.diffuserType,
    diffuserDiamMm: d.spec.diffuserDiamMm, poreSizeUm: d.spec.poreSizeUm,
    airflowPerDiffNm3h: d.spec.airflowPerDiffNm3h, mountingHeightM: d.spec.mountingHeightM,
    cornerOffsetM: d.spec.cornerOffsetM, diffuserDropKpa: d.spec.diffuserDropKpa,
    blowerEff: d.spec.blowerEff, safetyPct: d.spec.safetyPct,
    alphaFactor: d.spec.alphaFactor, betaFactor: d.spec.betaFactor,
    tubeLengthM: d.spec.tubeLengthM, tubeCount: d.spec.tubeCount,
    tubeDiamMm: d.spec.tubeDiamMm, tubePattern: d.spec.tubePattern,
    tubeCentreOffsetM: d.spec.tubeCentreOffsetM, porePerCm: d.spec.porePerCm,
  }, {
    areaHa: d.reservoir.areaHa, meanDepthM: d.reservoir.meanDepth,
    surveyRef: 'Shoreline Surveys Ltd J2020 (09 Jan 2023)',
  });

  // ── RECOMMENDATION RATIONALE (with reasons) ──────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'R', 'Recommendation Rationale — why this layout?');
  paragraph(ctx, 'The recommended layout is derived from two independent constraints. The stricter constraint governs the recommended module count. Spacing is then derived from the count and the available aeration area.');
  spacer(ctx, 2);

  formulaBlock(ctx, [
    `By SOD demand:    n_SOD = ceil(SOD_total x safety / AOTR_module)`,
    `By coverage:      n_cov = ceil(target% x A_effective / (pi x r_mix^2 x 0.65))`,
    `Recommended:      n_rec = max(n_SOD, n_cov)`,
    `Spacing:          s = sqrt(A_effective / n_rec)`,
    `Bounds:           2 x r_surface <= s <= 2 x r_mix`,
  ]);

  // Numbered reasoning
  for (let i = 0; i < d.recommendations.reasons.length; i++) {
    paragraph(ctx, `${i + 1}. ${d.recommendations.reasons[i]}`, { small: true });
  }

  spacer(ctx, 4);
  kvTable(ctx, [
    { k: 'Reservoir area',                 v: `${d.reservoir.areaHa.toFixed(2)} ha` },
    { k: 'Effective aeration area (>= 2 m)', v: `${d.reservoir.effAreaHa.toFixed(2)} ha` },
    { k: 'Mean depth',                      v: `${d.reservoir.meanDepth.toFixed(2)} m` },
    { k: 'Mixing radius r_mix = 5 x H_bar', v: `${d.recommendations.rMix.toFixed(1)} m`, src: 'Wuest 1992' },
    { k: 'Area per module',                v: `${d.recommendations.areaPerMod.toFixed(0)} m2`, src: 'pi x r^2 x 0.65' },
    { k: 'SOD demand (this season)',       v: `${d.recommendations.sodDemand.toFixed(3)} kg O2/h`, src: profile.source },
    { k: 'SOD with safety factor',         v: `${d.recommendations.sodWithSafety.toFixed(3)} kg O2/h` },
    { k: 'Total AOTR (recommended layout)', v: `${d.recommendations.totalAOTR.toFixed(3)} kg O2/h` },
    { k: 'O2 surplus',                     v: `${d.recommendations.o2Surplus >= 0 ? '+' : ''}${d.recommendations.o2Surplus.toFixed(3)} kg O2/h`,
      src: d.recommendations.o2Surplus >= 0 ? 'PASS' : 'DEFICIT' },
    { k: 'Total airflow',                  v: `${d.recommendations.totalAirflow.toFixed(0)} Nm3/h` },
    { k: 'Total blower power',             v: `${d.recommendations.totalKw.toFixed(1)} kW` },
    { k: 'Expected coverage',              v: `${d.recommendations.expectedCoverage.toFixed(1)} %` },
  ]);

  // ── CURRENT vs RECOMMENDED COMPARISON ────────────────────────────────
  sectionHeading(ctx, 'C', 'Current vs Recommended Layout Comparison');
  dataTable(ctx,
    ['Metric', 'Current', 'Recommended', 'Delta'],
    [
      ['Module count',     d.layout.currentModuleCount, d.recommendations.bestCount,
        `${d.recommendations.bestCount - d.layout.currentModuleCount > 0 ? '+' : ''}${d.recommendations.bestCount - d.layout.currentModuleCount}`],
      ['Spacing (m)',      d.layout.currentSpacing.toFixed(0), d.recommendations.bestSpacing.toFixed(1),
        `${(d.recommendations.bestSpacing - d.layout.currentSpacing).toFixed(1)}`],
      ['Coverage (%)',     d.layout.currentCoverage.toFixed(0), d.recommendations.expectedCoverage.toFixed(0),
        `${(d.recommendations.expectedCoverage - d.layout.currentCoverage).toFixed(0)}%`],
    ]);

  // ── DETAILED PLAN VIEW (own page) ────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'L', 'Detailed Plan View (20 m Grid)');
  paragraph(ctx, 'The reservoir plan view with array positions, modules and 20 m grid for distance reference. Coordinates are metres from the SW corner.');
  drawReservoirPlanView(doc, margin, ctx.y, pageW - 2 * margin, 200, {
    bedColour: [124, 74, 42], bedLabel: 'Reservoir bed',
    arrayLabels: d.arrayLabels,
    modules: d.modules,
    gridSpacingM: 20, showCoordinates: true,
  });
  ctx.y += 202;
  captionLine(ctx, 'Fig 2 - Full-page detailed plan view with 20 m grid');

  // Array allocation table
  newPage(ctx);
  sectionHeading(ctx, 'A', 'Array Allocation (modules per zone)');
  dataTable(ctx,
    ['Array', 'Centroid (x, y) m', 'Depth (m)', 'Modules', 'Diffusers'],
    d.arrayLabels.map(a => [
      a.id, `(${a.x}, ${a.y})`, a.depthM.toFixed(1),
      a.modulesAssigned, a.modulesAssigned * d.spec.diffuserCount,
    ]));

  // ── PLUME GEOMETRY NOTE (for chosen diffuser type) ───────────────────
  sectionHeading(ctx, 'P', 'Plume Geometry for this Diffuser Type');
  paragraph(ctx, isTube
    ? 'TUBE (line source): the bubble plume forms a planar curtain rather than a cone. Buoyancy flux is divided by tube length, giving lower bed shear per point but covering a larger rectangular footprint. Reference: Wuest, Brooks & Imboden (1992).'
    : 'DISC (point source): each diffuser is a buoyant point source generating an axisymmetric cone. Plume centreline velocity is U = 1.46 x (B0 x H)^(1/3) with cone half-angle 5.7 degrees. Reference: Seol et al. (2007).');

  sectionHeading(ctx, 'X', 'References');
  for (const ref of REFERENCES) paragraph(ctx, ref, { small: true });

  drawSignatureBlock(ctx);
  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`${reportRef}.pdf`);
}

// ═════════════════════════════════════════════════════════════════════════════
// MODULE ARRAY REPORT — detailed N-module visualisation
// ═════════════════════════════════════════════════════════════════════════════
export interface ModuleArrayData {
  spec: {
    diffuserCount: number; diffuserType: string; diffuserDiamMm: number;
    poreSizeUm: number; airflowPerDiffNm3h: number; mountingHeightM: number;
    waterTempC: number; currentDoMgL: number; alphaFactor: number; betaFactor: number;
    cornerOffsetM: number; diffuserDropKpa: number; blowerEff: number; safetyPct: number;
    waterDepthM: number;
    tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
    tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
  };
  config: {
    moduleCount: number; cols: number; rows: number;
    gapXM: number; gapYM: number;
    arrayWidthM: number; arrayHeightM: number;
    reservoirAreaHa: number;
  };
  physics: {
    poresPerUnit: number; unitsPerModule: number; poresPerModule: number;
    qPerModule: number; d_b: number; v_rise: number; soteM: number;
    sotrPerModule: number; aotrPerModule: number; kwPerModule: number;
    r_inf_bed: number; r_surface: number; effSubmM: number;
  };
  totals: {
    diffusers: number; airflow: number; sotr: number; aotr: number; kw: number;
  };
}

export function generateModuleArrayPDF(d: ModuleArrayData, profile: SeasonalProfile): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const reportRef = `RB-MOD-ARRAY-${profile.id}-${new Date().toISOString().slice(0, 10)}`;
  applyPDFMetadata(doc, {
    title:    `Module Array Report (${d.config.moduleCount} modules) - Barcombe Reservoir`,
    subject:  `Multi-module array configuration & coverage for ${profile.label}`,
    extra:    reportRef,
    keywords: ['module array', 'coverage', 'plume', 'array', profile.id],
  });
  const ctx: PageContext = { doc, pageW, pageH, margin, y: 0,
    reportTitle: 'Module Array Report', reportRef };
  const isTube = d.spec.diffuserType === 'hdpe_porous_tube' || d.spec.diffuserType === 'epdm_porous_tube';

  // ── Cover
  ctx.y = drawHeader(ctx);
  doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(...C.navy);
  doc.text(s('MODULE ARRAY REPORT'), pageW / 2, 55, { align: 'center' });
  doc.setFontSize(13); doc.setTextColor(...C.grey);
  doc.text(s(`${d.config.moduleCount}-Module Array Configuration & Coverage`), pageW / 2, 64, { align: 'center' });
  doc.setDrawColor(...C.gold); doc.setLineWidth(0.8);
  doc.line(margin, 72, pageW - margin, 72);

  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.grey);
  doc.text(s(`Seasonal profile: ${profile.label}  .  ${isTube ? 'TUBE diffuser - line source' : 'DISC diffuser - point source'}`),
    pageW / 2, 84, { align: 'center' });

  // KPI panel
  doc.setFillColor(255, 251, 235); doc.setDrawColor(...C.gold);
  doc.rect(margin, 95, pageW - 2 * margin, 30, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...C.gold);
  doc.text(s('ARRAY AT-A-GLANCE'), pageW / 2, 102, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.black);
  const cw = (pageW - 2 * margin) / 4;
  const kp = [
    ['Modules', String(d.config.moduleCount)],
    ['Diffusers/units', String(d.totals.diffusers)],
    ['Array size', `${d.config.arrayWidthM.toFixed(1)} x ${d.config.arrayHeightM.toFixed(1)} m`],
    ['Power', `${d.totals.kw.toFixed(2)} kW`],
  ];
  for (let i = 0; i < kp.length; i++) {
    const xc = margin + cw * i + cw / 2;
    doc.setTextColor(...C.grey); doc.text(s(kp[i][0]), xc, 110, { align: 'center' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...C.navy);
    doc.text(s(kp[i][1]), xc, 119, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  }

  // Array visual representation (vector-drawn modules)
  drawModuleArrayVisual(doc, margin, 132, pageW - 2 * margin, 120, d);
  ctx.y = 254;
  captionLine(ctx, `Fig 1 - Plan view of ${d.config.moduleCount}-module array (${d.config.cols} cols x ${d.config.rows} rows)`);

  // ── Assumptions
  newPage(ctx);
  drawAssumptionsBlock(ctx, profile, {
    diffuserCount: d.spec.diffuserCount, diffuserType: d.spec.diffuserType,
    diffuserDiamMm: d.spec.diffuserDiamMm, poreSizeUm: d.spec.poreSizeUm,
    airflowPerDiffNm3h: d.spec.airflowPerDiffNm3h, mountingHeightM: d.spec.mountingHeightM,
    cornerOffsetM: d.spec.cornerOffsetM, diffuserDropKpa: d.spec.diffuserDropKpa,
    blowerEff: d.spec.blowerEff, safetyPct: d.spec.safetyPct,
    alphaFactor: d.spec.alphaFactor, betaFactor: d.spec.betaFactor,
    tubeLengthM: d.spec.tubeLengthM, tubeCount: d.spec.tubeCount,
    tubeDiamMm: d.spec.tubeDiamMm, tubePattern: d.spec.tubePattern,
    tubeCentreOffsetM: d.spec.tubeCentreOffsetM, porePerCm: d.spec.porePerCm,
  }, {
    areaHa: d.config.reservoirAreaHa, meanDepthM: d.spec.waterDepthM,
    surveyRef: 'Shoreline Surveys Ltd J2020 (09 Jan 2023)',
  });

  // ── Hierarchy
  newPage(ctx);
  drawHierarchyBlock(ctx,
    { qNm3h: d.spec.airflowPerDiffNm3h, bubbleMm: d.physics.d_b, soteMperM: d.physics.soteM, poresEach: d.physics.poresPerUnit },
    { nDiff: d.physics.unitsPerModule, qNm3h: d.physics.qPerModule,
      sotrKgh: d.physics.sotrPerModule, aotrKgh: d.physics.aotrPerModule,
      kw: d.physics.kwPerModule, poresTotal: d.physics.poresPerModule },
    [{ id: 'Single array', nModules: d.config.moduleCount, nDiff: d.totals.diffusers,
       qNm3h: d.totals.airflow, aotrKgh: d.totals.aotr, kw: d.totals.kw, depthM: d.spec.waterDepthM }],
    { nArrays: 1, nModules: d.config.moduleCount, nDiff: d.totals.diffusers,
      qNm3h: d.totals.airflow, sotrKgh: d.totals.sotr, aotrKgh: d.totals.aotr, kw: d.totals.kw });

  // ── Coverage analysis
  sectionHeading(ctx, '4', 'Array Coverage & Influence Zones');
  paragraph(ctx, `Each module's bed influence radius is ${d.physics.r_inf_bed.toFixed(2)} m (${isTube ? 'tube line plume - Wuest 1992' : 'disc point plume - Seol 2007'}). At the water surface, the plume radius expands to ${d.physics.r_surface.toFixed(2)} m. The array footprint of ${d.config.arrayWidthM.toFixed(1)} x ${d.config.arrayHeightM.toFixed(1)} m covers approximately ${(d.config.arrayWidthM * d.config.arrayHeightM).toFixed(0)} m^2 of reservoir bed.`);
  kvTable(ctx, [
    { k: 'Array footprint',                v: `${d.config.arrayWidthM.toFixed(1)} x ${d.config.arrayHeightM.toFixed(1)} m` },
    { k: 'Array area',                     v: `${(d.config.arrayWidthM * d.config.arrayHeightM).toFixed(0)} m2` },
    { k: 'Inter-module gap (X)',           v: `${d.config.gapXM.toFixed(2)} m` },
    { k: 'Inter-module gap (Y)',           v: `${d.config.gapYM.toFixed(2)} m` },
    { k: 'Bed influence radius / module',  v: `${d.physics.r_inf_bed.toFixed(2)} m` },
    { k: 'Surface plume radius / module',   v: `${d.physics.r_surface.toFixed(2)} m` },
    { k: 'Effective submergence',          v: `${d.physics.effSubmM.toFixed(2)} m` },
    { k: 'Bubble diameter at bed',         v: `${d.physics.d_b.toFixed(2)} mm` },
    { k: 'SOTE per metre',                  v: `${d.physics.soteM.toFixed(2)} %/m` },
    { k: 'Total array AOTR',                v: `${d.totals.aotr.toFixed(2)} kg O2/h` },
    { k: 'Specific aeration',              v: `${(d.totals.aotr / Math.max(0.001, d.config.arrayWidthM * d.config.arrayHeightM) * 1000 * 24).toFixed(1)} g O2/m2/day` },
  ]);

  // ── Affected zones discussion
  sectionHeading(ctx, '5', 'Affected Zones (Customer Reference)');
  paragraph(ctx, '1. DIRECT BED ZONE: Each module exerts plume-induced bed velocity within a radius from its centroid. For this array configuration the total direct bed influence covers approximately ' +
    `${(d.config.moduleCount * Math.PI * d.physics.r_inf_bed * d.physics.r_inf_bed).toFixed(0)} m^2 (sum of individual influence circles).`);
  paragraph(ctx, '2. CIRCULATION CELL: Beyond the direct plume, induced upwelling creates a circulation cell extending ~5 x depth = ' +
    `${(5 * d.physics.effSubmM).toFixed(0)} m radius from each module (Wuest et al. 1992 mixing-radius criterion).`);
  paragraph(ctx, '3. SURFACE FOOTPRINT: At the water surface, the bubble plume spreads to a circular (disc) or rectangular (tube) footprint of ' +
    `radius ~${d.physics.r_surface.toFixed(2)} m. Visible boil pattern indicates active mixing zone.`);
  paragraph(ctx, '4. OXYGEN TRANSFER ZONE: The water column directly above each module receives the full SOTE/m of ' +
    `${d.physics.soteM.toFixed(1)} %/m over ${d.physics.effSubmM.toFixed(1)} m submergence, giving total SOTE = ${(d.physics.soteM * d.physics.effSubmM).toFixed(1)} %.`);

  // ── References
  sectionHeading(ctx, '6', 'References');
  for (const ref of REFERENCES) paragraph(ctx, ref, { small: true });

  drawSignatureBlock(ctx);
  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`${reportRef}.pdf`);
}

/** Draw the multi-module array visual (top-down) into a PDF region. */
function drawModuleArrayVisual(doc: jsPDF, x: number, y: number, w: number, h: number, d: ModuleArrayData): void {
  doc.setDrawColor(...C.greyLt); doc.setLineWidth(0.3);
  doc.rect(x, y, w, h);

  const FRAME = 2.0;
  const padInner = 4;
  const drawW = w - padInner * 2;
  const drawH = h - padInner * 2;
  const sX = drawW / d.config.arrayWidthM;
  const sY = drawH / d.config.arrayHeightM;
  const scale = Math.min(sX, sY);
  const offX = x + padInner + (drawW - d.config.arrayWidthM * scale) / 2;
  const offY = y + padInner + (drawH - d.config.arrayHeightM * scale) / 2;
  const tx = (m: number) => offX + m * scale;
  const ty = (m: number) => offY + m * scale;

  const isTube = d.spec.diffuserType === 'hdpe_porous_tube' || d.spec.diffuserType === 'epdm_porous_tube';

  // Draw each module
  for (let i = 0; i < d.config.moduleCount; i++) {
    const r = Math.floor(i / d.config.cols);
    const c = i % d.config.cols;
    const fx = c * (FRAME + d.config.gapXM);
    const fy = r * (FRAME + d.config.gapYM);

    // Frame outline
    doc.setDrawColor(...C.navy); doc.setLineWidth(0.5);
    doc.rect(tx(fx), ty(fy), FRAME * scale, FRAME * scale, 'S');

    // Surface plume footprint (circle/rectangle)
    if (isTube) {
      // Curtain plume - parallel to tube
      const halfW = d.physics.r_surface * scale * 0.8;
      doc.setFillColor(120, 200, 230);
      doc.setGState && doc.setGState(new (doc as any).GState({ opacity: 0.15 }));
      doc.rect(tx(fx + 0.1), ty(fy + FRAME / 2) - halfW,
        (FRAME - 0.2) * scale, halfW * 2, 'F');
      doc.setGState && doc.setGState(new (doc as any).GState({ opacity: 1.0 }));
    } else {
      // Circular plume per diffuser - approximate as one centred circle
      doc.setFillColor(120, 200, 230);
      doc.setGState && doc.setGState(new (doc as any).GState({ opacity: 0.15 }));
      doc.circle(tx(fx + FRAME / 2), ty(fy + FRAME / 2), d.physics.r_surface * scale, 'F');
      doc.setGState && doc.setGState(new (doc as any).GState({ opacity: 1.0 }));
    }

    // Diffuser units
    if (isTube) {
      const tubeCount = Math.min(4, d.spec.tubeCount ?? 2);
      const tubeLen   = Math.min(2.0, d.spec.tubeLengthM ?? 1.8);
      const cTube = (FRAME - tubeLen) / 2;
      const tubeW = Math.max(1.2, ((d.spec.tubeDiamMm ?? 75) / 1000) * scale * 1.5);
      doc.setDrawColor(...C.gold); doc.setLineWidth(tubeW);
      for (let t = 0; t < tubeCount; t++) {
        const sign = (t % 2 === 0) ? -1 : 1;
        const idx = Math.floor(t / 2) + 1;
        const off = (d.spec.tubeCentreOffsetM ?? 0.5);
        const yL = FRAME / 2 + sign * off * idx;
        doc.line(tx(fx + cTube), ty(fy + yL), tx(fx + cTube + tubeLen), ty(fy + yL));
      }
    } else {
      // Draw discs at corner-offset pattern
      const N = d.spec.diffuserCount;
      const c0 = d.spec.cornerOffsetM;
      const L = FRAME;
      const mid = L / 2;
      const positions: [number, number][] = [];
      if (N === 4) {
        positions.push([c0, c0], [L - c0, c0], [c0, L - c0], [L - c0, L - c0]);
      } else if (N === 6 || N === 8) {
        positions.push([c0, c0], [L - c0, c0], [c0, mid], [L - c0, mid], [c0, L - c0], [L - c0, L - c0]);
        if (N === 8) { positions.push([mid, c0], [mid, L - c0]); }
      } else {
        // generic grid
        const k = Math.ceil(Math.sqrt(N));
        for (let r = 0; r < k; r++) for (let c = 0; c < k && positions.length < N; c++) {
          positions.push([c0 + (r * (L - 2 * c0) / (k - 1 || 1)), c0 + (c * (L - 2 * c0) / (k - 1 || 1))]);
        }
      }
      doc.setFillColor(...C.gold); doc.setDrawColor(...C.navy); doc.setLineWidth(0.15);
      const r_d = Math.max(0.8, (d.spec.diffuserDiamMm / 1000) * scale * 0.5);
      for (const [px, py] of positions) {
        doc.circle(tx(fx + px), ty(fy + py), r_d, 'FD');
      }
    }

    // Module label
    doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(...C.navy);
    doc.text(`M${i + 1}`, tx(fx + FRAME / 2), ty(fy + FRAME - 0.1), { align: 'center' });
  }

  // Gap dimension annotations
  if (d.config.cols > 1) {
    const gapX = d.config.gapXM;
    const yMid = ty(FRAME / 2);
    doc.setDrawColor(...C.grey); doc.setLineWidth(0.2);
    doc.line(tx(FRAME), yMid, tx(FRAME + gapX), yMid);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...C.grey);
    doc.text(s(`${gapX.toFixed(2)} m gap`), tx(FRAME + gapX / 2), yMid - 1, { align: 'center' });
  }

  // Scale bar 1 m
  const sx0 = x + 4;
  const sy0 = y + h - 4;
  const sb1 = 1 * scale;
  doc.setDrawColor(...C.black); doc.setLineWidth(0.4);
  doc.line(sx0, sy0, sx0 + sb1, sy0);
  doc.line(sx0, sy0 - 1, sx0, sy0 + 1);
  doc.line(sx0 + sb1, sy0 - 1, sx0 + sb1, sy0 + 1);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...C.black);
  doc.text('1 m', sx0 + sb1 / 2, sy0 - 1.5, { align: 'center' });

  // Legend
  doc.setFillColor(...C.gold);
  doc.rect(x + w - 30, y + 4, 3, 3, 'F');
  doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...C.black);
  doc.text(isTube ? 'Tube diffuser' : 'Disc diffuser', x + w - 26, y + 6.5);
  doc.setFillColor(120, 200, 230);
  doc.rect(x + w - 30, y + 9, 3, 3, 'F');
  doc.text('Plume footprint', x + w - 26, y + 11.5);
}

// ═════════════════════════════════════════════════════════════════════════════
// RESERVOIR VALIDATION REPORT — comprehensive 13-section engineering audit
// ═════════════════════════════════════════════════════════════════════════════
export interface ValidationReportData {
  spec: {
    diffuserCount: number; diffuserType: string; diffuserDiamMm: number;
    poreSizeUm: number; airflowPerDiffNm3h: number; mountingHeightM: number;
    cornerOffsetM: number; diffuserDropKpa: number; waterDepthM: number;
    waterTempC: number; currentDoMgL: number; alphaFactor: number;
    betaFactor: number; blowerEff: number; safetyPct: number;
    tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
    tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
  };
  reservoir: {
    areaHa: number; areaM2: number; meanDepth: number; volumeM3: number;
    shallow_m2: number; medium_m2: number; deep_m2: number; deepest_m2: number;
  };
  physics: {
    T: number; rho: number; mu: number; nu: number; sigma: number; dosat: number;
    poresEach: number; poresTotal: number; qPerDiff: number; qPerModule: number;
    d_b: number; v_rise: number; soteM: number; effSubm: number; sote_total: number;
    sotr: number; aotr: number; kw: number; r_inf_bed: number; r_surface: number;
    r_module: number; SAE: number;
  };
  optimization: {
    totalSOD: number; sodSafety: number; modsBySOD: number;
    rMix: number; areaPerMod: number; effArea: number; modsByCov: number;
    rec: number; recSpacing: number; minSpacing: number; maxSpacing: number;
    totalAirflow: number; totalAOTR: number; surplus: number;
    expectedCov: number; totalKw: number;
  };
  arrayLabels: { id: string; x: number; y: number }[];
  // Per-array placement (for plan view modules)
  modules?: { x: number; y: number }[];
  checks: { lbl: string; pass: boolean }[];
  sodRate: number;
  safetyFactor: number;
  targetCoverage: number;
}

export function generateReservoirValidationPDF(d: ValidationReportData, profile: SeasonalProfile): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const reportRef = `RB-RES-VAL-${profile.id}-${new Date().toISOString().slice(0, 10)}`;
  applyPDFMetadata(doc, {
    title:    'Reservoir Validation Report - Barcombe Reservoir',
    subject:  `Independent engineering audit for ${profile.label} profile`,
    extra:    reportRef,
    keywords: ['validation', 'audit', 'engineering review', 'pass-fail', profile.id],
  });
  const ctx: PageContext = {
    doc, pageW, pageH, margin, y: 0,
    reportTitle: 'Reservoir Validation Report',
    reportRef,
  };

  const passes = d.checks.filter(c => c.pass).length;
  const total = d.checks.length;
  const overallStatus = passes === total ? 'PASS' : passes >= total - 2 ? 'WARN' : 'FAIL';
  const isTube = d.spec.diffuserType === 'hdpe_porous_tube' || d.spec.diffuserType === 'epdm_porous_tube';

  // ── COVER PAGE ────────────────────────────────────────────────────────
  ctx.y = drawHeader(ctx);
  doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(...C.navy);
  doc.text(s('RESERVOIR VALIDATION REPORT'), pageW / 2, 55, { align: 'center' });
  doc.setFontSize(13); doc.setTextColor(...C.grey);
  doc.text(s('Independent Engineering Audit of Reservoir Aeration Design'), pageW / 2, 64, { align: 'center' });
  doc.setDrawColor(...C.gold); doc.setLineWidth(0.8);
  doc.line(margin, 72, pageW - margin, 72);

  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...C.navy);
  doc.text(s('Project: Barcombe Reservoir Aeration'), pageW / 2, 84, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.grey);
  doc.text(s(`Seasonal water profile: ${profile.label}  .  T=${profile.waterTempC} degC  .  DO_baseline=${profile.backgroundDOMgL} mg/L`),
    pageW / 2, 91, { align: 'center' });

  // Overall pass/fail box
  const statusColour = overallStatus === 'PASS' ? C.emerald : overallStatus === 'WARN' ? C.amber : C.red;
  doc.setFillColor(...statusColour); doc.setDrawColor(...statusColour); doc.setLineWidth(0.5);
  doc.rect(margin, 100, pageW - 2 * margin, 28, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(...C.white);
  doc.text(s(`OVERALL VALIDATION: ${overallStatus}`), pageW / 2, 112, { align: 'center' });
  doc.setFontSize(11);
  doc.text(s(`${passes} / ${total} independent checks satisfied`), pageW / 2, 120, { align: 'center' });

  // KPI panel
  doc.setFillColor(255, 251, 235); doc.setDrawColor(...C.gold); doc.setLineWidth(0.4);
  doc.rect(margin, 134, pageW - 2 * margin, 30, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...C.gold);
  doc.text(s('VALIDATED SYSTEM PARAMETERS'), pageW / 2, 140, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...C.black);
  const kpis = [
    { lbl: 'Reservoir', v: `${d.reservoir.areaHa.toFixed(2)} ha . ${d.reservoir.meanDepth.toFixed(2)} m mean` },
    { lbl: 'Recommended', v: `${d.optimization.rec} modules . ${d.optimization.recSpacing.toFixed(1)} m spacing` },
    { lbl: 'Supply', v: `${d.optimization.totalAOTR.toFixed(2)} kg O2/h AOTR . ${d.optimization.totalKw.toFixed(1)} kW` },
    { lbl: 'Coverage', v: `${d.optimization.expectedCov.toFixed(0)}% of ${(d.optimization.effArea / 10000).toFixed(2)} ha aeration zone` },
  ];
  let ky = 147;
  for (const k of kpis) {
    doc.setTextColor(...C.grey); doc.text(s(k.lbl), margin + 5, ky);
    doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold');
    doc.text(s(k.v), margin + 35, ky);
    doc.setFont('helvetica', 'normal');
    ky += 4;
  }

  // Reservoir plan view WITH 20m GRID on cover
  drawReservoirPlanView(doc, margin, 175, pageW - 2 * margin, 95, {
    bedColour: [124, 74, 42],
    bedLabel: 'Reservoir bed (validated design)',
    arrayLabels: d.arrayLabels,
    modules: d.modules,
    gridSpacingM: 20,
    showCoordinates: true,
  });
  ctx.y = 272;
  captionLine(ctx, 'Fig 1 - Reservoir plan view with 20 m grid overlay and validated array positions (A-H)');

  // ── ASSUMPTIONS ───────────────────────────────────────────────────────
  newPage(ctx);
  drawAssumptionsBlock(ctx, profile, {
    diffuserCount: d.spec.diffuserCount, diffuserType: d.spec.diffuserType,
    diffuserDiamMm: d.spec.diffuserDiamMm, poreSizeUm: d.spec.poreSizeUm,
    airflowPerDiffNm3h: d.spec.airflowPerDiffNm3h, mountingHeightM: d.spec.mountingHeightM,
    cornerOffsetM: d.spec.cornerOffsetM, diffuserDropKpa: d.spec.diffuserDropKpa,
    blowerEff: d.spec.blowerEff, safetyPct: d.spec.safetyPct,
    alphaFactor: d.spec.alphaFactor, betaFactor: d.spec.betaFactor,
    tubeLengthM: d.spec.tubeLengthM, tubeCount: d.spec.tubeCount,
    tubeDiamMm: d.spec.tubeDiamMm, tubePattern: d.spec.tubePattern,
    tubeCentreOffsetM: d.spec.tubeCentreOffsetM, porePerCm: d.spec.porePerCm,
  }, {
    areaHa: d.reservoir.areaHa, meanDepthM: d.reservoir.meanDepth,
    surveyRef: 'Shoreline Surveys Ltd J2020 (09 Jan 2023)',
  });

  // ── DETAILED PLAN VIEW (own page, larger, with 20 m grid) ─────────────
  newPage(ctx);
  sectionHeading(ctx, 'V', 'Reservoir Layout - Validated Module Placement (20 m Grid)');
  paragraph(ctx, 'The reservoir plan view below shows the validated array positions (A-H) overlaid on the J2020 bathymetric survey polygon. A 20 m grid is provided for distance reference. Coordinates are in metres from the polygon SW corner.');
  spacer(ctx, 2);
  drawReservoirPlanView(doc, margin, ctx.y, pageW - 2 * margin, 200, {
    bedColour: [124, 74, 42],
    bedLabel: 'Reservoir bed',
    arrayLabels: d.arrayLabels,
    modules: d.modules,
    gridSpacingM: 20,
    showCoordinates: true,
  });
  ctx.y += 202;
  captionLine(ctx, 'Fig 2 - Validated reservoir plan view with 20 m grid (60% of available A4 page)');

  // Array position table
  newPage(ctx);
  sectionHeading(ctx, 'V.1', 'Validated Array Positions');
  dataTable(ctx,
    ['Array', 'Position (E, N) m', 'Spacing to nearest', 'Status'],
    d.arrayLabels.map((a, i) => {
      // Distance to nearest neighbour
      let minD = Infinity;
      for (let j = 0; j < d.arrayLabels.length; j++) {
        if (i === j) continue;
        const dist = Math.hypot(a.x - d.arrayLabels[j].x, a.y - d.arrayLabels[j].y);
        if (dist < minD) minD = dist;
      }
      return [a.id, `(${a.x}, ${a.y})`, `${minD.toFixed(0)} m`, 'PLACED'];
    }));

  // ── §1 Survey data ────────────────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '1', 'Survey Data Validation');
  kvTable(ctx, [
    { k: 'Survey provider',          v: 'Shoreline Surveys Ltd', src: 'verified' },
    { k: 'Project number',           v: 'J2020' },
    { k: 'Drawing',                  v: '01v2' },
    { k: 'Date',                     v: '09 January 2023' },
    { k: 'Method',                   v: 'Single-beam sonar' },
    { k: 'Scale',                    v: '1:1,000 on A1' },
    { k: 'Datum',                    v: 'ODN Newlyn' },
    { k: 'Water level',              v: '8.79 m ODN' },
    { k: 'Depth range surveyed',     v: '0.79 - 4.79 m' },
    { k: 'Coordinate system',        v: 'OSGB36 OSTN15' },
    { k: 'Grid easting',             v: '~ 543,750 - 544,000 E' },
    { k: 'Grid northing',            v: '~ 115,000 N' },
  ]);

  // ── §2 Reservoir geometry ────────────────────────────────────────────
  sectionHeading(ctx, '2', 'Reservoir Geometry - Area, Volume, Depth Distribution');
  formulaBlock(ctx, [
    'Shoelace area: A = 0.5 x |Sum(x_i x y_{i+1} - x_{i+1} x y_i)|',
    'Volume: V = Sum(d(x,y) x dA)  (cell sampling Delta = 3 m)',
    'Mean depth: H_bar = V / A',
  ]);
  kvTable(ctx, [
    { k: 'Polygon vertices', v: '57' },
    { k: 'Plan area',        v: `${d.reservoir.areaHa.toFixed(3)} ha`, src: 'shoelace' },
    { k: 'Plan area (m2)',   v: `${d.reservoir.areaM2.toFixed(0)} m2` },
    { k: 'Mean depth',       v: `${d.reservoir.meanDepth.toFixed(2)} m`, src: 'V/A' },
    { k: 'Volume',           v: `${d.reservoir.volumeM3.toFixed(0)} m3`, src: 'Sum d x dA' },
    { k: 'Area < 2 m deep',  v: `${(d.reservoir.shallow_m2 / 10000).toFixed(2)} ha (${(d.reservoir.shallow_m2 / d.reservoir.areaM2 * 100).toFixed(0)}%)` },
    { k: 'Area 2 - 3 m',     v: `${(d.reservoir.medium_m2 / 10000).toFixed(2)} ha (${(d.reservoir.medium_m2 / d.reservoir.areaM2 * 100).toFixed(0)}%)`, src: 'silt zone' },
    { k: 'Area 3 - 4 m',     v: `${(d.reservoir.deep_m2 / 10000).toFixed(2)} ha (${(d.reservoir.deep_m2 / d.reservoir.areaM2 * 100).toFixed(0)}%)` },
    { k: 'Area > 4 m',       v: `${(d.reservoir.deepest_m2 / 10000).toFixed(2)} ha (${(d.reservoir.deepest_m2 / d.reservoir.areaM2 * 100).toFixed(0)}%)`, src: 'NE basin' },
    { k: 'Effective area (>= 2 m)', v: `${(d.optimization.effArea / 10000).toFixed(2)} ha`, src: 'aeration zone' },
  ]);

  // ── §3 Module design ──────────────────────────────────────────────────
  sectionHeading(ctx, '3', 'Module Design Summary');
  kvTable(ctx, [
    { k: 'Frame size',        v: '2.0 x 2.0 m' },
    { k: 'Diffuser type',     v: d.spec.diffuserType },
    { k: 'Diffuser shape',    v: isTube ? 'TUBE (line source)' : 'DISC (point source)' },
    ...(isTube ? [
      { k: 'Tube outer diameter', v: `${d.spec.tubeDiamMm ?? 75} mm` },
      { k: 'Tube length each',    v: `${(d.spec.tubeLengthM ?? 1.8).toFixed(2)} m` },
      { k: 'Tubes per module',    v: String(d.spec.tubeCount ?? 2) },
      { k: 'Pattern',             v: String(d.spec.tubePattern ?? 'parallel') },
      { k: 'Pore density',        v: `${d.spec.porePerCm ?? 8} pores/cm` },
    ] : [
      { k: 'Diffuser diameter',   v: `${d.spec.diffuserDiamMm} mm` },
      { k: 'Diffusers per module', v: String(d.spec.diffuserCount) },
      { k: 'Corner offset',       v: `${d.spec.cornerOffsetM.toFixed(2)} m` },
    ]),
    { k: 'Pore size',          v: `${d.spec.poreSizeUm} um` },
    { k: 'Pores per diffuser', v: String(d.physics.poresEach) },
    { k: 'Total pores/module', v: String(d.physics.poresTotal) },
    { k: 'Airflow per unit',   v: `${d.spec.airflowPerDiffNm3h.toFixed(2)} Nm3/h` },
    { k: 'Airflow per module', v: `${d.physics.qPerModule.toFixed(2)} Nm3/h` },
    { k: 'Mounting height',    v: `${d.spec.mountingHeightM.toFixed(2)} m` },
    { k: 'Water temperature',  v: `${d.spec.waterTempC.toFixed(1)} degC` },
    { k: 'Current DO',         v: `${d.spec.currentDoMgL.toFixed(2)} mg/L` },
  ]);

  // ── §4 Water properties ───────────────────────────────────────────────
  sectionHeading(ctx, '4', 'Water Physical Properties');
  kvTable(ctx, [
    { k: 'Density rho_w',          v: `${d.physics.rho.toFixed(2)} kg/m3`, src: 'Kell 1975' },
    { k: 'Dynamic viscosity mu_w', v: `${(d.physics.mu * 1000).toFixed(3)} mPa.s`, src: 'Andrade-Vogel' },
    { k: 'Surface tension sigma',   v: `${(d.physics.sigma * 1000).toFixed(2)} mN/m`, src: 'Vargaftik 1983' },
    { k: 'DO saturation',          v: `${d.physics.dosat.toFixed(2)} mg/L`, src: 'Benson & Krause' },
  ]);

  // ── §5 Bubble physics ─────────────────────────────────────────────────
  sectionHeading(ctx, '5', 'Per-Module Bubble Physics');
  kvTable(ctx, [
    { k: 'Bubble diameter',       v: `${d.physics.d_b.toFixed(3)} mm`, src: 'Fritz + Loubiere' },
    { k: 'Rise velocity',         v: `${d.physics.v_rise.toFixed(3)} m/s`, src: 'Clift 1978' },
    { k: 'SOTE/m',                v: `${d.physics.soteM.toFixed(2)} %/m`, src: 'Stenstrom 2008' },
    { k: 'Effective submergence', v: `${d.physics.effSubm.toFixed(2)} m` },
    { k: 'Bed influence radius',  v: `${d.physics.r_inf_bed.toFixed(2)} m`, src: 'Seol 2007 (disc) or Wuest 1992 (tube)' },
    { k: 'Surface plume radius',  v: `${d.physics.r_surface.toFixed(2)} m` },
  ]);

  // ── §6 O2 transfer ────────────────────────────────────────────────────
  sectionHeading(ctx, '6', 'Per-Module Oxygen Transfer');
  kvTable(ctx, [
    { k: 'SOTR per module',  v: `${d.physics.sotr.toFixed(4)} kg O2/h`, src: 'ASCE 2-06' },
    { k: 'AOTR per module',  v: `${d.physics.aotr.toFixed(4)} kg O2/h`, src: 'alpha . beta . theta . dDO' },
    { k: 'Blower kW/module', v: `${d.physics.kw.toFixed(3)} kW`, src: 'isentropic' },
    { k: 'Aeration efficiency', v: `${d.physics.SAE.toFixed(2)} kg O2/kWh`, src: 'AOTR / kW' },
  ]);

  // ── §7 SOD & O2 balance ───────────────────────────────────────────────
  sectionHeading(ctx, '7', 'Sediment Oxygen Demand & O2 Balance');
  formulaBlock(ctx, [
    `SOD_total = A_res x rate / 24000 = ${d.optimization.totalSOD.toFixed(3)} kg O2/h`,
    `SOD_safety = SOD x ${d.safetyFactor} = ${d.optimization.sodSafety.toFixed(3)} kg O2/h`,
    `n_required (SOD) = ceil(SOD_safety / AOTR_module) = ${d.optimization.modsBySOD} modules`,
  ]);
  kvTable(ctx, [
    { k: 'SOD rate',           v: `${d.sodRate.toFixed(2)} g O2/m2/d`, src: profile.source },
    { k: 'Total SOD demand',   v: `${d.optimization.totalSOD.toFixed(3)} kg O2/h` },
    { k: 'Safety factor',      v: `x ${d.safetyFactor}` },
    { k: 'SOD with safety',    v: `${d.optimization.sodSafety.toFixed(3)} kg O2/h` },
    { k: 'Total AOTR (system)', v: `${d.optimization.totalAOTR.toFixed(3)} kg O2/h` },
    { k: 'O2 surplus',         v: `${d.optimization.surplus >= 0 ? '+' : ''}${d.optimization.surplus.toFixed(3)} kg O2/h`,
      src: d.optimization.surplus >= 0 ? 'PASS' : 'DEFICIT' },
    { k: 'Supply ratio',       v: `${(d.optimization.totalAOTR / d.optimization.totalSOD).toFixed(2)}x` },
  ]);

  // ── §8 Destratification coverage ──────────────────────────────────────
  sectionHeading(ctx, '8', 'Destratification Coverage');
  formulaBlock(ctx, [
    `r_mix = 5 x H_mean = ${d.optimization.rMix.toFixed(1)} m  [Wuest 1992]`,
    `A_per_module = pi x r^2 x 0.65 = ${d.optimization.areaPerMod.toFixed(0)} m2`,
    `n_required (cov) = ceil(${d.targetCoverage}% x A_eff / A_mod) = ${d.optimization.modsByCov} modules`,
  ]);
  kvTable(ctx, [
    { k: 'Mean depth H_bar',    v: `${d.reservoir.meanDepth.toFixed(2)} m` },
    { k: 'Mixing radius r_mix', v: `${d.optimization.rMix.toFixed(1)} m` },
    { k: 'Area per module',     v: `${d.optimization.areaPerMod.toFixed(0)} m2` },
    { k: 'Effective area',      v: `${(d.optimization.effArea / 10000).toFixed(2)} ha` },
    { k: 'Target coverage',     v: `${d.targetCoverage}%` },
    { k: 'Modules for coverage', v: String(d.optimization.modsByCov) },
    { k: 'Expected coverage',   v: `${d.optimization.expectedCov.toFixed(1)}%` },
  ]);

  // ── §9 Module count derivation ────────────────────────────────────────
  sectionHeading(ctx, '9', 'Optimal Module Count');
  kvTable(ctx, [
    { k: 'By SOD demand',       v: `${d.optimization.modsBySOD} modules` },
    { k: 'By coverage',         v: `${d.optimization.modsByCov} modules` },
    { k: 'Recommended count',   v: `${d.optimization.rec} modules`,
      src: d.optimization.modsBySOD >= d.optimization.modsByCov ? 'SOD-limited' : 'coverage-limited' },
    { k: 'Modules per hectare', v: `${(d.optimization.rec / d.reservoir.areaHa).toFixed(2)} /ha`, src: 'AWWA 1-4 /ha' },
    { k: 'Total airflow',       v: `${d.optimization.totalAirflow.toFixed(1)} Nm3/h` },
    { k: 'Total power',         v: `${d.optimization.totalKw.toFixed(2)} kW` },
    { k: 'Specific energy',     v: `${(d.optimization.totalKw / d.reservoir.areaHa).toFixed(2)} kW/ha` },
  ]);

  // ── §10 Spacing ───────────────────────────────────────────────────────
  sectionHeading(ctx, '10', 'Optimal Spacing');
  kvTable(ctx, [
    { k: 'Square-grid spacing', v: `${d.optimization.recSpacing.toFixed(2)} m` },
    { k: 'Hex-grid spacing',    v: `${(d.optimization.recSpacing * 1.0746).toFixed(2)} m` },
    { k: 'Minimum allowable',   v: `${d.optimization.minSpacing.toFixed(2)} m`, src: 'no plume merge' },
    { k: 'Maximum allowable',   v: `${d.optimization.maxSpacing.toFixed(2)} m`, src: 'continuous coverage' },
    { k: 'Recommended in bounds', v:
      d.optimization.recSpacing >= d.optimization.minSpacing && d.optimization.recSpacing <= d.optimization.maxSpacing
      ? 'WITHIN BOUNDS' : 'CHECK' },
  ]);

  // ── §11 Final pass/fail summary ───────────────────────────────────────
  sectionHeading(ctx, '11', `Final Pass / Fail Summary (${passes}/${total} pass)`);
  dataTable(ctx,
    ['#', 'Validation check', 'Status'],
    d.checks.map((c, i) => [i + 1, c.lbl, c.pass ? 'PASS' : 'WARN']));

  // ── §12 References ────────────────────────────────────────────────────
  sectionHeading(ctx, '12', 'References');
  for (const ref of REFERENCES) paragraph(ctx, ref, { small: true });

  drawSignatureBlock(ctx);
  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`${reportRef}.pdf`);
}

// ═════════════════════════════════════════════════════════════════════════════
// DIFFUSER VALIDATION REPORT — 11-step engineering audit of the diffuser module
// ═════════════════════════════════════════════════════════════════════════════
export interface DiffuserValidationData {
  spec: {
    diffuserCount: number; cornerOffsetM: number; mountingHeightM: number;
    diffuserType: string;  diffuserDiamMm: number; poreSizeUm: number;
    airflowPerDiffNm3h: number; diffuserDropKpa: number; waterDepthM: number;
    waterTempC: number; currentDoMgL: number; alphaFactor: number; betaFactor: number;
    blowerEff: number; safetyPct: number;
    sedimentD50Um: number; sedimentRho: number;
    reservoirAreaHa: number; targetCoveragePct: number; sodGO2m2d: number;
    tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
    tubePattern?: string; tubeCentreOffsetM?: number; porePerCm?: number;
  };
  // All 32+ computed values from the V useMemo on the validation page
  V: {
    T: number; rho_w: number; mu_w: number; sigma_w: number; cs_T: number;
    effSubm: number; P_bed: number; d_pore: number; d_stat_mm: number;
    q_pore_m3s: number; dyn_corr: number; d_b_mm: number; d_b_surf: number;
    v_rise_bed: number; v_rise_surf: number; riseTimeS: number; Re_b: number; Eo_b: number;
    corner: number; innerSpanM: number; s_min: number; r_inf: number;
    edgeCoverM: number; effectivePitchM: number;
    influenceAreaM2: number; modulesPerHa: number; modulesPerHa_check: number;
    pores: number; q_act_bed: number; B_buoy: number; U_plume: number; v_bed: number;
    tau_bed: number; shields: number; shields_cr: number; disturbRatio: number;
    turbCorr: number; d_b_dist: number; d_b_surf_dist: number;
    sote_frac: number; modAirNm3h: number; modSOTR: number; modAOTR: number;
    SAE_mODR: number; sodResuspFactor: number; effectiveSOD: number;
    reservoirAreaM2: number; targetAreaM2: number;
    modulesRequired: number; coveredAreaM2: number; coveredPct: number;
    totalAOTR: number; sodNomKgO2h: number; sodDistKgO2h: number; oxygenSurplus: number;
    dtype: { value: string; label: string; sote: number; notes?: string };
  };
  checks: { lbl: string; pass: boolean; value: string; note?: string }[];
}

export function generateDiffuserValidationPDF(d: DiffuserValidationData, profile?: SeasonalProfile): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const reportRef = `RB-DIFF-VAL-${new Date().toISOString().slice(0, 10)}`;
  applyPDFMetadata(doc, {
    title:    'Diffuser Validation Report - Barcombe Reservoir',
    subject:  `11-step independent recalculation of diffuser module physics`,
    extra:    reportRef,
    keywords: ['diffuser', 'validation', 'Fritz', 'Clift', 'Shields', 'SOTR', 'AOTR'],
  });
  const ctx: PageContext = {
    doc, pageW, pageH, margin, y: 0,
    reportTitle: 'Diffuser Validation Report',
    reportRef,
  };
  const isTube = d.spec.diffuserType === 'hdpe_porous_tube' || d.spec.diffuserType === 'epdm_porous_tube';
  const isCustom = d.spec.diffuserType === 'custom';

  const passes = d.checks.filter(c => c.pass).length;
  const total = d.checks.length;
  const overall = passes === total ? 'PASS' : passes >= total - 2 ? 'WARN' : 'FAIL';

  // ── COVER ─────────────────────────────────────────────────────────────
  ctx.y = drawHeader(ctx);
  doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(...C.navy);
  doc.text(s('DIFFUSER VALIDATION REPORT'), pageW / 2, 55, { align: 'center' });
  doc.setFontSize(13); doc.setTextColor(...C.grey);
  doc.text(s('Independent Recalculation of Module Physics from First Principles'), pageW / 2, 64, { align: 'center' });
  doc.setDrawColor(...C.gold); doc.setLineWidth(0.8);
  doc.line(margin, 72, pageW - margin, 72);

  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(...C.navy);
  doc.text(s('Project: Barcombe Reservoir Aeration'), pageW / 2, 84, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.grey);
  doc.text(s('Client: Atkins Realis'), pageW / 2, 91, { align: 'center' });
  if (profile) {
    doc.text(s(`Seasonal profile: ${profile.label}  .  T=${profile.waterTempC} degC  .  DO_baseline=${profile.backgroundDOMgL} mg/L`),
      pageW / 2, 97, { align: 'center' });
  }

  // Overall status panel
  const statusColour = overall === 'PASS' ? C.emerald : overall === 'WARN' ? C.amber : C.red;
  doc.setFillColor(...statusColour); doc.setDrawColor(...statusColour);
  doc.rect(margin, 105, pageW - 2 * margin, 25, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(...C.white);
  doc.text(s(`OVERALL VALIDATION: ${overall}`), pageW / 2, 116, { align: 'center' });
  doc.setFontSize(10);
  doc.text(s(`${passes} / ${total} independent calculation checks satisfied`), pageW / 2, 124, { align: 'center' });

  // Key parameters panel
  doc.setFillColor(255, 251, 235); doc.setDrawColor(...C.gold); doc.setLineWidth(0.4);
  doc.rect(margin, 138, pageW - 2 * margin, 50, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...C.gold);
  doc.text(s('VALIDATED MODULE PARAMETERS'), pageW / 2, 145, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.black);
  let cy = 152;
  const summary: [string, string][] = [
    ['Diffuser type', `${d.V.dtype.label}${isTube ? ' (TUBE - line source)' : isCustom ? ' (CUSTOM)' : ' (DISC - point source)'}`],
    ['Module airflow', `${fmt(d.V.modAirNm3h, 2)} Nm3/h (= ${d.spec.diffuserCount} x ${fmt(d.spec.airflowPerDiffNm3h, 2)})`],
    ['Bubble diameter at bed', `${fmt(d.V.d_b_mm, 3)} mm (Fritz + Loubiere)`],
    ['Effective submergence', `${fmt(d.V.effSubm, 2)} m`],
    ['SOTE (total)', `${fmt(d.V.sote_frac * 100, 2)} % (= ${fmt(d.V.dtype.sote, 2)} %/m x H)`],
    ['Module AOTR', `${fmt(d.V.modAOTR, 3)} kg O2/h`],
    ['Shields ratio (bed)', `${fmt(d.V.disturbRatio, 2)} x (theta/theta_cr)`],
  ];
  for (const [k, v] of summary) {
    doc.setTextColor(...C.grey); doc.text(s(k), margin + 5, cy);
    doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold');
    doc.text(s(v), pageW / 2 + 5, cy);
    doc.setFont('helvetica', 'normal');
    cy += 5;
  }

  doc.setFont('helvetica', 'italic'); doc.setFontSize(8); doc.setTextColor(...C.grey);
  doc.text(s('All calculations below are independently re-derived from peer-reviewed sources.'),
    pageW / 2, 230, { align: 'center' });
  doc.text(s('No values pulled from any other report - this is a fresh independent audit.'),
    pageW / 2, 235, { align: 'center' });

  // ── CALCULATION STEPS METHOD (Table of Contents) ──────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'M', 'Calculation Method - Step-by-Step Audit Trail');
  paragraph(ctx, 'This validation report follows a strict 11-step methodology. Each step uses peer-reviewed formulas with all input values substituted explicitly so the reader can independently verify every result. No values are taken from any other report.');
  dataTable(ctx,
    ['Step', 'Title', 'Method / Reference', 'Formula type'],
    [
      ['1',  'Water Physical Properties',     'Kell 1975 / Andrade-Vogel / Vargaftik 1983 / Benson & Krause', 'temp-dependent correlations'],
      ['2',  'Bubble Formation at Orifice',   'Fritz 1935 (static) + Loubiere & Hebrard 2003 (dynamic)',       'force balance + flow correction'],
      ['3',  'Bubble Rise & Surface Expansion','Clift et al. 1978 + ideal gas',                                 'piecewise velocity + P^(1/3)'],
      ['4',  'Frame Geometry & Spacing',       'Corner-offset model + influence radius',                       'geometric'],
      ['5',  'Bed Velocity & Shear Stress',   'Seol 2007 / Schladow 1993 plume',                              'B0 = g.q ; tau = rho.C_D.U^2'],
      ['6',  'Shields Criterion',              'Shields 1936 / van Rijn 1984 / Mehta 1986',                    'theta = tau / [(rho_s - rho_w).g.d50]'],
      ['7',  'Oxygen Transfer (SOTR/AOTR)',   'ASCE 2-06 / Stenstrom & Rosso 2008 / Metcalf & Eddy 2014',     'SOTE.Q.rho.w x alpha.beta.theta'],
      ['8',  'SOD & O2 Mass Balance',         'Hickey & Gibbs 2009 / Mehta resuspension',                     'AOTR - effective_SOD'],
      ['9',  'Cross-Checks',                   'Independent derivation paths',                                  'geometry vs derived'],
      ['10', 'Final Pass/Fail Summary',        '12 independent criteria',                                       'pass/warn/fail'],
      ['11', 'Literature References',          'Peer-reviewed sources only',                                    'list'],
    ]);

  spacer(ctx, 4);
  paragraph(ctx, 'Reader verification: each formula in the report is presented with its inputs substituted numerically (e.g. "d_b = d_static x 1.234 = 2.567 mm"). You can re-derive each result with a calculator. All intermediate values are traceable back to the input parameters listed in section A (Assumptions).', { italic: true, small: true });

  // ── ASSUMPTIONS ──────────────────────────────────────────────────────
  newPage(ctx);
  if (profile) {
    drawAssumptionsBlock(ctx, profile, {
      diffuserCount: d.spec.diffuserCount, diffuserType: d.spec.diffuserType,
      diffuserDiamMm: d.spec.diffuserDiamMm, poreSizeUm: d.spec.poreSizeUm,
      airflowPerDiffNm3h: d.spec.airflowPerDiffNm3h, mountingHeightM: d.spec.mountingHeightM,
      cornerOffsetM: d.spec.cornerOffsetM, diffuserDropKpa: d.spec.diffuserDropKpa,
      blowerEff: d.spec.blowerEff, safetyPct: d.spec.safetyPct,
      alphaFactor: d.spec.alphaFactor, betaFactor: d.spec.betaFactor,
      tubeLengthM: d.spec.tubeLengthM, tubeCount: d.spec.tubeCount,
      tubeDiamMm: d.spec.tubeDiamMm, tubePattern: d.spec.tubePattern,
      tubeCentreOffsetM: d.spec.tubeCentreOffsetM, porePerCm: d.spec.porePerCm,
    }, {
      areaHa: d.spec.reservoirAreaHa, meanDepthM: d.spec.waterDepthM,
      surveyRef: 'Shoreline Surveys Ltd J2020 (09 Jan 2023)',
    });
  } else {
    sectionHeading(ctx, 'A', 'Design Inputs Summary');
    kvTable(ctx, [
      { k: 'Diffuser type',           v: d.spec.diffuserType,                          src: 'client selection' },
      { k: 'Diffusers per module',    v: String(d.spec.diffuserCount) },
      { k: 'Diffuser diameter',       v: `${d.spec.diffuserDiamMm} mm` },
      { k: 'Pore size',               v: `${d.spec.poreSizeUm} um` },
      { k: 'Corner offset',           v: `${fmt(d.spec.cornerOffsetM, 2)} m` },
      { k: 'Mounting height',         v: `${fmt(d.spec.mountingHeightM, 2)} m` },
      { k: 'Airflow per diffuser',    v: `${fmt(d.spec.airflowPerDiffNm3h, 2)} Nm3/h` },
      { k: 'Water depth',             v: `${fmt(d.spec.waterDepthM, 2)} m` },
      { k: 'Water temperature',       v: `${fmt(d.spec.waterTempC, 1)} degC` },
      { k: 'Current DO',              v: `${fmt(d.spec.currentDoMgL, 2)} mg/L` },
      { k: 'alpha factor',            v: fmt(d.spec.alphaFactor, 2) },
      { k: 'beta factor',             v: fmt(d.spec.betaFactor, 2) },
      { k: 'Reservoir area',          v: `${fmt(d.spec.reservoirAreaHa, 2)} ha` },
      { k: 'Target coverage',         v: `${d.spec.targetCoveragePct} %` },
      { k: 'SOD rate (nominal)',      v: `${fmt(d.spec.sodGO2m2d, 2)} g O2/m2/d` },
    ]);
  }

  // ── EXECUTIVE SUMMARY (customer-facing, top-of-report verdict) ───────
  newPage(ctx);
  sectionHeading(ctx, 'X', 'Executive Summary');
  paragraph(ctx, `This report independently re-derives the physics of the proposed diffuser module from first principles. The analysis uses ${total} independent validation criteria covering bubble physics, geometry, hydrodynamics, sediment disturbance, oxygen transfer and the oxygen mass balance.`);
  spacer(ctx, 2);
  paragraph(ctx, `OVERALL RESULT: ${overall} - ${passes} of ${total} checks satisfied.`, { bold: true });
  spacer(ctx, 2);
  kvTable(ctx, [
    { k: 'Diffuser geometry',          v: isTube ? 'TUBE - line source (Wuest 1992)' : isCustom ? `CUSTOM (${d.V.dtype.label})` : 'DISC - point source (Seol 2007)' },
    { k: 'Operating bubble diameter',   v: `${fmt(d.V.d_b_mm, 2)} mm at orifice, ${fmt(d.V.d_b_surf, 2)} mm at surface`,
      src: d.V.d_b_mm >= 1 && d.V.d_b_mm <= 5 ? 'fine-bubble band' : 'OUT OF BAND' },
    { k: 'Bubble rise time',            v: `${fmt(d.V.riseTimeS, 1)} s through ${fmt(d.V.effSubm, 1)} m water column` },
    { k: 'SOTE (total)',                v: `${(d.V.sote_frac * 100).toFixed(2)} %`,
      src: 'Stenstrom & Rosso (2008)' },
    { k: 'Module airflow',              v: `${fmt(d.V.modAirNm3h, 2)} Nm3/h` },
    { k: 'Module AOTR',                 v: `${fmt(d.V.modAOTR, 3)} kg O2/h`,
      src: `at current DO ${fmt(d.spec.currentDoMgL, 1)} mg/L` },
    { k: 'Aeration efficiency SAE',     v: `${fmt(d.V.SAE_mODR, 2)} kg O2/kWh`,
      src: d.V.SAE_mODR >= 1.2 ? 'good' : 'check' },
    { k: 'Shields ratio (bed)',         v: `${fmt(d.V.disturbRatio, 2)} x theta_cr`,
      src: d.V.disturbRatio < 1 ? 'STABLE' : d.V.disturbRatio < 2 ? 'mild resuspension' : 'ERODING' },
    { k: 'O2 supply vs demand',         v: `${d.V.oxygenSurplus >= 0 ? '+' : ''}${fmt(d.V.oxygenSurplus, 3)} kg O2/h`,
      src: d.V.oxygenSurplus >= 0 ? 'PASS' : 'DEFICIT' },
    { k: 'Modules required for system', v: `${d.V.modulesRequired} (covers ${fmt(d.V.coveredPct, 1)}% of ${fmt(d.spec.reservoirAreaHa, 2)} ha)` },
  ]);

  spacer(ctx, 4);
  if (overall === 'PASS') {
    paragraph(ctx, 'CONCLUSION: The proposed diffuser module design satisfies all engineering criteria. Detailed step-by-step calculations follow in sections 1-11.', { bold: true });
  } else if (overall === 'WARN') {
    paragraph(ctx, `CONCLUSION: The proposed design satisfies the majority of criteria but ${total - passes} non-critical warnings require review. See section 10 for the full pass/fail table.`, { bold: true });
  } else {
    paragraph(ctx, `CONCLUSION: ${total - passes} critical issues identified. Design revision recommended before deployment. See section 10 for failing criteria and consult sections 1-9 for the underlying calculations.`, { bold: true });
  }

  // ── STEP 1: Water physical properties ────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '1', 'Water Physical Properties at Design Temperature');
  paragraph(ctx, 'Temperature-dependent water properties drive every downstream calculation. All formulas are peer-reviewed.');
  formulaBlock(ctx, [
    'rho_w = 999.842 + 6.794e-2*T - 9.095e-3*T^2 + ... [Kell 1975]',
    'mu_w = 2.414e-5 x 10^(247.8 / (T+133.15)) [Andrade-Vogel]',
    'sigma_w = 0.07564 - 1.376e-4 x T [Vargaftik 1983]',
    'C_sat = 468 / (31.6 + T) [Benson & Krause]',
  ]);
  kvTable(ctx, [
    { k: 'Water temperature',           v: `${fmt(d.V.T, 1)} degC` },
    { k: 'Density rho_w',               v: `${fmt(d.V.rho_w, 2)} kg/m3`,           src: 'Kell 1975' },
    { k: 'Dynamic viscosity mu_w',      v: `${(d.V.mu_w * 1000).toFixed(3)} mPa.s`, src: 'Andrade-Vogel' },
    { k: 'Surface tension sigma_w',      v: `${(d.V.sigma_w * 1000).toFixed(2)} mN/m`, src: 'Vargaftik 1983' },
    { k: 'DO saturation C_sat',         v: `${fmt(d.V.cs_T, 2)} mg/L`,             src: 'Benson & Krause' },
  ]);

  // ── STEP 2: Bubble formation at orifice ──────────────────────────────
  sectionHeading(ctx, '2', 'Bubble Formation at Orifice (Fritz 1935 + Loubière & Hébrard 2003)');
  paragraph(ctx, 'Bubble diameter at detachment is the foundation for SOTE, rise velocity and plume scaling. Two-step formulation: static balance + dynamic correction for flow per pore.');
  formulaBlock(ctx, [
    'STEP A: Static balance (Fritz 1935)',
    'd_static = [6 x sigma x d_pore / (rho_w x g)]^(1/3)',
    `        = [6 x ${(d.V.sigma_w * 1000).toFixed(2)}e-3 x ${(d.V.d_pore * 1e6).toFixed(0)}e-6 / (${fmt(d.V.rho_w, 1)} x 9.81)]^(1/3)`,
    `        = ${fmt(d.V.d_stat_mm, 3)} mm`,
    '',
    'STEP B: Dynamic correction (Loubiere & Hebrard 2003)',
    'q_pore = Q_diff / (3600 x N_pores) [m3/s/pore]',
    `       = ${fmt(d.spec.airflowPerDiffNm3h, 2)} / (3600 x ${d.V.pores}) = ${(d.V.q_pore_m3s * 1e9).toFixed(2)} uL/s`,
    'd_b = d_static x (1 + 0.9 x (q_pore x 1e9)^0.25)',
    `    = ${fmt(d.V.d_stat_mm, 3)} x ${fmt(d.V.dyn_corr, 3)} = ${fmt(d.V.d_b_mm, 3)} mm`,
  ]);
  kvTable(ctx, [
    { k: 'Active pores per diffuser',     v: String(d.V.pores),                   src: 'TYPE-AWARE' },
    { k: 'Air flow per pore',              v: `${(d.V.q_pore_m3s * 1e9).toFixed(2)} uL/s` },
    { k: 'Static bubble diameter',         v: `${fmt(d.V.d_stat_mm, 3)} mm`,        src: 'Fritz 1935' },
    { k: 'Dynamic correction factor',      v: `${fmt(d.V.dyn_corr, 3)} x`,           src: 'Loubiere 2003' },
    { k: 'OPERATING bubble diameter d_b',  v: `${fmt(d.V.d_b_mm, 3)} mm` },
  ]);

  // ── STEP 3: Rise & expansion ─────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '3', 'Bubble Rise Velocity & Surface Expansion (Clift et al. 1978)');
  paragraph(ctx, 'Rise velocity follows piecewise linear correlation by bubble size, with temperature correction via kinematic viscosity ratio.');
  formulaBlock(ctx, [
    'v0 = 0.14 + 0.065 x d_b  [for 0.5 < d_b <= 3.0 mm]',
    'v_rise = v0 x (nu_20 / nu_T)^0.7   [temperature correction]',
    '',
    'Bubble expansion (ideal gas):',
    'd_surf = d_bed x (P_bed / P_atm)^(1/3)',
    `      = ${fmt(d.V.d_b_mm, 3)} x (${fmt(d.V.P_bed/1000, 1)} / 101.3)^(1/3) = ${fmt(d.V.d_b_surf, 3)} mm`,
  ]);
  kvTable(ctx, [
    { k: 'Rise velocity at bed',        v: `${fmt(d.V.v_rise_bed, 3)} m/s`,        src: 'Clift 1978' },
    { k: 'Rise velocity at surface',     v: `${fmt(d.V.v_rise_surf, 3)} m/s`,       src: 'larger bubble' },
    { k: 'Bubble diameter at surface',   v: `${fmt(d.V.d_b_surf, 3)} mm`,            src: 'ideal gas' },
    { k: 'Expansion ratio',              v: `${fmt(d.V.d_b_surf / Math.max(0.001, d.V.d_b_mm), 2)} x` },
    { k: 'Bed-to-surface rise time',     v: `${fmt(d.V.riseTimeS, 1)} s` },
    { k: 'Reynolds number',              v: `${fmt(d.V.Re_b, 0)}`,                  src: 'rho.v.d/mu' },
    { k: 'Eotvos number',                v: `${fmt(d.V.Eo_b, 3)}`,                  src: 'rho.g.d^2/sigma' },
  ]);

  // ── STEP 4: Frame geometry & spacing ─────────────────────────────────
  sectionHeading(ctx, '4', 'Frame Geometry, Diffuser Spacing & Influence Radius');
  paragraph(ctx, 'Corner-offset geometry: diffusers placed at corner offset c from each edge of the 2x2 m frame. The closest pair spacing s_min determines the per-diffuser influence radius r_inf.');
  formulaBlock(ctx, [
    'innerSpan = L - 2 x c = 2.0 - 2 x ' + fmt(d.V.corner, 2) + ' = ' + fmt(d.V.innerSpanM, 2) + ' m',
    's_min = min adjacent pair distance for ' + d.spec.diffuserCount + '-diffuser layout = ' + fmt(d.V.s_min, 3) + ' m',
    'r_inf = s_min / 2 = ' + fmt(d.V.r_inf, 3) + ' m',
    'edgeCover = max(0, r_inf - c) = ' + fmt(d.V.edgeCoverM, 3) + ' m',
    'P_eff = L + 2 x edgeCover = ' + fmt(d.V.effectivePitchM, 3) + ' m',
    'modulesPerHa = 10000 / P_eff^2 = ' + fmt(d.V.modulesPerHa, 1),
  ]);
  kvTable(ctx, [
    { k: 'Corner offset c',           v: `${fmt(d.V.corner, 3)} m` },
    { k: 'Inner span',                v: `${fmt(d.V.innerSpanM, 3)} m` },
    { k: 'Min diffuser spacing s_min', v: `${fmt(d.V.s_min, 3)} m` },
    { k: 'Influence radius r_inf',     v: `${fmt(d.V.r_inf, 3)} m`,             src: 's_min/2' },
    { k: 'Edge coverage beyond frame', v: `${fmt(d.V.edgeCoverM, 3)} m/side` },
    { k: 'Effective pitch P_eff',      v: `${fmt(d.V.effectivePitchM, 3)} m` },
    { k: 'Modules per hectare',        v: `${fmt(d.V.modulesPerHa, 1)} /ha` },
  ]);

  // ── STEP 5: Bed velocity & shear ─────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '5', 'Bed-Induced Velocity & Shear Stress (Seol 2007 / Schladow 1993)');
  paragraph(ctx, 'The rising bubble plume entrains water and creates an induced velocity at the bed. This bed velocity, multiplied by the drag coefficient, gives the shear stress used in the Shields criterion.');
  formulaBlock(ctx, [
    'B_0 = g x q_act     [buoyancy flux, m4/s3]',
    `    = 9.81 x ${(d.V.q_act_bed * 1000).toFixed(2)}e-3 = ${fmt(d.V.B_buoy, 4)}`,
    'U_plume = 1.46 x (B_0 x H)^(1/3)   [Seol 2007 point source]',
    `        = 1.46 x (${fmt(d.V.B_buoy, 4)} x ${fmt(d.V.effSubm, 2)})^(1/3) = ${fmt(d.V.U_plume, 3)} m/s`,
    'v_bed = 0.15 x U_plume / sqrt(H)  [approx near-bed velocity]',
    `      = ${fmt(d.V.v_bed, 3)} m/s`,
    'tau_bed = rho_w x C_D x v_bed^2  [C_D = 0.01, van Rijn 1984]',
    `        = ${fmt(d.V.rho_w, 0)} x 0.01 x ${fmt(d.V.v_bed, 3)}^2 = ${fmt(d.V.tau_bed, 4)} Pa`,
  ]);
  kvTable(ctx, [
    { k: 'Air flow at bed pressure',   v: `${(d.V.q_act_bed * 1000).toFixed(2)} L/s`,  src: 'P_atm/P_bed correction' },
    { k: 'Buoyancy flux B_0',          v: `${fmt(d.V.B_buoy, 4)} m4/s3` },
    { k: 'Plume centreline U_plume',    v: `${fmt(d.V.U_plume, 3)} m/s`,             src: 'Seol 2007' },
    { k: 'Near-bed velocity v_bed',     v: `${fmt(d.V.v_bed, 3)} m/s` },
    { k: 'Bed shear stress tau_bed',    v: `${fmt(d.V.tau_bed, 4)} Pa` },
  ]);

  // ── STEP 6: Shields criterion ────────────────────────────────────────
  sectionHeading(ctx, '6', 'Shields Criterion - Sediment Disturbance (Shields 1936 / van Rijn 1984)');
  paragraph(ctx, 'Shields parameter theta compares bed shear to the resistance to mobilisation. If theta/theta_cr < 1.0, the bed is stable. Theta_cr = 0.047 for non-cohesive sediment.');
  formulaBlock(ctx, [
    'theta = tau_bed / [(rho_s - rho_w) x g x d50]',
    `      = ${fmt(d.V.tau_bed, 4)} / [(${d.spec.sedimentRho} - ${fmt(d.V.rho_w, 0)}) x 9.81 x ${(d.spec.sedimentD50Um / 1e6).toExponential(2)}]`,
    `      = ${d.V.shields.toExponential(3)}`,
    `theta_cr = ${fmt(d.V.shields_cr, 3)}   [van Rijn 1984]`,
    `theta / theta_cr = ${fmt(d.V.disturbRatio, 3)} x`,
    '',
    'Loubiere & Hebrard (2003) turbulence correction to bubble size:',
    `turbCorr = min(0.15, 0.08 x min(theta/theta_cr, 2)) = ${fmt(d.V.turbCorr, 3)}`,
    `d_b_dist = d_b x (1 + turbCorr) = ${fmt(d.V.d_b_dist, 3)} mm`,
  ]);
  kvTable(ctx, [
    { k: 'Sediment d50',                v: `${d.spec.sedimentD50Um} um` },
    { k: 'Sediment density rho_s',      v: `${d.spec.sedimentRho} kg/m3` },
    { k: 'Shields parameter theta',      v: `${d.V.shields.toExponential(3)}` },
    { k: 'Critical Shields theta_cr',    v: `${fmt(d.V.shields_cr, 3)}` },
    { k: 'Disturbance ratio',            v: `${fmt(d.V.disturbRatio, 2)} x`,         src: d.V.disturbRatio < 1 ? 'STABLE' : 'MILD' },
    { k: 'Turbulence bubble correction', v: `${(d.V.turbCorr * 100).toFixed(1)} %`,   src: 'Loubiere 2003' },
    { k: 'Disturbed bubble diameter',    v: `${fmt(d.V.d_b_dist, 3)} mm` },
  ]);

  // ── STEP 7: O2 transfer (SOTR/AOTR) ─────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '7', 'Oxygen Transfer - SOTR / AOTR (ASCE 2-06 / Metcalf & Eddy 2014)');
  paragraph(ctx, 'Standard Oxygen Transfer Rate (SOTR) is the clean-water mass transfer at zero DO. Actual Oxygen Transfer Rate (AOTR) applies alpha/beta/temperature/deficit corrections.');
  formulaBlock(ctx, [
    'SOTE_total = SOTE_per_m x H_eff = ' + fmt(d.V.dtype.sote, 2) + ' x ' + fmt(d.V.effSubm, 2) + ' = ' + (d.V.sote_frac * 100).toFixed(2) + ' %',
    'SOTR = Q_module x rho_air x w_O2 x SOTE_frac',
    `     = ${fmt(d.V.modAirNm3h, 2)} x 1.293 x 0.2314 x ${fmt(d.V.sote_frac, 4)} = ${fmt(d.V.modSOTR, 4)} kg O2/h`,
    'AOTR = SOTR x alpha x beta x 1.024^(T-20) x (C_sat - C_L) / C_sat,20',
    `     = ${fmt(d.V.modSOTR, 4)} x ${fmt(d.spec.alphaFactor, 2)} x ${fmt(d.spec.betaFactor, 2)} x ${fmt(d.V.theta, 3)} x ${fmt(d.V.cs_deficit, 2)} / 9.09`,
    `     = ${fmt(d.V.modAOTR, 4)} kg O2/h`,
    `SAE = AOTR / kW = ${fmt(d.V.SAE_mODR, 2)} kg O2/kWh`,
  ]);
  kvTable(ctx, [
    { k: 'SOTE per metre',            v: `${fmt(d.V.dtype.sote, 2)} %/m`,        src: d.V.dtype.label },
    { k: 'SOTE total (at H_eff)',     v: `${(d.V.sote_frac * 100).toFixed(2)} %` },
    { k: 'Module airflow',             v: `${fmt(d.V.modAirNm3h, 2)} Nm3/h` },
    { k: 'Module SOTR',                v: `${fmt(d.V.modSOTR, 4)} kg O2/h`,         src: 'clean water' },
    { k: 'DO deficit (C_sat - C_L)',    v: `${fmt(d.V.cs_deficit, 2)} mg/L` },
    { k: 'Temperature factor theta',   v: `${fmt(d.V.theta, 3)}`,                src: '1.024^(T-20)' },
    { k: 'Module AOTR',                v: `${fmt(d.V.modAOTR, 4)} kg O2/h`,        src: 'site corrected' },
    { k: 'Aeration efficiency SAE',     v: `${fmt(d.V.SAE_mODR, 2)} kg O2/kWh` },
  ]);

  // ── STEP 8: SOD & O2 balance ────────────────────────────────────────
  sectionHeading(ctx, '8', 'Sediment Oxygen Demand & O2 Mass Balance');
  paragraph(ctx, 'The SOD is enhanced by sediment disturbance from the bubble plume. Total system AOTR must exceed the disturbed SOD over the covered area.');
  formulaBlock(ctx, [
    `SOD nominal = ${fmt(d.spec.sodGO2m2d, 2)} g O2/m2/d`,
    `Resuspension factor = ${fmt(d.V.sodResuspFactor, 2)} x (Mehta 1986)`,
    `SOD effective = ${fmt(d.V.effectiveSOD, 2)} g O2/m2/d`,
    `Coverage area = ${fmt(d.V.coveredAreaM2, 0)} m2 (${fmt(d.V.coveredPct, 1)} % of reservoir)`,
    `Modules required = ${d.V.modulesRequired}`,
    `Total AOTR = ${d.V.modulesRequired} x ${fmt(d.V.modAOTR, 4)} = ${fmt(d.V.totalAOTR, 3)} kg O2/h`,
    `SOD nominal = ${fmt(d.V.sodNomKgO2h, 3)} kg O2/h`,
    `SOD disturbed = ${fmt(d.V.sodDistKgO2h, 3)} kg O2/h`,
    `O2 surplus = AOTR - SOD_dist = ${fmt(d.V.oxygenSurplus, 3)} kg O2/h ${d.V.oxygenSurplus >= 0 ? '(PASS)' : '(DEFICIT)'}`,
  ]);
  kvTable(ctx, [
    { k: 'Reservoir area',         v: `${fmt(d.spec.reservoirAreaHa, 2)} ha` },
    { k: 'Target coverage',         v: `${d.spec.targetCoveragePct} %` },
    { k: 'Modules required',        v: String(d.V.modulesRequired) },
    { k: 'Coverage achieved',       v: `${fmt(d.V.coveredPct, 1)} %` },
    { k: 'Total system AOTR',       v: `${fmt(d.V.totalAOTR, 3)} kg O2/h` },
    { k: 'SOD demand (disturbed)',  v: `${fmt(d.V.sodDistKgO2h, 3)} kg O2/h` },
    { k: 'O2 surplus',              v: `${d.V.oxygenSurplus >= 0 ? '+' : ''}${fmt(d.V.oxygenSurplus, 3)} kg O2/h`,
      src: d.V.oxygenSurplus >= 0 ? 'PASS' : 'DEFICIT' },
  ]);

  // ── STEP 9: Cross-checks ────────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '9', 'Cross-Checks (independent verification)');
  paragraph(ctx, 'Cross-validation of key derived parameters by alternate calculation paths to detect errors.');
  kvTable(ctx, [
    { k: 'modulesPerHa (geometric)',   v: `${fmt(d.V.modulesPerHa, 1)} /ha` },
    { k: 'modulesPerHa (cross-check)', v: `${fmt(d.V.modulesPerHa_check, 1)} /ha`,
      src: Math.abs(d.V.modulesPerHa - d.V.modulesPerHa_check) < 0.01 ? 'MATCH' : 'MISMATCH' },
    { k: 'Total influence area',       v: `${fmt(d.V.totalInfluM2, 0)} m2` },
    { k: 'Reservoir area (m2)',        v: `${fmt(d.V.reservoirAreaM2, 0)} m2` },
    { k: 'Target coverage area',        v: `${fmt(d.V.targetAreaM2, 0)} m2` },
    { k: 'Covered area',                v: `${fmt(d.V.coveredAreaM2, 0)} m2` },
  ]);

  // ── STEP 10: Validation summary ─────────────────────────────────────
  sectionHeading(ctx, '10', `Final Pass/Fail Summary (${passes} / ${total})`);
  dataTable(ctx,
    ['#', 'Validation check', 'Status', 'Value'],
    d.checks.map((c, i) => [i + 1, c.lbl, c.pass ? 'PASS' : 'WARN', c.value]));

  // ── STEP 11: References ─────────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, '11', 'Literature References');
  for (const ref of REFERENCES) paragraph(ctx, ref, { small: true });

  drawSignatureBlock(ctx);
  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`${reportRef}.pdf`);
}

// ═════════════════════════════════════════════════════════════════════════════
// MASTER RESERVOIR REPORT — single consolidated PDF combining all pages
// ═════════════════════════════════════════════════════════════════════════════
export interface MasterReportData {
  layout: LayoutReportData;
  array: ModuleArrayData;
  validation: ValidationReportData;
  preDredge: DredgingScenarioData;
  postDredge: DredgingScenarioData;
  // v45 — advanced physics block (optional for backward-compat)
  advanced?: {
    SOTE_DBM_pct: number;
    SOTE_empirical_pct: number;
    d_b_surf_DBM: number;
    f_O2_DBM: number;
    P_plume_W_m2: number;
    E_strat_per_m2: number;
    wedderburnLike: number;
    trophicStatus: string;
    HOD_mg_L_d: number;
    days_to_anoxia: number;
    epsilon_W_kg: number;
    epsilon_threshold: number;
    bloomSuppressionOk: boolean;
    tau_mix_hours: number;
    doubling_time_h: number;
    mixingFasterThanGrowth: boolean;
  };
}

export function generateMasterReservoirReportPDF(d: MasterReportData, profile: SeasonalProfile): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const reportRef = `RB-MASTER-${profile.id}-${new Date().toISOString().slice(0, 10)}`;
  applyPDFMetadata(doc, {
    title:    'Reservoir Master Engineering Report - Barcombe Reservoir',
    subject:  `Consolidated engineering report (9 parts) for ${profile.label} profile`,
    extra:    reportRef,
    keywords: ['master report', 'consolidated', 'engineering', 'all calculations', profile.id],
  });
  const ctx: PageContext = {
    doc, pageW, pageH, margin, y: 0,
    reportTitle: 'Reservoir Master Engineering Report',
    reportRef,
  };
  const isTube = d.layout.spec.diffuserType === 'hdpe_porous_tube' || d.layout.spec.diffuserType === 'epdm_porous_tube';

  // ── COVER ─────────────────────────────────────────────────────────────
  ctx.y = drawHeader(ctx);
  doc.setFontSize(22); doc.setFont('helvetica', 'bold'); doc.setTextColor(...C.navy);
  doc.text(s('RESERVOIR MASTER REPORT'), pageW / 2, 55, { align: 'center' });
  doc.setFontSize(13); doc.setTextColor(...C.grey);
  doc.text(s('Consolidated Engineering Report - All Data from All Pages'), pageW / 2, 64, { align: 'center' });
  doc.setDrawColor(...C.gold); doc.setLineWidth(0.8);
  doc.line(margin, 72, pageW - margin, 72);

  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(...C.navy);
  doc.text(s('Project: Barcombe Reservoir Aeration'), pageW / 2, 86, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...C.grey);
  doc.text(s('East Sussex, UK . Client: Atkins Realis'), pageW / 2, 93, { align: 'center' });

  // Big summary box
  doc.setFillColor(255, 251, 235); doc.setDrawColor(...C.gold); doc.setLineWidth(0.5);
  doc.rect(margin, 105, pageW - 2 * margin, 60, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...C.gold);
  doc.text(s('EXECUTIVE SUMMARY'), pageW / 2, 114, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...C.black);
  let cy = 122;
  const summary = [
    ['Seasonal water profile', `${profile.label}`],
    ['Reservoir area / mean depth', `${d.layout.reservoir.areaHa.toFixed(2)} ha . ${d.layout.reservoir.meanDepth.toFixed(2)} m`],
    ['Diffuser type', `${isTube ? 'TUBE (line source)' : 'DISC (point source)'} - ${d.layout.spec.diffuserType}`],
    ['Recommended modules', `${d.layout.recommendations.bestCount} modules (${d.layout.recommendations.modulesPerHa.toFixed(1)} /ha)`],
    ['Recommended spacing', `${d.layout.recommendations.bestSpacing.toFixed(1)} m square grid`],
    ['Total airflow / power', `${d.layout.recommendations.totalAirflow.toFixed(0)} Nm3/h . ${d.layout.recommendations.totalKw.toFixed(1)} kW`],
    ['Total AOTR vs SOD demand', `${d.layout.recommendations.totalAOTR.toFixed(2)} >= ${d.layout.recommendations.sodDemand.toFixed(2)} kg O2/h`],
    ['Validation', `${d.validation.checks.filter(c => c.pass).length} / ${d.validation.checks.length} checks pass`],
  ];
  for (const [k, v] of summary) {
    doc.setTextColor(...C.grey); doc.text(s(k), margin + 8, cy);
    doc.setTextColor(...C.navy); doc.setFont('helvetica', 'bold');
    doc.text(s(v), pageW / 2 + 8, cy);
    doc.setFont('helvetica', 'normal');
    cy += 5;
  }

  // Cover plan view
  drawReservoirPlanView(doc, margin, 173, pageW - 2 * margin, 90, {
    bedColour: [124, 74, 42], bedLabel: 'Reservoir bed',
    arrayLabels: d.layout.arrayLabels,
    modules: d.layout.modules,
    gridSpacingM: 20, showCoordinates: true,
  });
  ctx.y = 265;
  captionLine(ctx, 'Fig 1 - Plan view (recommended layout, 20 m grid)');

  // ── TABLE OF CONTENTS ─────────────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'TOC', 'Table of Contents');
  const toc = [
    ['Part A', 'Assumptions, seasonal profile, and equipment specification'],
    ['Part B', 'Calculation hierarchy (diffuser -> module -> array -> system)'],
    ['Part C', 'Reservoir geometry, plan view & array placement'],
    ['Part D', 'Bed physics & recommended settings'],
    ['Part E', 'Pre-dredging scenario summary'],
    ['Part F', 'Post-dredging scenario summary'],
    ['Part G', 'Validation summary (19 independent checks)'],
    ['Part H', 'Bill of materials & operational cost'],
    ['Part I', 'Literature references'],
  ];
  dataTable(ctx, ['Part', 'Contents'], toc.map(([p, t]) => [p, t]));

  // ── PART A: Assumptions ───────────────────────────────────────────────
  newPage(ctx);
  drawAssumptionsBlock(ctx, profile, {
    diffuserCount: d.layout.spec.diffuserCount, diffuserType: d.layout.spec.diffuserType,
    diffuserDiamMm: d.layout.spec.diffuserDiamMm, poreSizeUm: d.layout.spec.poreSizeUm,
    airflowPerDiffNm3h: d.layout.spec.airflowPerDiffNm3h, mountingHeightM: d.layout.spec.mountingHeightM,
    cornerOffsetM: d.layout.spec.cornerOffsetM, diffuserDropKpa: d.layout.spec.diffuserDropKpa,
    blowerEff: d.layout.spec.blowerEff, safetyPct: d.layout.spec.safetyPct,
    alphaFactor: d.layout.spec.alphaFactor, betaFactor: d.layout.spec.betaFactor,
    tubeLengthM: d.layout.spec.tubeLengthM, tubeCount: d.layout.spec.tubeCount,
    tubeDiamMm: d.layout.spec.tubeDiamMm, tubePattern: d.layout.spec.tubePattern,
    tubeCentreOffsetM: d.layout.spec.tubeCentreOffsetM, porePerCm: d.layout.spec.porePerCm,
  }, {
    areaHa: d.layout.reservoir.areaHa, meanDepthM: d.layout.reservoir.meanDepth,
    surveyRef: 'Shoreline Surveys Ltd J2020 (09 Jan 2023)',
  });

  // ── PART B: Hierarchy ─────────────────────────────────────────────────
  newPage(ctx);
  drawHierarchyBlock(ctx,
    { qNm3h: d.array.spec.airflowPerDiffNm3h, bubbleMm: d.array.physics.d_b,
      soteMperM: d.array.physics.soteM, poresEach: d.array.physics.poresPerUnit },
    { nDiff: d.array.physics.unitsPerModule, qNm3h: d.array.physics.qPerModule,
      sotrKgh: d.array.physics.sotrPerModule, aotrKgh: d.array.physics.aotrPerModule,
      kw: d.array.physics.kwPerModule, poresTotal: d.array.physics.poresPerModule },
    d.layout.arrayLabels.map(a => ({
      id: a.id, nModules: a.modulesAssigned,
      nDiff: a.modulesAssigned * d.array.physics.unitsPerModule,
      qNm3h: a.modulesAssigned * d.array.physics.qPerModule,
      aotrKgh: a.modulesAssigned * d.array.physics.aotrPerModule,
      kw: a.modulesAssigned * d.array.physics.kwPerModule, depthM: a.depthM,
    })),
    { nArrays: d.layout.arrayLabels.length, nModules: d.layout.recommendations.bestCount,
      nDiff: d.layout.recommendations.bestCount * d.array.physics.unitsPerModule,
      qNm3h: d.layout.recommendations.totalAirflow,
      sotrKgh: d.array.physics.sotrPerModule * d.layout.recommendations.bestCount,
      aotrKgh: d.layout.recommendations.totalAOTR,
      kw: d.layout.recommendations.totalKw });

  // ── PART C: Plan view & array placement ──────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'C', 'Reservoir Geometry, Plan View & Array Placement');
  paragraph(ctx, 'The reservoir polygon is digitised from the J2020 bathymetric survey. The layout below shows recommended array positions and individual module placements with a 20 m reference grid.');
  drawReservoirPlanView(doc, margin, ctx.y, pageW - 2 * margin, 170, {
    bedColour: [124, 74, 42], bedLabel: 'Reservoir bed',
    arrayLabels: d.layout.arrayLabels,
    modules: d.layout.modules,
    gridSpacingM: 20, showCoordinates: true,
  });
  ctx.y += 172;
  captionLine(ctx, 'Fig 2 - Plan view with 20 m grid');

  newPage(ctx);
  sectionHeading(ctx, 'C.1', 'Array Allocation Table');
  dataTable(ctx,
    ['Array', 'Centroid (x, y) m', 'Depth (m)', 'Modules', 'Diffusers'],
    d.layout.arrayLabels.map(a => [
      a.id, `(${a.x}, ${a.y})`, a.depthM.toFixed(1),
      a.modulesAssigned, a.modulesAssigned * d.layout.spec.diffuserCount,
    ]));

  // Single module-array detail visual
  sectionHeading(ctx, 'C.2', 'Module Array Visual (1 representative array)');
  drawModuleArrayVisual(doc, margin, ctx.y, pageW - 2 * margin, 90, d.array);
  ctx.y += 92;
  captionLine(ctx, `Fig 3 - ${d.array.config.moduleCount}-module representative array (${isTube ? 'tubes' : 'discs'} shown to scale)`);

  // ── PART D: Bed physics recommendations ──────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'D', 'Bed Physics & Recommended Settings');
  paragraph(ctx, 'The recommended bed-to-module mounting height, airflow, pore size and diffuser count have been optimised for the worst-case bed type at the shallowest deployment depth, while maintaining bubble Ø in the 1-5 mm fine-bubble band and SOTE >= 2.5 %/m.');
  kvTable(ctx, [
    { k: 'Recommended mounting height',    v: `${d.layout.spec.mountingHeightM.toFixed(2)} m`, src: 'minimise bed shear' },
    { k: 'Recommended airflow per unit',    v: `${d.layout.spec.airflowPerDiffNm3h.toFixed(2)} Nm3/h`, src: 'balance SOTE vs bed shear' },
    { k: 'Recommended pore size',           v: `${d.layout.spec.poreSizeUm} um`, src: 'fine-bubble band' },
    { k: 'Recommended diffusers/module',    v: String(d.layout.spec.diffuserCount), src: 'distributed load' },
    { k: 'Operating bubble diameter',       v: `${d.array.physics.d_b.toFixed(2)} mm` },
    { k: 'SOTE per metre',                  v: `${d.array.physics.soteM.toFixed(2)} %/m`, src: 'Stenstrom 2008' },
    { k: 'Bed influence radius',            v: `${d.array.physics.r_inf_bed.toFixed(2)} m`, src: isTube ? 'Wuest 1992' : 'Seol 2007' },
  ]);

  // ── PART E: Pre-dredging ─────────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'E', 'Pre-Dredging Scenario');
  const pre = preDredgeParams(d.preDredge);
  paragraph(ctx, `Pre-dredge condition: bed comprises ${pre.bedLabel.toLowerCase()} with ${pre.siltDepthM.toFixed(1)} m of accumulated silt over the original substrate. Effective water column is ${pre.effectiveWaterDepthM.toFixed(1)} m. Recommended mounting height raised to ${pre.mountingHeightM.toFixed(2)} m to avoid silt impingement.`);
  kvTable(ctx, [
    { k: 'Silt depth',                     v: `${pre.siltDepthM.toFixed(2)} m` },
    { k: 'Effective water column',         v: `${pre.effectiveWaterDepthM.toFixed(2)} m` },
    { k: 'Mounting height (raised)',       v: `${pre.mountingHeightM.toFixed(2)} m` },
    { k: 'SOD rate (high)',                v: `${pre.sodRate.toFixed(2)} g O2/m2/d` },
    { k: 'Modules required',               v: String(pre.totalModules) },
    { k: 'Expected DO after aeration',     v: `${pre.expectedDOAfter.toFixed(1)} mg/L` },
  ]);
  paragraph(ctx, 'Goals: Suppress sediment oxygen demand to break anaerobic cycle. Prevent P / NH4 release. Reduce algal bloom risk. Pre-condition silt for dredging.', { small: true });

  // Cross-section visual for pre-dredge
  drawBedCrossSection(doc, margin, ctx.y, pageW - 2 * margin, 80, {
    title: 'Pre-Dredging Bed Cross-Section',
    scenarioLabel: 'PRE-DREDGE',
    totalDepthM: pre.effectiveWaterDepthM,
    siltDepthM: pre.siltDepthM,
    bedColour: C.cleanSand, siltColour: C.siltBrown,
    mountingHeightM: pre.mountingHeightM,
    moduleWidthM: 2.0,
    diffuserCount: d.layout.spec.diffuserCount,
    bubbleDiamMm: d.array.physics.d_b,
    plumeSurfaceRadiusM: d.array.physics.r_surface,
    arrayLabel: 'A',
    diffuserShape: isTube ? 'tube' : 'disc',
    tubeLengthM: d.layout.spec.tubeLengthM, tubeCount: d.layout.spec.tubeCount,
    doAfter: pre.expectedDOAfter,
  });
  ctx.y += 82;
  captionLine(ctx, 'Fig 4 - Pre-dredge cross-section (raised mounting above silt)');

  // ── PART F: Post-dredging ────────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'F', 'Post-Dredging Scenario');
  const post = postDredgeParams(d.postDredge);
  paragraph(ctx, `Post-dredge: clean sand substrate exposed, full ${post.effectiveWaterDepthM.toFixed(1)} m water column restored, mounting height lowered to ${post.mountingHeightM.toFixed(2)} m. SOD reduced to ${post.sodRate.toFixed(2)} g O2/m2/d. Module count reduced by ~35% vs pre-dredge.`);
  kvTable(ctx, [
    { k: 'Clean bed',                       v: post.bedLabel },
    { k: 'Effective water column',          v: `${post.effectiveWaterDepthM.toFixed(2)} m` },
    { k: 'Mounting height (lowered)',       v: `${post.mountingHeightM.toFixed(2)} m` },
    { k: 'SOD rate (reduced)',              v: `${post.sodRate.toFixed(2)} g O2/m2/d` },
    { k: 'Modules required (reduced)',      v: String(post.totalModules) },
    { k: 'Expected DO',                     v: `${post.expectedDOAfter.toFixed(1)} mg/L` },
  ]);

  drawBedCrossSection(doc, margin, ctx.y, pageW - 2 * margin, 80, {
    title: 'Post-Dredging Bed Cross-Section',
    scenarioLabel: 'POST-DREDGE',
    totalDepthM: post.effectiveWaterDepthM,
    siltDepthM: 0,
    bedColour: C.cleanSand, siltColour: C.cleanSand,
    mountingHeightM: post.mountingHeightM,
    moduleWidthM: 2.0,
    diffuserCount: d.layout.spec.diffuserCount,
    bubbleDiamMm: d.array.physics.d_b,
    plumeSurfaceRadiusM: d.array.physics.r_surface,
    arrayLabel: 'A',
    diffuserShape: isTube ? 'tube' : 'disc',
    tubeLengthM: d.layout.spec.tubeLengthM, tubeCount: d.layout.spec.tubeCount,
    doAfter: post.expectedDOAfter,
  });
  ctx.y += 82;
  captionLine(ctx, 'Fig 5 - Post-dredge cross-section (lowered mounting on firm bed)');

  // ── PART G: Validation ───────────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'G', 'Validation Summary');
  const vpass = d.validation.checks.filter(c => c.pass).length;
  paragraph(ctx, `Overall: ${vpass} / ${d.validation.checks.length} independent checks pass.`);
  dataTable(ctx, ['#', 'Check', 'Status'],
    d.validation.checks.map((c, i) => [i + 1, c.lbl, c.pass ? 'PASS' : 'WARN']));

  // ── PART H: BoM ──────────────────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'H', 'Bill of Materials & Operational Cost');
  dataTable(ctx, ['Item', 'Quantity', 'Notes'], [
    ['Diffuser arrays',     d.layout.arrayLabels.length, 'one sub-manifold per array'],
    ['Modules (2x2 m)',     d.layout.recommendations.bestCount, 'stainless steel frame'],
    ['Diffusers',           d.layout.recommendations.bestCount * d.layout.spec.diffuserCount,
      `${d.layout.spec.diffuserType} ${d.layout.spec.diffuserDiamMm} mm`],
    ['Total airflow',       `${d.layout.recommendations.totalAirflow.toFixed(0)} Nm3/h`, 'continuous duty'],
    ['Total power',         `${d.layout.recommendations.totalKw.toFixed(1)} kW`, 'recommend N+1 redundancy'],
    ['Energy cost @ 0.18 GBP/kWh, 24h',
      `${(d.layout.recommendations.totalKw * 24 * 0.18).toFixed(2)} GBP/day`,
      `${(d.layout.recommendations.totalKw * 8760 * 0.18 / 1000).toFixed(1)} k GBP/year continuous`],
  ]);

  // ── PART H.2: Lifecycle Economics ─────────────────────────────────────
  sectionHeading(ctx, 'H.2', 'Lifecycle Economics — CAPEX / OPEX / 20-year NPV');
  paragraph(ctx, 'Indicative engineering economics. Final pricing depends on tender quotes for membranes, frames, blowers and install. Reference framework: Cooke et al. 2005 ch.18 (cost-benefit framework for lake/reservoir restoration).');

  const nMod = d.layout.recommendations.bestCount;
  const nDiff_total = nMod * d.layout.spec.diffuserCount;
  const totalKw = d.layout.recommendations.totalKw;

  // Indicative unit costs (GBP, 2026, UK market — calibrated from typical tenders)
  const cost_module_GBP = 1800;         // 316L SS frame + manifold + connectors
  const cost_diffuser_GBP = 75;          // EPDM membrane disc Ø300 mm typical
  const cost_blower_GBP_kW = 1200;       // PD blower + VFD + enclosure per kW
  const cost_install_GBP_module = 600;   // diver placement + commissioning
  const cost_controls_GBP = 25000;       // PLC + sensors + telemetry lump sum
  const cost_pipe_GBP_module = 250;      // sub-manifold + connection pipework
  // Lifecycle assumptions
  const energy_GBP_kWh = 0.18;
  const maintenance_pct_per_yr = 0.025;   // 2.5% of CAPEX/yr typical for water industry
  const replacement_diffuser_yr = 7;      // diffuser membrane replacement cycle
  const replacement_blower_yr = 12;
  const discountRate = 0.04;              // 4% public-sector discount rate (Green Book)
  const horizonYrs = 20;
  // CAPEX
  const capex_modules = nMod * cost_module_GBP;
  const capex_diffusers = nDiff_total * cost_diffuser_GBP;
  const capex_blower = totalKw * cost_blower_GBP_kW;
  const capex_install = nMod * cost_install_GBP_module;
  const capex_pipe = nMod * cost_pipe_GBP_module;
  const capex_controls = cost_controls_GBP;
  const capex_total = capex_modules + capex_diffusers + capex_blower + capex_install + capex_pipe + capex_controls;
  // OPEX
  const opex_energy_yr = totalKw * 8760 * energy_GBP_kWh;
  const opex_maintenance_yr = capex_total * maintenance_pct_per_yr;
  const opex_total_yr = opex_energy_yr + opex_maintenance_yr;
  // Replacement costs over 20 yrs
  const n_diff_replacements = Math.floor(horizonYrs / replacement_diffuser_yr);
  const n_blower_replacements = Math.floor(horizonYrs / replacement_blower_yr);
  const replacement_diff_cost = n_diff_replacements * capex_diffusers * 0.9;  // 90% of original cost
  const replacement_blower_cost = n_blower_replacements * capex_blower * 0.8;
  // NPV
  let npv_total = -capex_total;
  for (let yr = 1; yr <= horizonYrs; yr++) {
    const yr_opex = opex_total_yr;
    let yr_replacement = 0;
    if (yr % replacement_diffuser_yr === 0) yr_replacement += capex_diffusers * 0.9;
    if (yr % replacement_blower_yr === 0) yr_replacement += capex_blower * 0.8;
    const yr_cost = yr_opex + yr_replacement;
    npv_total -= yr_cost / Math.pow(1 + discountRate, yr);
  }
  const totalLifecycle_yr_avg = -npv_total / horizonYrs;

  paragraph(ctx, 'CAPEX breakdown (£):', { bold: true, small: true });
  dataTable(ctx, ['Item', 'Quantity', 'Unit cost (£)', 'Total (£)'], [
    ['Modules (frames + manifolds)', nMod, cost_module_GBP, capex_modules.toFixed(0)],
    ['Diffusers',                     nDiff_total, cost_diffuser_GBP, capex_diffusers.toFixed(0)],
    ['Blower system + VFD',           `${totalKw.toFixed(1)} kW`, cost_blower_GBP_kW + '/kW', capex_blower.toFixed(0)],
    ['Sub-manifold pipework',          nMod, cost_pipe_GBP_module, capex_pipe.toFixed(0)],
    ['Install (diver + commissioning)', nMod, cost_install_GBP_module, capex_install.toFixed(0)],
    ['PLC + sensors + telemetry',     'lump sum', '—', capex_controls.toFixed(0)],
    ['TOTAL CAPEX',                   '', '', capex_total.toFixed(0)],
  ]);

  paragraph(ctx, 'Annual OPEX (£/year):', { bold: true, small: true });
  dataTable(ctx, ['Item', 'Basis', '£/year'], [
    ['Blower energy',         `${totalKw.toFixed(1)} kW × 8760 h × £${energy_GBP_kWh}/kWh`, opex_energy_yr.toFixed(0)],
    ['Maintenance',            `${(maintenance_pct_per_yr * 100).toFixed(1)}% of CAPEX`,    opex_maintenance_yr.toFixed(0)],
    ['TOTAL ANNUAL OPEX',     '',                                                          opex_total_yr.toFixed(0)],
  ]);

  paragraph(ctx, '20-year lifecycle cost (£, with replacements):', { bold: true, small: true });
  dataTable(ctx, ['Item', 'Cycle', 'Total over 20 yrs (£)'], [
    ['Diffuser replacements', `every ${replacement_diffuser_yr} yrs (${n_diff_replacements} replacements)`, replacement_diff_cost.toFixed(0)],
    ['Blower replacement',     `every ${replacement_blower_yr} yrs (${n_blower_replacements} replacements)`, replacement_blower_cost.toFixed(0)],
    ['Total OPEX 20 yrs',     `${horizonYrs} × £${opex_total_yr.toFixed(0)}/yr`,        (opex_total_yr * horizonYrs).toFixed(0)],
  ]);

  paragraph(ctx, `Net Present Value (NPV) — 20-year horizon, ${(discountRate * 100).toFixed(0)}% discount rate:`, { bold: true, small: true });
  kvTable(ctx, [
    { k: 'Total CAPEX',            v: `£ ${(capex_total/1000).toFixed(0)} k` },
    { k: 'Annual OPEX',            v: `£ ${(opex_total_yr/1000).toFixed(1)} k/year` },
    { k: 'NPV total (20 yr)',      v: `£ ${(npv_total/1000).toFixed(0)} k`,
      src: 'all costs present-valued' },
    { k: 'Annualised lifecycle',   v: `£ ${(totalLifecycle_yr_avg/1000).toFixed(1)} k/year`,
      src: 'amortised CAPEX + OPEX' },
    { k: 'Cost per kg O2 delivered', v: `£ ${(opex_total_yr / Math.max(1, d.layout.recommendations.totalAOTR * 8760)).toFixed(2)} /kg O₂`,
      src: 'OPEX only basis' },
  ]);

  paragraph(ctx, 'Notes: indicative figures only. UK Treasury Green Book 2022 prescribes 3.5% real discount rate for public-sector investments — we use 4% as conservative. Reference: Cooke et al. (2005) ch.18 for cost-benefit framework for lake restoration.', { italic: true, small: true });

  // ── PART J: Advanced physics & long-term outcomes (v45) ─────────────
  if (d.advanced) {
    const A = d.advanced;
    newPage(ctx);
    sectionHeading(ctx, 'J', 'Advanced Physics & Long-Term Outcomes');
    paragraph(ctx, 'Beyond the standard sizing physics, this section applies four further state-of-the-art methods to cross-validate the design. Each method comes from peer-reviewed water-industry / limnology literature.');

    sectionHeading(ctx, 'J.1', 'Discrete-Bubble Model (DBM) - McGinnis & Little 2002');
    paragraph(ctx, 'The DBM tracks individual gas species (O2, N2, CO2) through bubble rise, accounting for O2 dissolution, N2 stripping, ideal-gas expansion, and bubble-size evolution. It complements the Stenstrom & Rosso empirical SOTE used in standard sizing.');
    kvTable(ctx, [
      { k: 'Stenstrom & Rosso SOTE (empirical, used in sizing)', v: `${fmt(A.SOTE_empirical_pct, 2)} %`, src: 'Stenstrom & Rosso 2008' },
      { k: 'DBM-predicted SOTE',                                   v: `${fmt(A.SOTE_DBM_pct, 2)} %`,        src: 'McGinnis & Little 2002' },
      { k: 'Agreement (deviation)',                                v: `${fmt((A.SOTE_DBM_pct - A.SOTE_empirical_pct) / Math.max(0.1, A.SOTE_empirical_pct) * 100, 1)} %` },
      { k: 'Fraction of bubble O2 transferred',                    v: `${fmt(A.f_O2_DBM * 100, 1)} %` },
      { k: 'DBM-predicted surface bubble diameter',                v: `${fmt(A.d_b_surf_DBM, 2)} mm`,        src: 'Both transfer + expansion' },
    ]);
    paragraph(ctx, 'Conclusion: both methods give consistent results for the fine-bubble regime (1-5 mm, depth < 10 m). The DBM becomes essential for deeper waters or N2 stripping applications (e.g. hypolimnetic Speece cones).', { italic: true, small: true });

    sectionHeading(ctx, 'J.2', 'Destratification Energy - Lake / Wedderburn Number (Imberger 1985)');
    paragraph(ctx, 'The Lake Number framework compares the mechanical energy input from bubble plumes to the stratification potential energy. Adapted for aeration (Imteaz & Asaeda 2000):');
    formulaBlock(ctx, [
      `Bubble plume power: P_plume = rho × g × q_bed × H / A_plume = ${fmt(A.P_plume_W_m2, 3)} W/m²`,
      `Stratification energy (DeltaT~5degC summer): E_strat = ${fmt(A.E_strat_per_m2, 1)} J/m²`,
      `Wedderburn-like ratio: W = P_plume × 1h / E_strat = ${fmt(A.wedderburnLike, 2)}`,
      `Threshold: W > 1.0 (destratification capability sufficient)`,
      `Result: ${A.wedderburnLike > 1 ? 'PASS' : 'MARGINAL'}`,
    ]);

    sectionHeading(ctx, 'J.3', 'Hypolimnetic Oxygen Depletion - Trophic Status (Cornett & Rigler 1979)');
    paragraph(ctx, 'Areal Hypolimnetic Oxygen Depletion (AHOD) is the empirical link between SOD and lake trophic status. Reference values (Charlton 1980, Welch 1980):');
    kvTable(ctx, [
      { k: 'AHOD assumed (= SOD rate)',          v: `${fmt(d.advanced.HOD_mg_L_d * 2.5, 2)} g O2/m²/d` },
      { k: 'Trophic status classification',       v: A.trophicStatus.toUpperCase(),
        src: A.trophicStatus === 'oligotrophic' ? '< 0.25 g/m²/d'
           : A.trophicStatus === 'mesotrophic' ? '0.25–0.5'
           : A.trophicStatus === 'eutrophic' ? '0.5–1.0' : '> 1.0' },
      { k: 'HOD (volumetric, 2.5 m hypolimnion)', v: `${fmt(A.HOD_mg_L_d, 3)} mg/L/day` },
      { k: 'Days from C_sat to anoxia (no aeration)', v: `${fmt(A.days_to_anoxia, 0)} days`,
        src: A.days_to_anoxia > 60 ? 'longer than UK stratification' : 'aeration essential' },
    ]);

    sectionHeading(ctx, 'J.4', 'Algal Bloom Suppression Criteria (Welch & Cooke 1995; Visser 1996; Reynolds 1984)');
    paragraph(ctx, 'Three independent criteria for suppression of buoyant cyanobacteria (Microcystis, Anabaena) by artificial mixing:');
    kvTable(ctx, [
      { k: 'Power dissipation epsilon',         v: `${A.epsilon_W_kg.toExponential(2)} W/kg`,
        src: A.epsilon_W_kg > A.epsilon_threshold ? 'ABOVE threshold' : 'BELOW threshold' },
      { k: 'Threshold (Reynolds 1984)',         v: `${A.epsilon_threshold.toExponential(1)} W/kg`,
        src: 'vertical entrainment criterion' },
      { k: 'Mixing time tau_mix',               v: `${fmt(A.tau_mix_hours, 1)} h` },
      { k: 'Cyanobacterial doubling time',      v: `${A.doubling_time_h} h`, src: 'Microcystis typical' },
      { k: 'Mixing faster than growth?',        v: A.mixingFasterThanGrowth ? 'YES' : 'NO',
        src: 'criterion for bloom suppression' },
      { k: 'Overall bloom suppression',          v: A.bloomSuppressionOk ? 'EFFECTIVE' : 'MARGINAL' },
    ]);

    sectionHeading(ctx, 'J.5', 'Long-Term Outcomes (Beutel & Horne 1999 meta-review)');
    paragraph(ctx, 'Beutel & Horne (1999) reviewed 27 long-term hypolimnetic aeration installations. Typical outcomes after 2-3 years of continuous operation:');
    dataTable(ctx,
      ['Parameter', 'Typical change', 'Mechanism'],
      [
        ['Hypolimnetic DO',         '+4 to +7 mg/L',         'direct mass transfer from bubble plumes'],
        ['Total P in water column', '-30% to -60%',           'sediment iron-bound P stays oxidised, internal P loading shut down'],
        ['Chlorophyll-a (Apr-Sep)', '-25% to -50%',           'mixing prevents surface bloom accumulation'],
        ['Iron (Fe)',                '-60% to -90%',           'Fe(II) re-oxidises to insoluble Fe(III) at sediment-water interface'],
        ['Manganese (Mn)',          '-60% to -90%',           'same redox mechanism as Fe'],
        ['Hydrogen sulphide (H2S)', 'eliminated',             'no sulphate reduction in aerated layer'],
        ['Total cyanobacterial cells', '-30% to -80%',         'suppressed by mixing intensity > epsilon threshold'],
      ]);
    paragraph(ctx, 'Expected outcomes at this site based on this evidence base: similar order-of-magnitude DO/P/Fe/Mn improvements within 1-2 operating seasons.', { italic: true, small: true });
  }

  // ── PART I: References ───────────────────────────────────────────────
  newPage(ctx);
  sectionHeading(ctx, 'I', 'Literature References');
  for (const ref of REFERENCES) paragraph(ctx, ref, { small: true });
  if (d.advanced) {
    paragraph(ctx, 'Additional sources (v45 advanced physics):', { bold: true, small: true });
    const advRefs = [
      'Cornett, R.J. & Rigler, F.H. (1979). Hypolimnetic oxygen deficits. Limnol. Oceanogr. 24(1), 161-176.',
      'Charlton, M.N. (1980). Hypolimnion oxygen consumption in lakes. Can. J. Fish. Aquat. Sci. 37, 1531-1539.',
      'Reynolds, C.S. (1984). The Ecology of Freshwater Phytoplankton. Cambridge University Press.',
      'Imberger, J. (1985). The diurnal mixed layer. Limnol. Oceanogr. 30(4), 737-770.',
      'Welch, E.B. & Cooke, G.D. (1995). Internal phosphorus loading in shallow lakes. Lake Reserv. Manag. 11(3), 273-281.',
      'Visser, P.M. et al. (1996). Artificial mixing prevents nuisance blooms of buoyant cyanobacteria. Freshwat. Biol. 36, 435-450.',
      'Beutel, M.W. & Horne, A.J. (1999). A review of hypolimnetic oxygenation effects on lake water quality. Lake Reserv. Manag. 15(4), 285-297.',
      'Imteaz, M.A. & Asaeda, T. (2000). Artificial mixing of lake water by bubble plume & effects on algal bloom. Water Res. 34(6), 1919-1929.',
      'McGinnis, D.F. & Little, J.C. (2002). Predicting diffused-bubble oxygen transfer rate using discrete-bubble model. Water Res. 36(18), 4627-4635.',
      'DeMoyer, C.D. et al. (2003). Impact of bubble & free surface oxygen transfer on diffused aeration. Water Res. 37(8), 1890-1904.',
      'Cooke, G.D. et al. (2005). Restoration & Management of Lakes & Reservoirs, 3rd ed. CRC Press.',
      'Singleton, V.L. & Little, J.C. (2006). Designing hypolimnetic aeration & oxygenation systems. Environ. Sci. Technol. 40(24), 7512-7520.',
    ];
    for (const ref of advRefs) paragraph(ctx, ref, { small: true });
  }

  drawSignatureBlock(ctx);
  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`${reportRef}.pdf`);
}

// ─── Shared references & signature ────────────────────────────────────────────
const REFERENCES = [
  '1. Shoreline Surveys Ltd (2023). Barcombe Reservoir bathymetric survey. Project J2020.',
  '2. Fritz, W. (1935). Berechnung des Maximalvolumens von Dampfblasen. Physik. Zeitschrift 36, 379-384.',
  '3. Shields, A. (1936). Anwendung der Aehnlichkeitsmechanik auf die Geschiebebewegung.',
  '4. Partheniades, E. (1965). Erosion and deposition of cohesive soils. J. Hydraul. Div. 91(1), 105-139.',
  '5. Kell, G.S. (1975). Density, thermal expansivity, and compressibility of liquid water. J. Chem. Eng. Data 20(1), 97-105.',
  '6. Clift, R., Grace, J.R. & Weber, M.E. (1978). Bubbles, drops and particles. Academic Press.',
  '7. Vargaftik, N.B. et al. (1983). International tables of the surface tension of water. J. Phys. Chem. Ref. Data 12(3), 817-820.',
  '8. van Rijn, L.C. (1984). Sediment transport, part I: bed load transport. J. Hydraul. Eng. 110(10), 1431-1456.',
  '9. Mehta, A.J. (1986). Characterization of cohesive sediment properties and transport processes in estuaries.',
  '10. Wuest, A., Brooks, N.H. & Imboden, D.M. (1992). Bubble plume modeling for lake restoration. Water Resour. Res. 28(12), 3235-3250.',
  '11. Rasmussen, H. & Jorgensen, B.B. (1992). Microelectrode studies of seasonal oxygen uptake. Mar. Ecol. Prog. Ser. 81, 289-303.',
  '12. Lemckert, C.J. & Imberger, J. (1993). Energetic bubble plumes in arbitrary stratification. J. Hydraul. Eng. 119(6), 680-703.',
  '13. Schladow, S.G. (1993). Lake destratification by bubble-plume systems. J. Hydraul. Eng. 119(3), 350-368.',
  '14. Loubiere, K. & Hebrard, G. (2003). Bubble formation at a rigid or flexible orifice. Chem. Eng. Process. 42(11), 875-886.',
  '15. ASCE (2006). Measurement of oxygen transfer in clean water. ASCE/EWRI 2-06.',
  '16. Seol, D.G., Bhaumik, T., Bergmann, C. & Bhate, R.K. (2007). PIV measurements of bubble plume. J. Hydraul. Eng. 133(6), 627-640.',
  '17. Singleton, V.L. & Little, J.C. (2006). Designing hypolimnetic aeration and oxygenation systems. Environ. Sci. Technol. 40(24), 7512-7520.',
  '18. Stenstrom, M.K. & Rosso, D. (2008). A review of oxygen transfer standards. ASCE MOP 36.',
  '19. Hickey, C.W. & Gibbs, M.M. (2009). Lake sediment oxygen demand and nutrient dynamics. N.Z. J. Mar. Freshwater Res. 43(2), 693-714.',
  '20. AWWA (2011). Operational Control of Coagulation and Filtration Processes. AWWA Manual M37, 3rd edn.',
  '21. Metcalf & Eddy / Tchobanoglous, G. et al. (2014). Wastewater Engineering: Treatment & Resource Recovery, 5th edn. McGraw-Hill.',
];

function drawSignatureBlock(ctx: PageContext): void {
  spacer(ctx, 8);
  ensureSpace(ctx, 35);
  const doc = ctx.doc;
  doc.setDrawColor(...C.grey); doc.setLineWidth(0.3);
  doc.line(ctx.margin + 5, ctx.y + 15, ctx.margin + 70, ctx.y + 15);
  doc.line(ctx.pageW - ctx.margin - 70, ctx.y + 15, ctx.pageW - ctx.margin - 5, ctx.y + 15);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(...C.grey);
  doc.text(s('Design engineer (Reynolds & Bauhm Smart Water)'), ctx.margin + 5, ctx.y + 19);
  doc.text(s('Reviewed & approved'), ctx.pageW - ctx.margin - 70, ctx.y + 19);
  doc.text(s('Date: ____________'), ctx.pageW - ctx.margin - 70, ctx.y + 24);
}

// ═════════════════════════════════════════════════════════════════════════════
// v49 — BAND-2 ADDITIONS (Diffuser Comparison, Risk Register, Site Data Capture,
// Pre-Award Pitch)
// ═════════════════════════════════════════════════════════════════════════════

void fmt;

interface DiffuserComparisonCandidate {
  label: string;
  d_b_mm: number;
  sotePerM: number;
  coverageM2: number;
  modulesForFullCoverage: number;
  awwaOk: boolean;
  foulingResistance: number;
  embodiedCarbonBand: number;
  capexBand: 'low' | 'mid' | 'high';
  pilotSuitability: number;
  overallScore: number;
  description: string;
}

export interface DiffuserComparisonData {
  reservoirAreaM2: number;
  waterDepthM: number;
  mountingHeightM: number;
  waterTempC: number;
  candidates: DiffuserComparisonCandidate[];
}

export function generateDiffuserComparisonPDF(d: DiffuserComparisonData, profile: SeasonalProfile): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const reportRef = `DIFF-COMP-${Date.now().toString().slice(-6)}`;
  const ctx: PageContext = { doc, pageW, pageH, margin, y: 0, reportTitle: 'Diffuser Candidate Comparison', reportRef };
  ctx.y = drawHeader(ctx);

  applyPDFMetadata(doc, {
    title:    'Reservoir Aeration - Diffuser Candidate Comparison',
    subject:  'Side-by-side scoring of diffuser candidates against active design conditions',
    keywords: ['diffuser comparison', 'candidate scoring', 'AWWA M37', profile.label],
    extra:    `Seasonal profile: ${profile.label}`,
  });

  sectionHeading(ctx, '1', 'Comparison context');
  kvTable(ctx, [
    { k: 'Reservoir area',         v: `${d.reservoirAreaM2.toLocaleString()} m^2`,             src: 'Barcombe footprint' },
    { k: 'Working water depth',    v: `${d.waterDepthM.toFixed(2)} m`,                          src: 'active session' },
    { k: 'Mounting height',        v: `${d.mountingHeightM.toFixed(2)} m`,                      src: 'active session' },
    { k: 'Water temperature',      v: `${d.waterTempC.toFixed(1)} deg C`,                       src: 'active session' },
    { k: 'Seasonal profile',       v: profile.label,                                            src: 'session selection' },
    { k: 'Candidates evaluated',   v: `${d.candidates.length}`,                                 src: 'long-list filter' },
  ]);

  sectionHeading(ctx, '2', 'Scored comparison matrix');
  dataTable(ctx,
    ['Rank', 'Candidate', 'Bubble (mm)', 'SOTE/m', 'Cov m^2', 'Modules', 'AWWA', 'Foul', 'Carb', 'CAPEX', 'Pilot', 'Overall'],
    d.candidates.map((c, i) => [
      String(i + 1),
      c.label,
      c.d_b_mm.toFixed(2),
      c.sotePerM.toFixed(2) + '%',
      c.coverageM2.toFixed(0),
      String(c.modulesForFullCoverage),
      c.awwaOk ? 'PASS' : 'CHECK',
      `${c.foulingResistance}/5`,
      `${c.embodiedCarbonBand}/5`,
      c.capexBand.toUpperCase(),
      `${c.pilotSuitability}/5`,
      c.overallScore.toFixed(2),
    ])
  );

  sectionHeading(ctx, '3', 'Top-ranked candidate (pre-pilot)');
  if (d.candidates[0]) {
    const top = d.candidates[0];
    paragraph(ctx, `${top.label} - overall ${top.overallScore.toFixed(2)}/5`, { bold: true });
    paragraph(ctx, top.description);
    paragraph(ctx, `SOTE per metre at site submergence: ${top.sotePerM.toFixed(2)} %. Modules for full reservoir coverage: ${top.modulesForFullCoverage}. AWWA M37 module-density: ${top.awwaOk ? 'within envelope' : 'outside envelope - review'}.`);
  }

  sectionHeading(ctx, '4', 'Scoring methodology');
  paragraph(ctx,
    'The overall score is a weighted combination of (a) computed performance against the active session ' +
    '(bubble size, SOTE per metre, coverage area, module count, AWWA M37 compliance) and (b) qualitative ' +
    'ratings for fouling resistance, embodied carbon, CAPEX band and pilot-trial suitability.');
  paragraph(ctx,
    'The ranking is a pre-pilot indication only. Final recommendation requires the full pre-selection programme: ' +
    'site-measured sediment oxygen demand, surfactant index, bathymetric survey and an eight-week pilot trial ' +
    'against the top two or three candidates. Detailed CAPEX figures and supplier identities are not surfaced in this document.', { small: true, italic: true });

  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`Diffuser_Comparison_${reportRef}.pdf`);
}

// ─── Risk Register ───────────────────────────────────────────────────────────
export interface RiskRegisterData {
  rows: Array<{
    ref: string;
    category: string;
    description: string;
    cause: string;
    consequence: string;
    rawLikelihood: number;
    rawImpact: number;
    controls: string;
    resLikelihood: number;
    resImpact: number;
    owner: string;
    status: string;
  }>;
}

export function generateRiskRegisterPDF(d: RiskRegisterData): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const reportRef = `RISK-REG-${Date.now().toString().slice(-6)}`;
  const ctx: PageContext = { doc, pageW, pageH, margin, y: 0, reportTitle: 'Risk Register', reportRef };
  ctx.y = drawHeader(ctx);

  applyPDFMetadata(doc, {
    title:    'Reservoir Aeration - Risk Register',
    subject:  'Engineering, safety, environmental, commercial and regulatory risks',
    keywords: ['risk register', 'reservoir aeration', 'CDM 2015', 'DSEAR'],
  });

  sectionHeading(ctx, '1', 'Summary');
  const total = d.rows.length;
  const high  = d.rows.filter(r => r.resLikelihood * r.resImpact >= 15).length;
  const med   = d.rows.filter(r => { const s = r.resLikelihood * r.resImpact; return s >= 8 && s < 15; }).length;
  const low   = d.rows.filter(r => r.resLikelihood * r.resImpact < 8).length;
  kvTable(ctx, [
    { k: 'Total risks',                  v: String(total) },
    { k: 'High residual (>=15)',         v: String(high) },
    { k: 'Medium residual (8-14)',       v: String(med) },
    { k: 'Low residual (<8)',            v: String(low) },
  ]);

  sectionHeading(ctx, '2', 'Register');
  dataTable(ctx,
    ['Ref', 'Cat', 'Description', 'L(raw)', 'I(raw)', 'Raw', 'L(res)', 'I(res)', 'Res', 'Owner', 'Status'],
    d.rows.map(r => [
      r.ref, r.category.slice(0, 4).toUpperCase(),
      r.description.length > 60 ? r.description.slice(0, 58) + '..' : r.description,
      String(r.rawLikelihood), String(r.rawImpact), String(r.rawLikelihood * r.rawImpact),
      String(r.resLikelihood), String(r.resImpact), String(r.resLikelihood * r.resImpact),
      r.owner, r.status.toUpperCase(),
    ])
  );

  sectionHeading(ctx, '3', 'Detailed entries');
  for (const r of d.rows) {
    ensureSpace(ctx, 50);
    doc.setFillColor(...C.greyBg);
    doc.rect(margin, ctx.y, pageW - 2 * margin, 6, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...C.navy);
    doc.text(s(`${r.ref} . ${r.category.toUpperCase()} . ${r.description}`), margin + 2, ctx.y + 4.2);
    ctx.y += 8;
    paragraph(ctx, `Cause: ${r.cause}`, { small: true });
    paragraph(ctx, `Consequence: ${r.consequence}`, { small: true });
    paragraph(ctx, `Controls: ${r.controls}`, { small: true });
    paragraph(ctx, `Raw ${r.rawLikelihood}x${r.rawImpact}=${r.rawLikelihood * r.rawImpact} -> Residual ${r.resLikelihood}x${r.resImpact}=${r.resLikelihood * r.resImpact} | Owner: ${r.owner} | Status: ${r.status.toUpperCase()}`, { small: true, italic: true });
    spacer(ctx, 2);
  }

  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`Risk_Register_${reportRef}.pdf`);
}

// ─── Site Data Capture ───────────────────────────────────────────────────────
export interface SiteDataCaptureData {
  form: Record<string, string | number | boolean>;
  groups: Array<{
    id: string;
    title: string;
    fields: Array<{ key: string; label: string; type?: string; placeholder?: string }>;
  }>;
}

export function generateSiteDataCapturePDF(d: SiteDataCaptureData): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const reportRef = `SITE-VISIT-${Date.now().toString().slice(-6)}`;
  const ctx: PageContext = { doc, pageW, pageH, margin, y: 0, reportTitle: 'Site Visit Record', reportRef };
  ctx.y = drawHeader(ctx);

  applyPDFMetadata(doc, {
    title:    'Reservoir Aeration - Site Visit Data Capture',
    subject:  'Pre/post-award field record of bathymetry, sediment, water chemistry, hydrology, power, acoustics',
    keywords: ['site visit', 'data capture', 'bathymetry', 'sediment cores', 'CTD'],
  });

  paragraph(ctx,
    'Field record captured during a site visit to Barcombe Reservoir. Unfilled fields are explicit data gaps ' +
    'requiring follow-up. Each section maps directly into the Smart Water modelling pipeline once populated.',
    { italic: true });

  d.groups.forEach((group, idx) => {
    sectionHeading(ctx, String(idx + 1), group.title.replace(/^\d+\.\s*/, ''));
    const rows: { k: string; v: string }[] = group.fields.map(f => {
      const raw = d.form[f.key];
      let v: string;
      if (typeof raw === 'boolean') v = raw ? 'Yes' : 'No';
      else if (raw === undefined || raw === null || raw === '') v = '___ (not recorded)';
      else v = String(raw);
      return { k: f.label, v };
    });
    kvTable(ctx, rows);
  });

  drawSignatureBlock(ctx);
  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`Site_Visit_${reportRef}.pdf`);
}

// ─── Pre-Award Pitch ─────────────────────────────────────────────────────────
export interface PreAwardPitchData {
  client: string;
  recipient: string;
  reservoirAreaM2: number;
  completionPct: number;
}

export function generatePreAwardPitchPDF(d: PreAwardPitchData): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 16;
  const reportRef = `PITCH-${Date.now().toString().slice(-6)}`;
  const ctx: PageContext = { doc, pageW, pageH, margin, y: 0, reportTitle: 'Pre-Award Technical Pitch', reportRef };
  ctx.y = drawHeader(ctx);

  applyPDFMetadata(doc, {
    title:    'Reservoir Aeration - Pre-Award Technical Approach',
    subject:  'Reynolds & Bauhm Smart Water pre-award engineering pitch',
    keywords: ['pre-award', 'technical approach', 'Barcombe', 'pitch', 'CONFIDENTIAL'],
  });

  // Cover heading
  doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(...C.navy);
  doc.text(s('Reservoir Aeration - Pre-Award Technical Approach'), margin, ctx.y + 6);
  ctx.y += 12;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...C.grey);
  doc.text(s(`Prepared for ${d.recipient} - ${d.client}`), margin, ctx.y);
  ctx.y += 5;
  doc.text(s(new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })), margin, ctx.y);
  ctx.y += 10;

  sectionHeading(ctx, '1', 'Position');
  paragraph(ctx,
    'The correct diffuser for Barcombe Reservoir has not yet been determined. There is nothing being sold off the ' +
    'shelf. The work set out below is what genuinely needs doing in order to arrive at the right answer with confidence.');
  paragraph(ctx,
    `The Reynolds & Bauhm Smart Water design framework is presently at the conceptual stage - approximately ` +
    `${d.completionPct.toFixed(0)}% of the underlying engineering analysis is complete. The remainder is the work ` +
    `that the post-award programme is designed to deliver.`);

  sectionHeading(ctx, '2', 'Physics regimes within the design framework');
  const physicsList = [
    'Bubble formation at the diffuser pore, including high-flow and swarm-coalescence regimes',
    'Pore-axis geometry and initial bubble velocity vector',
    'Plume centre-line angle including wind drift and cross-flow correction',
    'Bubble rise dynamics across spherical, ellipsoidal and cap regimes (two independent terminal-velocity correlations cross-checked)',
    'Liquid-side mass transfer with surfactant penalty for real surface water',
    'Gas-holdup behaviour within the bubble plume',
    'Plume hydrodynamics - buoyancy flux, momentum flux, entrainment, mixing radius, plume Froude',
    'Pore-to-pore and module-to-module plume interaction',
    'Bed-sediment interaction - Shields critical shear modified for fine sediment',
    'Reservoir-scale stratification dynamics - Lake/Wedderburn, Schmidt stability, Brunt-Vaisala',
    'Oxygen-demand budgeting - SOD, hypolimnetic oxygen demand, surface re-aeration',
    'Time-domain dissolved oxygen simulation over 30-day horizon',
    'Carbonate chemistry and predicted pH response',
    'Ecological response - cyanobacteria suppression and mixing-energy thresholds',
  ];
  physicsList.forEach((p, i) => paragraph(ctx, `  ${i + 1}. ${p}`, { small: true }));

  sectionHeading(ctx, '3', 'Calculation variants planned per diffuser candidate');
  paragraph(ctx,
    'For each candidate diffuser, approximately 20 independent calculation variants are run across 6 seasonal ' +
    'profiles, producing several hundred individual scenarios. The tool is structured to absorb site-measured ' +
    'inputs the moment they arrive and re-run the full set automatically.');

  sectionHeading(ctx, '4', 'Pre-selection field & laboratory programme');
  const tasks = [
    'Multi-beam or RTK echo-sounder bathymetric survey',
    'Bed-slope mapping, inlet-deposition lobes, scour low-spots, embankment toe',
    'Sediment cores (Ekman/Van Veen) at multiple locations stratified by slope position',
    'Lab: grain-size, bulk density, LOI, total phosphorus, iron speciation',
    'Lab: sediment oxygen demand respirometry',
    'Porewater oxygen microelectrode profiling',
    'Vertical CTD profiling at 4 stations across 4 seasons',
    'Natural organic matter, surfactant index, alkalinity, hardness, nutrients',
    'Chlorophyll-a and cyanobacteria genus identification',
    'Inflow/outflow gauging and 24 months of SCADA abstraction data',
    '10 years of MIDAS wind, temperature, solar radiation, humidity',
    'Catchment runoff and storm-load characterisation',
    'Embankment power supply audit (capacity, harmonics, redundancy)',
    'Blower compound walk-down, ducting corridors, geotechnical confirmation',
    'Acoustic baseline (BS 4142:2014) at nearest noise-sensitive receptors',
    'Side-by-side pilot trial of 2-3 shortlisted diffusers (ASCE 2-06 SOTE, α-factor, fouling, ADV plume mapping)',
    'Weighted scoring of every diffuser candidate with pilot empirical input',
  ];
  tasks.forEach((t, i) => paragraph(ctx, `  ${i + 1}. ${t}`, { small: true }));

  sectionHeading(ctx, '5', 'Compliance & consent activities in parallel');
  const compliance = [
    'Reservoirs Act 1975 (panel-engineer involvement, supervising engineer continuity)',
    'DWI raw-water continuity and Reg 31 wetted-materials approval',
    'Environment Agency consent and WFD non-deterioration check',
    'CDM 2015 - Principal Designer / Principal Contractor duties',
    'DSEAR assessment for blower compound',
    'BS 4142:2014 acoustic baseline and prediction',
    'South East Water internal sign-off (Ops, AM, WQ)',
    'INNS / Check-Clean-Dry biosecurity protocol',
  ];
  compliance.forEach((c, i) => paragraph(ctx, `  ${i + 1}. ${c}`, { small: true }));

  sectionHeading(ctx, '6', 'Why each activity matters');
  paragraph(ctx,
    'Each task closes a specific engineering uncertainty. Without site-measured sediment oxygen demand the module ' +
    'count is exposed to material error. Without surfactant characterisation the clean-water SOTE will over-predict ' +
    'by 30-60%. Without full bathymetric coverage the plume design risks dead zones. Without pore-axis and ' +
    'release-geometry analysis the plume centre-line can drift under wind. Without a power audit the blower ' +
    'specification can outrun the available supply. Without a pilot trial the alpha-factor is a textbook estimate ' +
    'rather than a measured value. Without a parallel consent programme a late regulatory query can shift ' +
    'commissioning by an entire stratification season. None of these are insurmountable - they are the proper ' +
    'sequence of work for a reservoir of this importance.');

  sectionHeading(ctx, '7', 'Delivery platform');
  paragraph(ctx,
    'A purpose-built calculation tool is operational. Page-by-page engineering pages cover layout, modules, ' +
    'sediment bed, deep bed assessment, plume coverage, pore release geometry, diffuser candidate comparison, ' +
    'risk register, compliance gateway, site visit data capture, and master report. Nine native PDF generators ' +
    'produce CONFIDENTIAL-watermarked, metadata-tagged deliverables. A persistence layer captures every input. ' +
    'A live validation framework runs more than two dozen automated literature-band checks.');

  sectionHeading(ctx, '8', 'Closing');
  paragraph(ctx,
    'The methods by which each problem is resolved are the product of considerable in-house development and would ' +
    'be presented in full under appropriate commercial cover once the contract is awarded. What can be said plainly ' +
    'is that the delivery platform is built, it is operating, and it is ready to receive the site-measured data ' +
    'the moment the post-award programme begins.');
  paragraph(ctx, 'Available by telephone at any time to discuss further.', { italic: true });

  drawSignatureBlock(ctx);
  drawFooters(doc, pageW, pageH, margin, reportRef);
  doc.save(`Pre_Award_Pitch_${reportRef}.pdf`);
}

